/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  filterLetters,
  filterDigits,
  filterPhone,
  lettersKeyDown,
  digitsKeyDown,
  phoneKeyDown,
} from './utils/inputFilters';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Badge,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  TextField,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Checkbox,
  Drawer,
  AppBar,
  Toolbar,
  Divider,
  Tooltip,
  Popover,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import PhotoUploader from './components/PhotoUploader';
import MyProfileDialog from './components/common/MyProfileDialog';
import LeaveRequestForm from './components/Leave/LeaveRequestForm';
import LeaveBalancesSummary from './components/Leave/LeaveBalancesSummary';
import LeaveRequestsTable from './components/Leave/LeaveRequestsTable';
import LeaveAttachmentLink from './components/Leave/LeaveAttachmentLink';
import {
  Home as HomeIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatName } from './utils/formatName';
import { formatDateOnly, formatDateTimeToTime12h, formatTime12h, timePickerValueTo24hString, formatDateWithWeekday, toDateKey } from './utils/dateTimeUtils';
import { formatDatePH, formatDateTimePH } from './utils/dateFormat';
import SidebarNavItem from './components/Layout/SidebarNavItem';
import PersonalCalendar from './components/Calendar/PersonalCalendar';
import PayslipCard from './components/PayslipCard';
import LiveDateTimeWidget from './components/LiveDateTimeWidget';
import { API_BASE } from './utils/apiBase';
import { cleanStatus } from './utils/attendanceUi';
import { fetchPayslipRow } from './utils/payslipApi';

const INVENTORY_WARNING_STOCK_THRESHOLD = 70;
const INVENTORY_CRITICAL_STOCK_THRESHOLD = 30;
const APP_DATA_START_YEAR = 2026;
const APP_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const getManilaMonthYear = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(new Date());
  return {
    month: Number(parts.find((part) => part.type === 'month')?.value || 1),
    year: Number(parts.find((part) => part.type === 'year')?.value || APP_DATA_START_YEAR),
  };
};
const getAvailableReportYears = () => {
  const { year } = getManilaMonthYear();
  const lastYear = Math.max(APP_DATA_START_YEAR, year);
  return Array.from({ length: lastYear - APP_DATA_START_YEAR + 1 }, (_, index) => APP_DATA_START_YEAR + index);
};
const getAvailableReportMonths = (yearValue) => {
  const { month: currentMonth, year: currentYear } = getManilaMonthYear();
  const year = Number(yearValue) || currentYear;
  if (year < APP_DATA_START_YEAR || year > currentYear) return [];
  const lastMonth = year === currentYear ? currentMonth : 12;
  return Array.from({ length: lastMonth }, (_, index) => index + 1);
};
const clampReportPeriod = (monthValue, yearValue) => {
  const { month: currentMonth, year: currentYear } = getManilaMonthYear();
  const years = getAvailableReportYears();
  const yearRaw = Number(yearValue) || currentYear;
  const year = Math.min(Math.max(yearRaw, years[0] || APP_DATA_START_YEAR), years[years.length - 1] || currentYear);
  const months = getAvailableReportMonths(year);
  const monthRaw = Number(monthValue) || currentMonth;
  const month = Math.min(Math.max(monthRaw, months[0] || 1), months[months.length - 1] || currentMonth);
  return { month, year };
};
const getInventoryStockLevel = (quantity) => {
  const qty = Number(quantity || 0);
  if (qty < INVENTORY_CRITICAL_STOCK_THRESHOLD) return 'critical';
  if (qty <= INVENTORY_WARNING_STOCK_THRESHOLD) return 'warning';
  return 'ok';
};

const isEarlierDateKey = (left, right) => {
  if (!left || !right) return false;
  return String(left) < String(right);
};

const formatContribution = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};
const hasValidGovNumber = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return false;
  const low = s.toLowerCase();
  return s !== '0' && low !== 'n/a' && low !== 'na' && low !== '-';
};
const getGovContributionDisplay = (amount, govNumber) => {
  if (!hasValidGovNumber(govNumber)) return formatPeso(0);
  return formatPeso(amount);
};
const getPayrollGrossDisplay = (row) => {
  const gross = Number(row?.gross ?? row?.gross_pay ?? row?.grossSalary ?? row?.gross_salary ?? NaN);
  if (Number.isFinite(gross) && gross > 0) return gross;
  const net = Number(row?.netSalary ?? row?.net_salary ?? row?.net ?? 0) || 0;
  const sss = Number(row?.deductionsSss ?? row?.deductions_sss ?? 0) || 0;
  const philhealth = Number(row?.deductionsPhilhealth ?? row?.deductions_philhealth ?? 0) || 0;
  const pagibig = Number(row?.deductionsPagibig ?? row?.deductions_pagibig ?? 0) || 0;
  const late = Number(row?.deductionsLate ?? row?.deductions_lateness ?? row?.deductions_late ?? 0) || 0;
  return net + sss + philhealth + pagibig + late;
};
const getPayrollNetDisplay = (row, employeeRecord) => {
  const gross = getPayrollGrossDisplay(row);
  const sssNum = (employeeRecord && employeeRecord.sss_no) || row?.sss_no || '';
  const philNum = (employeeRecord && employeeRecord.philhealth_no) || row?.philhealth_no || '';
  const pagNum = (employeeRecord && employeeRecord.pagibig_no) || row?.pagibig_no || '';
  const sssAmt = hasValidGovNumber(sssNum) ? Number(row?.deductionsSss ?? row?.deductions_sss ?? 0) || 0 : 0;
  const philAmt = hasValidGovNumber(philNum) ? Number(row?.deductionsPhilhealth ?? row?.deductions_philhealth ?? 0) || 0 : 0;
  const pagAmt = hasValidGovNumber(pagNum) ? Number(row?.deductionsPagibig ?? row?.deductions_pagibig ?? 0) || 0 : 0;
  const lateAmt = Number(row?.deductionsLate ?? row?.deductions_lateness ?? row?.deductions_late ?? 0) || 0;
  return Math.max(0, gross - sssAmt - philAmt - pagAmt - lateAmt);
};
const formatPeso = (value, allowBlank = false) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return allowBlank ? '' : '\u20b10.00';
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(n);
};
const formatPayrollPeriodDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Manila',
  }).format(date);
};
const formatPayrollCutoffLabel = (cutoff, options = {}) => {
  const includeStatus = options.includeStatus !== false;
  const label = `Pay Period ${formatPayrollPeriodDate(cutoff?.period_start)} - ${formatPayrollPeriodDate(cutoff?.period_end)} | Payout Date ${formatPayrollPeriodDate(cutoff?.pay_date)}`;
  return includeStatus ? `${label} | ${cutoff?.status || '-'}` : label;
};
const buildServerFileUrl = (filePath) => {
  const raw = String(filePath || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith('/')) return `${API_BASE}${raw}`;
  return `${API_BASE}/${raw}`;
};
const getTranscriptSections = (report) => {
  if (Array.isArray(report?.transcripts) && report.transcripts.length > 0) return report.transcripts;
  if (report) {
    return [{
      cutoff_id: report.cutoff_id || null,
      period_start: report.period_start || null,
      period_end: report.period_end || null,
      cutoff_label: report.cutoff_label || null,
      summary: report.summary || {},
      salary: report.salary || {},
      rows: Array.isArray(report.rows) ? report.rows : [],
    }];
  }
  return [];
};
const cleanText = (value, fallback = '-') => {
  const s = String(value ?? '').trim();
  if (!s) return fallback;
  if (s.includes('Ã¢â‚¬â€') || s.includes('Ã¢â‚¬Â¦') || s === '—' || s === 'â€”') return fallback;
  return s;
};
const formatEmployeeNumber = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return /^\d+$/.test(raw) ? raw.padStart(8, '0') : raw;
};
const formatInventoryPersonLabel = (employeeNo, name, fallback = '-') => {
  const safeNo = formatEmployeeNumber(employeeNo);
  const safeName = cleanText(name, '');
  if (safeNo && safeName) return `${safeNo} - ${safeName}`;
  if (safeNo) return safeNo;
  if (safeName) return safeName;
  return fallback;
};
const BRANCH_HEADCOUNT_MIN = 1;
const BRANCH_HEADCOUNT_MAX = 6;
const parseBranchHeadcountValue = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return NaN;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : NaN;
};
const getBranchHeadcountValidation = (minValue, maxValue) => {
  const minHeadcount = parseBranchHeadcountValue(minValue);
  const maxHeadcount = parseBranchHeadcountValue(maxValue);
  const minError = !Number.isInteger(minHeadcount) || minHeadcount < BRANCH_HEADCOUNT_MIN || minHeadcount > BRANCH_HEADCOUNT_MAX
    ? 'Minimum headcount must be between 1 and 6.'
    : '';
  const maxError = !Number.isInteger(maxHeadcount) || maxHeadcount < BRANCH_HEADCOUNT_MIN || maxHeadcount > BRANCH_HEADCOUNT_MAX
    ? 'Maximum headcount must be between 1 and 6.'
    : '';
  const rangeError = !minError && !maxError && minHeadcount > maxHeadcount
    ? 'Minimum headcount cannot be greater than maximum headcount.'
    : '';
  return {
    minHeadcount,
    maxHeadcount,
    minError,
    maxError,
    rangeError,
    isValid: !minError && !maxError && !rangeError,
  };
};
const PROVINCE_CITY_OPTIONS = {
  NCR: [
    'Manila',
    'Mandaluyong',
    'Marikina',
    'Pasig',
    'Quezon City',
    'San Juan',
    'Caloocan',
    'Malabon',
    'Navotas',
    'Valenzuela',
    'Las Pinas',
    'Makati',
    'Muntinlupa',
    'Paranaque',
    'Pasay',
    'Pateros',
    'Taguig',
  ],
  Bulacan: ['Meycauayan', 'Malolos', 'Bocaue', 'Marilao', 'Baliuag', 'San Jose del Monte'],
  Cavite: ['Bacoor', 'Imus', 'Dasmarinas', 'General Trias', 'Tagaytay'],
  Laguna: ['Santa Rosa', 'Calamba', 'Binan', 'San Pablo', 'Los Banos'],
  Rizal: ['Antipolo', 'Cainta', 'Taytay', 'Rodriguez', 'San Mateo'],
};
const CITY_CODE_OVERRIDES = {
  'QUEZON CITY': 'QZN',
  MANDALUYONG: 'MDK',
  MARIKINA: 'MKN',
  VALENZUELA: 'VAL',
  MUNTINLUPA: 'MTP',
  PARANAQUE: 'PRN',
  CALOOCAN: 'CLC',
  LASPINAS: 'LPS',
  'LAS PINAS': 'LPS',
  PASIG: 'PSG',
  PASAY: 'PSY',
  TAGUIG: 'TGG',
  MANILA: 'MNL',
  MAKATI: 'MKT',
  NAVOTAS: 'NVT',
  SANJUAN: 'SJN',
  'SAN JUAN': 'SJN',
  MALABON: 'MLB',
  PATEROS: 'PTR',
};
const toCityCode = (city) => {
  const normalized = String(city || '').toUpperCase().replace(/[^A-Z ]/g, '').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (CITY_CODE_OVERRIDES[normalized]) return CITY_CODE_OVERRIDES[normalized];
  const compact = normalized.replace(/\s+/g, '');
  return compact.slice(0, 3).padEnd(3, 'X');
};
// Face recognition removed from HR dashboard per requirements.
// (Component import eliminated; section entirely hidden.)

export default function HRDashboard({ user: propUser, onLogout: propOnLogout }) {
  const location = useLocation();
  const user = propUser || null;
  const mustChangePassword = Boolean(user?.must_change_password);
  const onLogout = propOnLogout || (() => {});
  const normalizedRole = (user?.role || '').toLowerCase();
  const isHr = ['hr', 'admin', 'administrator', 'superuser'].includes(normalizedRole);
  const formatDateTime = (value) => value ? `${formatDateOnly(value)} ${formatDateTimeToTime12h(value)}`.trim() : '';
  const formatTime = (value) => value ? formatTime12h(value) : 'N/A';
  const buildAttendanceDateTime = React.useCallback((dateValue, timeValue) => {
    const dateKey = toDateKey(dateValue || new Date());
    const timePart = String(timeValue || '').trim();
    if (!dateKey || !timePart) return null;
    const parsed = new Date(`${dateKey}T${timePart}+08:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);
  const formatCountdownLabel = (remainingMs) => {
    const clamped = Math.max(0, Number(remainingMs || 0));
    const totalSeconds = Math.floor(clamped / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };
  const formatManualDate = (value) => {
    if (!value) return '-';
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split('-').map(Number);
      return `${d}/${m}/${y}`;
    }
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return raw;
    return `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
  };
  const formatManualTime = (value) => {
    if (!value) return '-';
    const s = String(value).trim().slice(0, 8);
    const parts = s.split(':');
    const h = Number(parts[0]);
    const m = String(parts[1] || '00').padStart(2, '0');
    if (!Number.isFinite(h)) return s;
    const ampm = h >= 12 ? 'pm' : 'am';
    const hour12 = (h % 12) || 12;
    return `${hour12}:${m}${ampm}`;
  };
  const formatManualEmployeeNo = (value) => {
    const digits = String(value ?? '').replace(/\D/g, '');
    return digits ? digits.padStart(8, '0') : '-';
  };
  const resolveManualEmployeeName = (row) => {
    const direct = String(row?.employee_name || '').trim();
    if (direct) return direct;
    const rowNo = String(row?.employee_no ?? '').replace(/\D/g, '').replace(/^0+/, '');
    if (!rowNo) return '-';
    const match = (employees || []).find((e) => {
      const eNo = String(e?.employeeid ?? e?.employee_no ?? e?.employee_id ?? '').replace(/\D/g, '').replace(/^0+/, '');
      return eNo && eNo === rowNo;
    });
    if (!match) return '-';
    const fromFields = [match.firstname, match.middlename, match.lastname].filter(Boolean).join(' ').trim();
    return fromFields || formatName(match, { lastFirst: false, middle: 'full', capitalize: true }) || '-';
  };
  const parseTimeToDate = (value) => {
    if (!value) return null;
    const parts = String(value).split(':');
    if (!parts.length) return null;
    const h = Number(parts[0]);
    const m = Number(parts[1] || 0);
    const s = Number(parts[2] || 0);
    if (Number.isNaN(h) || Number.isNaN(m) || Number.isNaN(s)) return null;
    const d = new Date();
    d.setHours(h, m, s, 0);
    return d;
  };
  const defaultReportPeriod = React.useMemo(() => clampReportPeriod(), []);
  const reportYearOptions = React.useMemo(() => getAvailableReportYears(), []);
  const getMonthOptionsForYear = React.useCallback((yearValue) => getAvailableReportMonths(yearValue || defaultReportPeriod.year), [defaultReportPeriod.year]);
  const currentInventoryMonthLabel = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Manila',
  }).format(new Date());
  const formatInventoryRequestTimestamp = (value) => value ? formatDateTimePH(value) : '-';

  // State
  const [employees, setEmployees] = useState([]);
  const [employeeStatusView, setEmployeeStatusView] = useState('active');
  const [employeeRoleView, setEmployeeRoleView] = useState('all');
  const [payrollRows, setPayrollRows] = useState([]);
  const [payrollCutoffs, setPayrollCutoffs] = useState([]);
  const [payrollCutoffsLoading, setPayrollCutoffsLoading] = useState(false);
  const [selectedCutoffId, setSelectedCutoffId] = useState('');
  const [cutoffStatusFilter, setCutoffStatusFilter] = useState('all');
  const [cutoffRecords, setCutoffRecords] = useState([]);
  const [cutoffRecordsLoading, setCutoffRecordsLoading] = useState(false);
  const [cutoffForm, setCutoffForm] = useState({
    period_start: '',
    period_end: '',
    pay_date: '',
    status: 'Open',
  });
  const [inventoryRequests, setInventoryRequests] = useState([]);
  const [pageError, setPageError] = useState('');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryAnalytics, setInventoryAnalytics] = useState({
    statusCounts: { pending: 0, approved: 0, rejected: 0, cancelled: 0 },
    lowStockItems: [],
    topRequestedItems: [],
    monthlyTrend: [],
    hourlyTrend: [],
    reportPeriod: null,
  });
  const [inventoryAnalyticsLoading, setInventoryAnalyticsLoading] = useState(false);
  const [inventoryReportOpen, setInventoryReportOpen] = useState(false);
  const [inventoryReportMonth, setInventoryReportMonth] = useState(String(defaultReportPeriod.month));
  const [inventoryReportYear, setInventoryReportYear] = useState(String(defaultReportPeriod.year));
  const [inventoryHistoryOpen, setInventoryHistoryOpen] = useState(false);
  const [inventoryHistoryLoading, setInventoryHistoryLoading] = useState(false);
  const [inventoryHistoryRows, setInventoryHistoryRows] = useState([]);
  const [inventoryHistoryFilters, setInventoryHistoryFilters] = useState({ month: '', year: '', status: '' });
  const [inventoryHistoryYearOptions, setInventoryHistoryYearOptions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [warningsLoading, setWarningsLoading] = useState(false);
  const [warningData, setWarningData] = useState({
    understaffedBranches: [],
    contractsEndingSoon: [],
    counts: { critical: 0, warning: 0 },
  });
  const [deployments, setDeployments] = useState([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(false);
  const [branchDeploymentDialog, setBranchDeploymentDialog] = useState({ open: false, branch: null });
  const [deploymentSearchTerm, setDeploymentSearchTerm] = useState('');
  const [deploymentStatusFilter, setDeploymentStatusFilter] = useState('active');
  const [deploymentFilterMonth, setDeploymentFilterMonth] = useState(String(defaultReportPeriod.month));
  const [deploymentFilterYear, setDeploymentFilterYear] = useState(String(defaultReportPeriod.year));
  const [deploymentForm, setDeploymentForm] = useState({
    user_id: '',
    branch_id: '',
    start_date: '',
    end_date: '',
    shift_start: '',
    shift_end: '',
    notes: '',
    is_primary: true,
  });
  const [branchForm, setBranchForm] = useState({
    branch_code: '',
    branch_name: '',
    address: '',
    city: '',
    province: '',
    min_headcount: 3,
    max_headcount: 5,
    is_active: true,
  });
  const branchHeadcountValidation = React.useMemo(
    () => getBranchHeadcountValidation(branchForm.min_headcount, branchForm.max_headcount),
    [branchForm.min_headcount, branchForm.max_headcount]
  );
  const [branchFilterProvince, setBranchFilterProvince] = useState('');
  const [branchFilterCity, setBranchFilterCity] = useState('');
  const [branchStatusFilter, setBranchStatusFilter] = useState('active');
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [applicantCount, setApplicantCount] = useState(0);
  const attendanceRevisionSections = React.useMemo(
    () => new Set(['overview', 'employees', 'reports', 'attendance', 'manual-requests', 'dtr-photos', 'branches', 'deployments']),
    []
  );
  const normalizeVisibleSection = React.useCallback(
    (section) => (attendanceRevisionSections.has(section) ? section : 'overview'),
    [attendanceRevisionSections]
  );
  // mirror EmployeeDashboard's activeSection design
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();
  
  const basePath = '/dashboard/hr';
  const [manualRequests, setManualRequests] = useState([]);
  const [manualPendingCount, setManualPendingCount] = useState(0);
  const [manualRequestsLoading, setManualRequestsLoading] = useState(false);
  const [manualRequestsError, setManualRequestsError] = useState('');
  const [manualRejectOpen, setManualRejectOpen] = useState(false);
  const [manualRejectReason, setManualRejectReason] = useState('');
  const [manualRejectTarget, setManualRejectTarget] = useState(null);
  const [manualRejectResolution, setManualRejectResolution] = useState('absent');
  const [manualRequestDetail, setManualRequestDetail] = useState(null);
  const [manualRequestEvidence, setManualRequestEvidence] = useState([]);
  const [manualRequestEvidenceLoading, setManualRequestEvidenceLoading] = useState(false);
  const [dtrEvidenceRows, setDtrEvidenceRows] = useState([]);
  const [dtrEvidenceLoading, setDtrEvidenceLoading] = useState(false);
  const [dtrEvidenceError, setDtrEvidenceError] = useState('');
  const [dtrEvidenceFilters, setDtrEvidenceFilters] = useState({
    employeeId: '',
    month: String(defaultReportPeriod.month),
    year: String(defaultReportPeriod.year),
  });
  const [profileOpen, setProfileOpen] = useState(false);
  // Payroll history state
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPages, setHistoryPages] = useState(1);
  const [historyFilters, setHistoryFilters] = useState({ employeeId: '', month: '', year: '' });
  const [historyNotice, setHistoryNotice] = useState('');
  const [payrollHistoryYears, setPayrollHistoryYears] = useState([]);
  // Payroll view mode within Payroll section
  const [payrollMode, setPayrollMode] = useState('current'); // 'current' | 'history'
  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => setMobileOpen(prev => !prev);
  // Edit employee dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [terminatingAccount, setTerminatingAccount] = useState(false);
  // Payroll action dialogs (re-added after refactor removals)
  const [showDisburseDialog, setShowDisburseDialog] = useState(false);
  const [autoSendEmails, setAutoSendEmails] = useState(false);
  const [showRecomputeDialog, setShowRecomputeDialog] = useState(false);
  // Add Employee modal state
  const [addOpen, setAddOpen] = useState(false);
  const [peekEmployeeId, setPeekEmployeeId] = useState('');
  const [addForm, setAddForm] = useState({
    employeeid: '', firstname: '', middlename: '', lastname: '', birthdate: '', age: '', gender: 'Male', civil_status: 'Single', address: '', email: '', phone: '', sss_no: '', philhealth_no: '', pagibig_no: '', password: '', role: 'Employee', date_hired: '', base_salary_per_day: ''
  });
  const [addNoMiddleName, setAddNoMiddleName] = useState(false);

  const availableCities = PROVINCE_CITY_OPTIONS[branchForm.province] || [];
  const branchFilterProvinceOptions = React.useMemo(() => {
    const set = new Set((branches || []).map((b) => String(b.province || '').trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [branches]);
  const branchFilterCityOptions = React.useMemo(() => {
    const list = (branches || []).filter((b) => {
      if (!branchFilterProvince) return true;
      return String(b.province || '').trim() === branchFilterProvince;
    });
    const set = new Set(list.map((b) => String(b.city || '').trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [branches, branchFilterProvince]);
  const visibleBranches = React.useMemo(() => {
    const filtered = (branches || []).filter((b) => {
      const province = String(b.province || '').trim();
      const city = String(b.city || '').trim();
      const active = String(b.is_active ?? b.status ?? '1').toLowerCase();
      const isActive = active === '1' || active === 'true' || active === 'active';
      if (branchFilterProvince && province !== branchFilterProvince) return false;
      if (branchFilterCity && city !== branchFilterCity) return false;
      if (branchStatusFilter === 'active' && !isActive) return false;
      if (branchStatusFilter === 'inactive' && isActive) return false;
      return true;
    });
    const seqOf = (code) => {
      const m = String(code || '').match(/-(\d+)\s*$/);
      return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
    };
    return filtered.sort((a, b) => {
      const seqA = seqOf(a.branch_code);
      const seqB = seqOf(b.branch_code);
      if (seqA !== seqB) return seqA - seqB;
      return String(a.branch_code || '').localeCompare(String(b.branch_code || ''));
    });
  }, [branches, branchFilterProvince, branchFilterCity, branchStatusFilter]);
  const buildBranchCode = useCallback((city, existingBranchId = null) => {
    const cityCode = toCityCode(city);
    if (!cityCode) return '';
    const prefix = `BNK-${cityCode}-`;
    const editingMatch = (branches || []).find((b) => Number(b.id) === Number(existingBranchId || 0));
    if (editingMatch && String(editingMatch.branch_code || '').toUpperCase().startsWith(prefix)) {
      return String(editingMatch.branch_code || '').toUpperCase();
    }
    const used = new Set(
      (branches || [])
        .map((b) => String(b.branch_code || '').toUpperCase())
        .filter((code) => code.startsWith(prefix))
    );
    let seq = 1;
    while (used.has(`${prefix}${String(seq).padStart(2, '0')}`)) seq += 1;
    return `${prefix}${String(seq).padStart(2, '0')}`;
  }, [branches]);
  const [addErrors, setAddErrors] = useState({});
  // Open Add Employee modal (no applicant prefill)
  const openAdd = () => {
    setAddOpen(true);
    setAddErrors({});
    // Peek next employee id (server preferred, fallback to client compute)
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/peek-next-employeeid`, { withCredentials: true });
        if (r?.data?.employeeid) {
          setPeekEmployeeId(r.data.employeeid);
          setAddForm(f => ({ ...f, employeeid: r.data.employeeid }));
          return;
        }
      } catch (e) {}
      try {
        const next = computeNextEmployeeId();
        setPeekEmployeeId(next);
        setAddForm(f => ({ ...f, employeeid: next }));
      } catch (e) {}
    })();
    // default birthdate to current month with default year (2004)
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const defaultYear = 2004;
    const defaultDate = `${defaultYear}-${month}-01`;
    setAddForm(f => ({ ...f, birthdate: defaultDate, age: '' }));
    handleBirthdateChange(defaultDate);
    // set date_hired to today (YYYY-MM-DD)
    const today = new Date();
    const y = today.getFullYear();
    const m2 = String(today.getMonth() + 1).padStart(2, '0');
    const d2 = String(today.getDate()).padStart(2, '0');
    const todayIso = `${y}-${m2}-${d2}`;
    setAddForm(f => ({ ...f, date_hired: todayIso }));
    // ensure base salary defaults to role-based value when opening
    setAddForm(f => {
      const role = f.role || 'Employee';
      const roleLower = String(role).toLowerCase();
      if (f.base_salary_per_day && String(f.base_salary_per_day).trim() !== '') return f;
      let base = '';
      if (roleLower === 'custodian') base = '600';
      else if (roleLower === 'employee') base = '650';
      else if (roleLower === 'hr') base = '700';
      return { ...f, base_salary_per_day: base };
    });
  };
  const closeAdd = () => { setAddOpen(false); setAddForm({ employeeid: peekEmployeeId || '', firstname: '', middlename: '', lastname: '', birthdate: '', age: '', gender: 'Male', civil_status: 'Single', address: '', email: '', phone: '', sss_no: '', philhealth_no: '', pagibig_no: '', password: '', role: 'Employee', date_hired: '' }); setAddErrors({}); setAddFromApplicantId(null); setAddNoMiddleName(false); };

  // If opening Add Employee from an applicant, store the applicant id so we can mark accepted after creation
  const [addFromApplicantId, setAddFromApplicantId] = useState(null);

  // Applicants list (HR view)
  const [applicants, setApplicants] = useState([]);
  const [applicantViewMode, setApplicantViewMode] = useState('active');
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [interviewApplicant, setInterviewApplicant] = useState(null);
  const [interviewSending, setInterviewSending] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    interview_at: '',
    location: 'EZRA Main Office',
    subject: '',
    message: '',
  });
  const fetchApplicants = useCallback(async () => {
    if (!isHr) return;
    try {
      const res = await axios.get(`${API_BASE}/api/applicants`, { withCredentials: true });
      setApplicants(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load applicants', err);
      setSnack({ open: true, message: 'Failed to load applicants', severity: 'error' });
    }
  }, [isHr]);

  const sortedApplicants = React.useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const statusRank = { pending: 0, accepted: 1, rejected: 2 };
    const getStatus = (app) => String(app?.status || 'pending').toLowerCase();
    const getAppliedDate = (app) => app?.date_applied || app?.created_at || app?.applied_at || app?.reviewed_at || null;
    const parseTime = (value) => {
      const parsed = value ? new Date(value).getTime() : 0;
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const isCurrentMonth = (app) => {
      const value = getAppliedDate(app);
      const parsed = value ? new Date(value) : null;
      if (!parsed || Number.isNaN(parsed.getTime())) return true;
      return parsed.getMonth() === currentMonth && parsed.getFullYear() === currentYear;
    };
    const isHistory = (app) => {
      const status = getStatus(app);
      if (status === 'pending') return false;
      return !isCurrentMonth(app);
    };
    return (Array.isArray(applicants) ? applicants : [])
      .filter((app) => (applicantViewMode === 'history' ? isHistory(app) : !isHistory(app)))
      .sort((a, b) => {
        const statusDiff = (statusRank[getStatus(a)] ?? 9) - (statusRank[getStatus(b)] ?? 9);
        if (statusDiff !== 0) return statusDiff;
        return parseTime(getAppliedDate(b)) - parseTime(getAppliedDate(a));
      });
  }, [applicants, applicantViewMode]);

  const applicantQueueCounts = React.useMemo(() => {
    const counts = { active: 0, history: 0, pending: 0, completed: 0 };
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    (Array.isArray(applicants) ? applicants : []).forEach((app) => {
      const status = String(app?.status || 'pending').toLowerCase();
      if (status === 'pending') {
        counts.pending += 1;
        counts.active += 1;
        return;
      }
      counts.completed += 1;
      const value = app?.date_applied || app?.created_at || app?.applied_at || app?.reviewed_at || null;
      const parsed = value ? new Date(value) : null;
      const current = !parsed || Number.isNaN(parsed.getTime()) || (parsed.getMonth() === currentMonth && parsed.getFullYear() === currentYear);
      if (current) counts.active += 1;
      else counts.history += 1;
    });
    return counts;
  }, [applicants]);

  const openInterviewDialog = (applicant) => {
    if (!applicant) return;
    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() + 1);
    baseDate.setHours(9, 0, 0, 0);
    const isoLocal = new Date(baseDate.getTime() - (baseDate.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
    const name = formatName(applicant) || [applicant.firstname, applicant.lastname].filter(Boolean).join(' ') || 'Applicant';
    const roleApplied = applicant.role_applied || applicant.role || 'the role you applied for';
    setInterviewApplicant(applicant);
    setInterviewForm({
      interview_at: applicant.interview_datetime ? String(applicant.interview_datetime).replace(' ', 'T').slice(0, 16) : isoLocal,
      location: applicant.interview_location || 'EZRA Main Office',
      subject: applicant.interview_subject || `Formal Interview Invitation - ${roleApplied}`,
      message: applicant.interview_message || `Dear ${name},\n\nThank you for your interest in the ${roleApplied} position at EZRA General Services.\n\nWe are pleased to invite you to a formal interview to further discuss your qualifications, work experience, and suitability for the role.\n\nPlease review the interview schedule details in this email and reply to confirm your availability. If you need to request a different schedule, kindly let us know as soon as possible.\n\nWe look forward to speaking with you.\n\nSincerely,\nEZRA General Services Human Resources`,
    });
    setInterviewDialogOpen(true);
  };

  const closeInterviewDialog = (force = false) => {
    if (interviewSending && !force) return;
    setInterviewDialogOpen(false);
    setInterviewApplicant(null);
    setInterviewForm({
      interview_at: '',
      location: 'EZRA Main Office',
      subject: '',
      message: '',
    });
  };

  const sendApplicantInterviewEmail = async () => {
    if (!interviewApplicant) return;
    if (!interviewForm.interview_at) {
      setSnack({ open: true, message: 'Interview date and time are required.', severity: 'warning' });
      return;
    }
    try {
      setInterviewSending(true);
      const applicantId = interviewApplicant.id || interviewApplicant.applicant_id;
      const res = await axios.post(`${API_BASE}/api/applicants/${applicantId}/interview-email`, interviewForm, { withCredentials: true });
      setSnack({ open: true, message: res?.data?.message || 'Interview email sent successfully.', severity: 'success' });
      await fetchApplicants();
      closeInterviewDialog(true);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || err?.response?.data?.message || 'Failed to send interview email.', severity: 'error' });
    } finally {
      setInterviewSending(false);
    }
  };

  // Open Add Employee modal prefilled from an applicant record
  const openAddFromApplicant = (applicant) => {
    if (!applicant) return openAdd();
    // Open modal and ensure we peek a valid next employee id in background
    setAddOpen(true);
    setAddErrors({});
    setAddFromApplicantId(applicant.id || applicant.applicant_id || null);
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/peek-next-employeeid`, { withCredentials: true });
        if (r && r.data && typeof r.data.employeeid === 'string') {
          setPeekEmployeeId(r.data.employeeid);
          setAddForm(f => ({ ...f, employeeid: r.data.employeeid }));
        }
      } catch (_) {
        try {
          const next = computeNextEmployeeId();
          setPeekEmployeeId(next);
          setAddForm(f => ({ ...f, employeeid: next }));
        } catch (e) {}
      }
    })();

    // Map applicant fields to form and set sensible defaults
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayIso = `${yyyy}-${mm}-${dd}`;
    // normalize applicant birthdate to yyyy-MM-dd (avoid passing ISO datetime to a date input)
    const rawBd = applicant.birthdate || applicant.birth_date || '';
    let bdStr = '';
    try {
      if (rawBd) {
        if (typeof rawBd === 'string') {
          bdStr = rawBd.includes('T') ? rawBd.split('T')[0] : rawBd;
          // if still not in yyyy-MM-dd, try to parse and format
          if (!/^\d{4}-\d{2}-\d{2}$/.test(bdStr)) {
            const tmp = new Date(rawBd);
            if (!isNaN(tmp.getTime())) bdStr = tmp.toISOString().split('T')[0];
          }
        } else if (rawBd instanceof Date) {
          bdStr = rawBd.toISOString().split('T')[0];
        }
      }
    } catch (e) { bdStr = ''; }

    const mapped = {
      employeeid: peekEmployeeId || '',
      firstname: applicant.firstname || '',
      middlename: applicant.middlename || '',
      lastname: applicant.lastname || '',
      birthdate: bdStr,
      age: applicant.age || '',
      gender: applicant.gender || 'Male',
      civil_status: applicant.civil_status || 'Single',
      address: applicant.address || '',
      email: applicant.email || '',
      phone: applicant.contact || applicant.phone || '',
      sss_no: applicant.sss_no || '',
      philhealth_no: applicant.philhealth_no || '',
      pagibig_no: applicant.pagibig_no || '',
      password: '',
      role: applicant.role_applied || applicant.role || 'Employee',
      date_hired: todayIso,
      base_salary_per_day: applicant.base_salary_per_day || applicant.base_salary || '',
    };
    setAddForm(mapped);
    setAddNoMiddleName(!String(mapped.middlename || '').trim());
    // If we have a birthdate, compute age immediately to reflect on UI
    const bd = mapped.birthdate;
    if (bd) {
      try { handleBirthdateChange(bd); } catch (e) {}
    }
  };

  // handle birthdate -> compute age
  const handleBirthdateChange = (val) => {
    setAddForm(f => ({ ...f, birthdate: val }));
    if (!val) { setAddForm(f => ({ ...f, age: '' })); return; }
    const bd = new Date(val);
    if (isNaN(bd.getTime())) { setAddErrors(e => ({ ...e, birthdate: 'Invalid date' })); return; }
    const today = new Date();
    let a = today.getFullYear() - bd.getFullYear();
    const m = today.getMonth() - bd.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) a--;
    setAddForm(f => ({ ...f, age: String(a) }));
    setAddErrors(e => { const ne = { ...e }; delete ne.birthdate; return ne; });
  };

  // handle birthdate change for edit form -> compute age
  const handleEditBirthdateChange = (val) => {
    setEditForm(f => ({ ...f, birthdate: val }));
    if (!val) { setEditForm(f => ({ ...f, age: '' })); return; }
    const bd = new Date(val);
    if (isNaN(bd.getTime())) { return; }
    const today = new Date();
    let a = today.getFullYear() - bd.getFullYear();
    const m = today.getMonth() - bd.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) a--;
    setEditForm(f => ({ ...f, age: String(a) }));
  };

  const normalizeRoleForEdit = (roleValue) => {
    const r = String(roleValue || '').trim().toLowerCase();
    if (r === 'hr') return 'HR';
    if (r === 'custodian') return 'Custodian';
    return 'Employee';
  };

  const govDigits = (value) => String(value || '').replace(/\D/g, '');
  const formatSssNumber = (value) => {
    const digits = govDigits(value).slice(0, 10);
    if (digits.length <= 2) return digits;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 9)}-${digits.slice(9)}`;
  };
  const formatGov12Number = (value) => {
    const digits = govDigits(value).slice(0, 12);
    if (digits.length <= 4) return digits;
    if (digits.length <= 8) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8)}`;
  };
  const isBlankGovNumber = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    const digits = raw.replace(/\D/g, '');
    return raw === '' || raw === 'n/a' || raw === 'na' || raw === 'none' || raw === 'null' || raw === '-' || raw === '0' || (digits && /^0+$/.test(digits));
  };
  const normalizeGovNumberForPayload = (field, value) => {
    if (isBlankGovNumber(value)) return null;
    return field === 'sss_no' ? formatSssNumber(value) : formatGov12Number(value);
  };
  const formatGovNumberForDisplay = (field, value) => {
    if (isBlankGovNumber(value)) return '';
    return field === 'sss_no' ? formatSssNumber(value) : formatGov12Number(value);
  };
  const getGovNumberError = (field, value) => {
    if (isBlankGovNumber(value)) return '';
    const digits = govDigits(value);
    if (field === 'sss_no') return digits.length === 10 ? '' : 'SSS must be exactly 10 digits';
    if (field === 'philhealth_no') return digits.length === 12 ? '' : 'PhilHealth must be exactly 12 digits';
    if (field === 'pagibig_no') return digits.length === 12 ? '' : 'Pag-IBIG must be exactly 12 digits';
    return '';
  };

  const openEditEmployee = (emp) => {
    if (!emp) return;
    const toTitle = (s) => String(s || '')
      .toLowerCase()
      .replace(/\b([a-z])/g, (m) => m.toUpperCase())
      .replace(/\s+/g, ' ')
      .trim();
    const normalized = {
      ...emp,
      firstname: toTitle(emp.firstname || emp.first_name || ''),
      lastname: toTitle(emp.lastname || emp.last_name || ''),
      middlename: toTitle(emp.middlename || emp.middle_name || ''),
      birthdate: emp.birthdate ? String(emp.birthdate).slice(0, 10) : '',
      age: emp.age || '',
      employeeid: emp.employeeid || emp.employee_no || emp.employee_id || emp.userId || emp.username || '',
      address: emp.address || '',
      phone: emp.phone || emp.contact || '',
      email: emp.email || '',
      role: normalizeRoleForEdit(emp.role),
      sss_no: formatGovNumberForDisplay('sss_no', emp.sss_no),
      philhealth_no: formatGovNumberForDisplay('philhealth_no', emp.philhealth_no),
      pagibig_no: formatGovNumberForDisplay('pagibig_no', emp.pagibig_no),
      baseSalary: emp.baseSalary || emp.base_salary || emp.base_salary_per_day || '',
      salaryType: emp.salaryType || emp.salary_type || 'Daily',
      date_hired: emp.date_hired ? String(emp.date_hired).slice(0, 10) : '',
      password: ''
    };
    setEditForm(normalized);
    setEditOpen(true);
  };

  const validateAddForm = () => {
    const errs = {};
    // Name validation: letters, spaces, hyphen, apostrophe
    const nameRe = /^[A-Za-z\s'-]+$/;
    if (!addForm.firstname || !String(addForm.firstname).trim()) errs.firstname = 'First name is required';
    else if (!nameRe.test(String(addForm.firstname).trim())) errs.firstname = 'First name must contain letters only';
    if (!addForm.lastname || !String(addForm.lastname).trim()) errs.lastname = 'Last name is required';
    else if (!nameRe.test(String(addForm.lastname).trim())) errs.lastname = 'Last name must contain letters only';
    if (!addForm.address || !String(addForm.address).trim()) errs.address = 'Address is required';
    if (!addForm.role) errs.role = 'Role is required';
    if (!addForm.email || String(addForm.email).indexOf('@') === -1) errs.email = 'Email must include @';
    const phoneDigits = String(addForm.phone || '').replace(/\D/g, '');
    if (!/^09\d{9}$/.test(phoneDigits)) errs.phone = 'Contact number must start with 09 and be 11 digits';
    if (!addForm.birthdate) errs.birthdate = 'Birthdate is required';
    // age is derived; only flag if we cannot compute a number
    if (!addForm.age || !/^[0-9]+$/.test(addForm.age)) errs.age = 'Invalid age';
    // password is optional on creation; remove hard requirement
    // SSS/PhilHealth/Pag-IBIG validations: optional but must match required lengths if provided
    const sssError = getGovNumberError('sss_no', addForm.sss_no);
    const philError = getGovNumberError('philhealth_no', addForm.philhealth_no);
    const pagError = getGovNumberError('pagibig_no', addForm.pagibig_no);
    if (sssError) errs.sss_no = sssError;
    if (philError) errs.philhealth_no = philError;
    if (pagError) errs.pagibig_no = pagError;
    // base salary per day required and numeric
    if (!addForm.base_salary_per_day || String(addForm.base_salary_per_day).trim() === '') errs.base_salary_per_day = 'Base salary per day is required';
    if (addForm.base_salary_per_day && !/^[0-9]+(\.[0-9]{1,2})?$/.test(String(addForm.base_salary_per_day))) errs.base_salary_per_day = 'Invalid amount';
    setAddErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Live-validate a single field as the user types
  const validateField = (field, value) => {
    setAddErrors(prev => {
      const errs = { ...prev };
      const v = String(value || '').trim();
      switch (field) {
        case 'firstname':
          if (!v) errs.firstname = 'First name is required';
          else if (!/^[A-Za-z\s'-]+$/.test(v)) errs.firstname = 'First name must contain letters only';
          else delete errs.firstname;
          break;
        case 'lastname':
          if (!v) errs.lastname = 'Last name is required';
          else if (!/^[A-Za-z\s'-]+$/.test(v)) errs.lastname = 'Last name must contain letters only';
          else delete errs.lastname;
          break;
        case 'address':
          if (!v) errs.address = 'Address is required'; else delete errs.address;
          break;
        case 'email':
          if (!v || v.indexOf('@') === -1) errs.email = 'Email must include @'; else delete errs.email;
          break;
        case 'phone':
          {
            const digits = v.replace(/\D/g, '');
            if (!/^09\d{9}$/.test(digits)) errs.phone = 'Contact number must start with 09 and be 11 digits'; else delete errs.phone;
          }
          break;
        case 'birthdate':
          if (!v) errs.birthdate = 'Birthdate is required'; else {
            const bd = new Date(v);
            const year = bd.getFullYear();
            if (isNaN(bd.getTime()) || year < 1983 || year > 2004) errs.birthdate = 'Year must be between 1983 and 2004'; else delete errs.birthdate;
          }
          break;
        case 'middlename':
          // optional, but if provided must be letters/spaces/hyphen/apostrophe
          if (v && !/^[A-Za-z\s'-]+$/.test(v)) errs.middlename = 'Middle name must contain letters only'; else delete errs.middlename;
          break;
        case 'ssn':
        case 'sss_no':
          {
            const err = getGovNumberError('sss_no', v);
            if (err) errs.sss_no = err; else delete errs.sss_no;
          }
          break;
        case 'philhealth_no':
          {
            const err = getGovNumberError('philhealth_no', v);
            if (err) errs.philhealth_no = err; else delete errs.philhealth_no;
          }
          break;
        case 'pagibig_no':
          {
            const err = getGovNumberError('pagibig_no', v);
            if (err) errs.pagibig_no = err; else delete errs.pagibig_no;
          }
          break;
        case 'password':
          // optional on add; if provided, enforce min length
          if (!!v && v.length > 0 && v.length < 8) errs.password = 'Min 8 chars'; else delete errs.password;
          break;
        case 'base_salary_per_day':
          if (!v) errs.base_salary_per_day = 'Base salary per day is required';
          else if (!/^[0-9]+(\.[0-9]{1,2})?$/.test(v)) errs.base_salary_per_day = 'Invalid amount';
          else delete errs.base_salary_per_day;
          break;
        default:
          break;
      }
      return errs;
    });
  };

  const handleChange = (field) => (e) => {
    const toTitleName = (s) => String(s || '')
      .toLowerCase()
      .replace(/\b([a-z])/g, (m) => m.toUpperCase())
      .replace(/\s+/g, ' ')
      .trimStart();
    let value = e && e.target ? e.target.value : e;
    if (field === 'firstname' || field === 'middlename' || field === 'lastname') {
      value = toTitleName(filterLetters(value));
    } else if (field === 'phone') {
      value = filterPhone(value);
    } else if (field === 'sss_no') {
      value = formatSssNumber(value);
    } else if (field === 'philhealth_no' || field === 'pagibig_no') {
      value = formatGov12Number(value);
    }
    // special handling for role select and birthdate
    if (field === 'role') {
      const v = value;
      let base = '';
      if (v === 'Custodian') base = '600';
      else if (v === 'Employee') base = '650';
      else if (v === 'HR') base = '700';
      setAddForm(f => ({ ...f, role: v, base_salary_per_day: f.base_salary_per_day || base }));
      validateField('role', v);
      validateField('base_salary_per_day', addForm.base_salary_per_day || base);
      return;
    }

    if (field === 'birthdate') {
      setAddForm(f => ({ ...f, birthdate: value }));
      handleBirthdateChange(value);
      validateField('birthdate', value);
      return;
    }

    // If editing identity number fields, restrict input to digits and enforce max length client-side
    if (['sss_no','philhealth_no','pagibig_no'].includes(field)) {
      const formatted = field === 'sss_no' ? formatSssNumber(value) : formatGov12Number(value);
      setAddForm(f => ({ ...f, [field]: formatted }));
      validateField(field, formatted);
      return;
    }

    setAddForm(f => ({ ...f, [field]: value }));
    validateField(field, value);
  };

  const normalizeRoleValue = (v) => {
    const s = String(v ?? '').trim().toLowerCase();
    // handle common labels/variants from UI
    if (['custodian', 'custodian ', 'custodian\n'].includes(s)) return 'custodian';
    if (['employee', 'emp'].includes(s)) return 'employee';
    if (['hr', 'human resources', 'humanresources'].includes(s)) return 'hr';
    if (['admin', 'administrator'].includes(s)) return 'admin';
    // handle Title Case labels
    if (s === 'custodian') return 'custodian';
    if (s === 'employee') return 'employee';
    if (s === 'hr') return 'hr';
    if (s === 'admin') return 'admin';
    return s; // fallthrough so backend can reject if truly unknown
  };

  const submitAdd = async () => {
    if (!validateAddForm()) return;
    try {
        // Map client form names to server-expected payload keys
      const payload = {
        employeeid: addForm.employeeid || peekEmployeeId || undefined,
        firstname: addForm.firstname,
        middlename: addNoMiddleName ? null : (String(addForm.middlename || '').trim() || null),
        lastname: addForm.lastname,
        birthdate: addForm.birthdate || null,
        age: addForm.age || null,
        gender: addForm.gender,
        civil_status: addForm.civil_status,
        address: addForm.address,
        email: addForm.email,
        phone: addForm.phone,
        // send null for optional identity numbers when blank so server treats them as not provided
        sss_no: normalizeGovNumberForPayload('sss_no', addForm.sss_no),
        philhealth_no: normalizeGovNumberForPayload('philhealth_no', addForm.philhealth_no),
        pagibig_no: normalizeGovNumberForPayload('pagibig_no', addForm.pagibig_no),
        password: addForm.password || null,
        role: normalizeRoleValue(addForm.role),
        date_hired: addForm.date_hired || null,
        // server expects `baseSalary` (camelCase)
        baseSalary: (addForm.base_salary_per_day !== undefined && addForm.base_salary_per_day !== null) ? addForm.base_salary_per_day : null,
      };
      console.log('[add-employee] raw role=', addForm.role, 'normalized=', normalizeRoleValue(addForm.role));
      const res = await axios.post(`${API_BASE}/api/employees`, payload, { withCredentials: true });
      setSnack({ open: true, message: 'Employee added', severity: 'success' });
      // If this add was initiated from an applicant, mark that applicant accepted on the server
      try {
        if (addFromApplicantId) {
          // call accept endpoint to mark applicant accepted (server may already have created the user in other flows)
          await axios.post(`${API_BASE}/api/applicants/${addFromApplicantId}/accept`, {}, { withCredentials: true });
          // notify other parts of the app to refresh applicant counts/lists
          try { window.dispatchEvent(new Event('applicantsChanged')); } catch (e) {}
        }
      } catch (e) {
        // non-fatal: show a gentle warning but continue
        console.warn('Failed to mark applicant accepted', e);
        setSnack({ open: true, message: 'Employee created but failed to update applicant status', severity: 'warning' });
      }
      closeAdd();
      fetchEmployees();
      try { fetchPayroll(filterMonth, filterYear); } catch (e) {}
      // advance peek employee id for next add using the id we just submitted (not server-assigned id)
      try {
        const next = computeNextEmployeeId(addForm.employeeid);
        setPeekEmployeeId(next);
      } catch (e) {}
    } catch (err) {
      console.error('Add employee failed', err);
      // Prefer showing structured validation errors if provided by the server
      if (err?.response) {
        const { status, data } = err.response;
        if (data?.validationErrors) {
          setAddErrors(data.validationErrors || {});
          setSnack({ open: true, message: 'Validation errors - please fix highlighted fields', severity: 'error' });
        } else {
          // Try to show a helpful message from server response; fall back to status text
          let msg = '';
          if (data && typeof data === 'string') msg = data;
          else if (data && data.error) msg = String(data.error);
          else if (data && data.message) msg = String(data.message);
          else msg = `Server returned ${status}`;
          // shorten long messages
          if (String(msg).length > 300) msg = String(msg).slice(0, 300) + '...';
          setSnack({ open: true, message: msg || 'Failed to add employee', severity: 'error' });
        }
      } else {
        // Network/unknown error
        setSnack({ open: true, message: err.message || 'Failed to add employee', severity: 'error' });
      }
    }
  };

  // Payroll filters / pagination
  const [filterMonth, setFilterMonth] = useState(defaultReportPeriod.month);
  const [filterYear, setFilterYear] = useState(defaultReportPeriod.year);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [payrollSearchTerm, setPayrollSearchTerm] = useState('');
  const [selectedDay, setSelectedDay] = useState('today');
  const [attendanceEmployeeFilter, setAttendanceEmployeeFilter] = useState('');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all');
  const [attendanceMethodFilter, setAttendanceMethodFilter] = useState('all');
  const [attendancePersonalOnly, setAttendancePersonalOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const esRef = useRef(null);
  const [payrollEditForm, setPayrollEditForm] = useState({
    month: filterMonth,
    year: filterYear,
    role: '',
    baseSalary: '',
    overtimeHours: 0,
    absences: 0,
    holidays: 0,
    bonuses: 0,
    salaryType: 'Monthly',
    sssDeduct: false,
    philhealthDeduct: false,
    pagibigDeduct: false,
  });

  // Payroll dialog state (declare before effects that reference them)
  const [editingPayrollRow, setEditingPayrollRow] = useState(null);
  const [showEditPayrollDialog, setShowEditPayrollDialog] = useState(false);
  // Salary history dialog state
  const [salaryHistoryOpen, setSalaryHistoryOpen] = useState(false);
  const [salaryHistoryRows, setSalaryHistoryRows] = useState([]);
  const [salaryHistoryLoading, setSalaryHistoryLoading] = useState(false);
  const [salaryHistoryEmployee, setSalaryHistoryEmployee] = useState(null);
  // Photo upload modal (custodian-only)
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoModalEmployee, setPhotoModalEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetailOpen, setEmployeeDetailOpen] = useState(false);
  const [employeeDetailShowPhotos, setEmployeeDetailShowPhotos] = useState(true);
  const [employeePhotos, setEmployeePhotos] = useState([]);
  const [loadingEmployeePhotos, setLoadingEmployeePhotos] = useState(false);
  const employeePhotoRequestRef = useRef('');
  const reportTranscriptRef = useRef(null);
  const transcriptRequestRef = useRef(0);
  const getModalEmployeeId = (emp) => String(
    emp?.employeeid ||
    emp?.employeeId ||
    emp?.employee_id ||
    emp?.username ||
    ''
  ).padStart(8, '0');
  const openPhotoModal = (emp) => {
    const modalEmployeeId = getModalEmployeeId(emp);
    console.info('[photo-modal] open', { employeeid: modalEmployeeId });
    setPhotoModalEmployee(emp);
    setPhotoModalOpen(true);
  };
  const closePhotoModal = () => { setPhotoModalOpen(false); setPhotoModalEmployee(null); };
  const modalEmployeeId = getModalEmployeeId(photoModalEmployee);

  useEffect(() => {
    if (editingPayrollRow) {
      setPayrollEditForm({
        month: editingPayrollRow.month || filterMonth,
        year: editingPayrollRow.year || filterYear,
        role: editingPayrollRow.role || '',
        baseSalary: editingPayrollRow.baseSalary || editingPayrollRow.base_salary || '',
        overtimeHours: editingPayrollRow.overtimeHours || editingPayrollRow.overtime_hours || 0,
        absences: editingPayrollRow.absences || 0,
        holidays: editingPayrollRow.holidays || 0,
        bonuses: editingPayrollRow.bonuses || editingPayrollRow.bonus || 0,
        salaryType: editingPayrollRow.salaryType || 'Monthly',
        sssDeduct: !!editingPayrollRow.sss_deduct || !!editingPayrollRow.sss_deduction,
        philhealthDeduct: !!editingPayrollRow.philhealth_deduct || !!editingPayrollRow.philhealth_deduction,
        pagibigDeduct: !!editingPayrollRow.pagibig_deduct || !!editingPayrollRow.pagibig_deduction,
      });
    } else {
      setPayrollEditForm(f => ({ ...f, month: filterMonth, year: filterYear }));
    }
  }, [editingPayrollRow, filterMonth, filterYear]);

  // Attendance rows (HR view)
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [homeAttendanceLoading, setHomeAttendanceLoading] = useState(false);
  const [homeAttendanceError, setHomeAttendanceError] = useState('');
  const [homeHasOpenShift, setHomeHasOpenShift] = useState(false);
  const [homeIsOnBreak, setHomeIsOnBreak] = useState(false);
  const [homeOpenShiftRecord, setHomeOpenShiftRecord] = useState(null);
  const [homeBreakCountdownMs, setHomeBreakCountdownMs] = useState(0);
  const [homeAttendanceRows, setHomeAttendanceRows] = useState([]);
  const [myPayslip, setMyPayslip] = useState(null);
  const [myPayslipLoading, setMyPayslipLoading] = useState(false);
  const [myPayslipError, setMyPayslipError] = useState('');
  const [myPayslipCutoffs, setMyPayslipCutoffs] = useState([]);
  const [selectedMyPayslipCutoffId, setSelectedMyPayslipCutoffId] = useState('');
  const [generalMonthlyReport, setGeneralMonthlyReport] = useState(null);
  const [generalMonthlyReportLoading, setGeneralMonthlyReportLoading] = useState(false);
  const [generalMonthlyReportError, setGeneralMonthlyReportError] = useState('');
  const [reportRoleFilter, setReportRoleFilter] = useState('all');
  const [individualTranscriptReport, setIndividualTranscriptReport] = useState(null);
  const [individualTranscriptLoading, setIndividualTranscriptLoading] = useState(false);
  const [individualTranscriptError, setIndividualTranscriptError] = useState('');
  const [selectedTranscriptEmployeeId, setSelectedTranscriptEmployeeId] = useState('');
  const [selectedTranscriptUserId, setSelectedTranscriptUserId] = useState('');
  const [reportCutoffId, setReportCutoffId] = useState('');
  const [reportEmailOpen, setReportEmailOpen] = useState(false);
  const [reportEmailTarget, setReportEmailTarget] = useState('');
  const [reportEmailType, setReportEmailType] = useState('general_monthly');
  const [reportEmailSending, setReportEmailSending] = useState(false);
  const formatAttendanceDate = (row) => {
    const raw = row?.log_date || row?.date;
    return formatDateWithWeekday(raw);
  };

  const getAttendanceStatus = (row) => {
    const lateMinutes = Number(row?.late_minutes || row?.lateMinutes || 0) || 0;
    const lateLabel = lateMinutes > 0 ? `Late (${lateMinutes} min)` : 'Late';
    const explicitStatus = cleanStatus(row?.attendance_status_label || row?.status_label || '');
    if (explicitStatus.toLowerCase().includes('late')) return lateLabel;
    if (explicitStatus) return explicitStatus;
    const manualStatus = String(row?.manual_request_status || '').trim().toLowerCase();
    const manualResolution = String(row?.manual_request_resolution || '').trim().toLowerCase();
    if (manualStatus === 'pending') return 'Pending Manual Review';
    if (manualStatus === 'approved') return 'Present';
    if (manualStatus === 'rejected' && manualResolution === 'late') return lateLabel;
    if (manualStatus === 'rejected' && manualResolution === 'absent') return 'Absent';
    const leaveWorkflowStatus = String(row?.leave_request_status || '').trim().toLowerCase();
    const leavePaid = Number(row?.leave_request_paid || 0) === 1;
    if (leaveWorkflowStatus === 'pending') return leavePaid ? 'Pending Paid Leave' : 'Pending Unpaid Leave';
    if (leaveWorkflowStatus === 'approved') return leavePaid ? 'Paid Leave' : 'Unpaid Leave';
    const isAbsent = row?.is_absent === true || row?.is_absent === 1 || row?.is_absent === '1';
    if (isAbsent) return 'Absent';
    if (row?.leave_approved) return 'Leave';
    if (row?.half_day === 1 || row?.half_day === true) return 'Half Day';
    if (lateMinutes > 0 || row?.late === 1 || row?.late === true) return lateLabel;
    if (row?.log_time || row?.time_in || row?.timeIn || row?.time_out || row?.timeOut) return 'Present';
    const cleaned = cleanStatus(row?.status || row?.attendance_status || '');
    if (cleaned.toLowerCase() === 'absent') return 'Absent';
    if (cleaned.toLowerCase() === 'late') return lateLabel;
    if (cleaned.toLowerCase() === 'present') return 'Present';
    return '';
  };

  const getAttendanceEditStatusValue = (row) => {
    const status = String(getAttendanceStatus(row) || '').toLowerCase();
    if (status.includes('leave') || row?.leave_approved) return 'leave';
    if (status.includes('absent') || row?.is_absent === true || row?.is_absent === 1 || row?.is_absent === '1') return 'absent';
    if (status.includes('late') || Number(row?.late_minutes || row?.lateMinutes || 0) > 0) return 'late';
    return 'present';
  };

  const getAttendanceRoleLabel = (row) => {
    const raw = String(row?.role || row?.user_role || row?.employee_role || row?.account_role || '').trim();
    if (!raw) return '-';
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  };

  const applyAttendanceStatusToForm = (currentForm, status) => {
    const nextStatus = String(status || 'present');
    const next = { ...currentForm, status: nextStatus };
    if (nextStatus === 'present') {
      next.is_absent = false;
      next.leave_approved = false;
      next.late_minutes = 0;
    } else if (nextStatus === 'late') {
      next.is_absent = false;
      next.leave_approved = false;
      next.late_minutes = Math.max(1, Number(next.late_minutes || 0));
    } else if (nextStatus === 'leave') {
      next.is_absent = false;
      next.leave_approved = true;
      next.late_minutes = 0;
    } else if (nextStatus === 'absent') {
      next.is_absent = true;
      next.leave_approved = false;
      next.late_minutes = 0;
    }
    return next;
  };

  const getAttendanceMethod = (row) => {
    const source = String(row?.attendance_source || '').trim().toLowerCase();
    const kioskId = String(row?.kiosk_id || '').trim();
    const isFace = row?.face_recognized === 1 || row?.face_recognized === true || source.includes('face') || source.includes('kiosk') || Boolean(kioskId);
    if (isFace) return 'Face';
    const explicitMethod = String(row?.attendance_method_label || row?.method || '').trim();
    if (explicitMethod) return explicitMethod;
    const statusLabel = String(getAttendanceStatus(row) || '').toLowerCase();
    if (statusLabel.includes('leave')) return 'Leave Request';
    const manualStatus = String(row?.manual_request_status || '').trim().toLowerCase();
    if (manualStatus === 'pending') return 'Manual Review';
    if (manualStatus === 'approved' || manualStatus === 'rejected') return 'Manual';
    const role = String(row?.user_role || row?.role || '').toLowerCase();
    const isCustodian = role === 'custodian';
    const hasAttendance = !!(row?.log_time || row?.time_in || row?.timeIn || row?.time_out || row?.timeOut || row?.is_absent || row?.leave_approved || row?.manual_request_status);
    if (!hasAttendance) return '-';
    if (isCustodian || role === 'hr' || role === 'employee') return 'Manual';
    return '-';
  };

  const renderAttendanceStatusChip = (statusLabel) => {
    const cleaned = cleanStatus(statusLabel);
    const s = cleaned.toLowerCase();
    let sx = { fontWeight: 700 };
    if (s.includes('pending')) sx = { ...sx, bgcolor: '#f57c00', color: '#fff' };
    else if (s.includes('present')) sx = { ...sx, bgcolor: '#2e7d32', color: '#fff' };
    else if (s.includes('half day')) sx = { ...sx, bgcolor: '#8b5cf6', color: '#fff' };
    else if (s.includes('late')) sx = { ...sx, bgcolor: '#fbc02d', color: '#1f2937' };
    else if (s === 'absent') sx = { ...sx, bgcolor: '#d32f2f', color: '#fff' };
    else if (s.includes('leave')) sx = { ...sx, bgcolor: '#1976d2', color: '#fff' };
    else sx = { ...sx, bgcolor: '#64748b', color: '#fff' };
    return <Chip size="small" label={cleaned || '-'} sx={sx} />;
  };

  const renderAttendanceMethodChip = (methodLabel) => {
    const method = String(methodLabel || '').trim();
    if (!method || method === '-') return '-';
    const isFace = method.toLowerCase() === 'face';
    return (
      <Chip
        size="small"
        variant="outlined"
        label={method}
        sx={isFace
          ? { fontWeight: 700, borderColor: '#1565c0', color: '#1565c0', bgcolor: '#e3f2fd' }
          : { fontWeight: 700, borderColor: '#6b7280', color: '#374151', bgcolor: '#f3f4f6' }}
      />
    );
  };

  const renderDtrEmployeeReviewChip = (status) => {
    const s = String(status || '').trim().toLowerCase();
    const label = s === 'validated'
      ? 'Employee Validated'
      : s === 'needs_edit'
        ? 'Needs Attendance Edit'
        : s === 'rejected'
          ? 'Employee Rejected'
          : 'Pending Employee Review';
    const color = s === 'validated' ? 'success' : (s === 'needs_edit' ? 'warning' : (s === 'rejected' ? 'error' : 'default'));
    return <Chip size="small" color={color} label={label} sx={{ fontWeight: 700 }} />;
  };

  const normalizeEmployeeId = useCallback((value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    return /^\d+$/.test(raw) ? raw.padStart(8, '0') : raw;
  }, []);

  const resolveAttendanceRowEmployee = useCallback((row) => {
    if (!row) return null;
    const rowUserId = String(row?.user_id || row?.userId || row?.employee_id || '').trim();
    const directEmployeeId = normalizeEmployeeId(row?.employee_no || row?.employeeid || row?.employeeNo || '');
    return (employees || []).find((emp) => {
      const empUserId = String(emp?.id || emp?.user_id || emp?.userId || '').trim();
      const empEmployeeId = normalizeEmployeeId(emp?.employeeid || emp?.employee_no || emp?.employeeNo || emp?.username || '');
      return (rowUserId && empUserId && rowUserId === empUserId) ||
        (directEmployeeId && empEmployeeId && directEmployeeId === empEmployeeId);
    }) || null;
  }, [employees, normalizeEmployeeId]);

  const getAttendanceRowEmployeeId = useCallback((row) => {
    const directEmployeeId = normalizeEmployeeId(
      row?.employee_no ||
      row?.employeeid ||
      row?.employeeNo ||
      ''
    );
    if (directEmployeeId) return directEmployeeId;
    const matchedEmployee = resolveAttendanceRowEmployee(row);
    if (matchedEmployee) {
      return normalizeEmployeeId(matchedEmployee.employeeid || matchedEmployee.employee_no || matchedEmployee.employeeNo || matchedEmployee.username || '');
    }
    return normalizeEmployeeId(row?.employee_id || row?.userId || row?.user_id || '');
  }, [normalizeEmployeeId, resolveAttendanceRowEmployee]);

  const currentEmployeeId = normalizeEmployeeId(user?.employeeid || user?.employee_no || user?.employeeNo || '');
  const currentUserId = String(user?.id || user?.user_id || user?.userId || '').trim();
  const currentUsername = String(user?.username || '').trim().toLowerCase();
  const isCurrentUserAttendanceRow = useCallback((row) => {
    if (!row) return false;
    const rowEmployeeId = getAttendanceRowEmployeeId(row);
    const rowUserId = String(row?.user_id || row?.userId || '').trim();
    const rowUsername = String(row?.username || '').trim().toLowerCase();
    if (currentEmployeeId && rowEmployeeId && rowEmployeeId === currentEmployeeId) return true;
    if (currentUserId && rowUserId && rowUserId === currentUserId) return true;
    if (currentUsername && rowUsername && rowUsername === currentUsername) return true;
    return false;
  }, [getAttendanceRowEmployeeId, currentEmployeeId, currentUserId, currentUsername]);
  const scopedAttendanceRows = React.useMemo(() => {
    if (!attendancePersonalOnly) return attendanceRows;
    return attendanceRows.filter((row) => isCurrentUserAttendanceRow(row));
  }, [attendanceRows, attendancePersonalOnly, isCurrentUserAttendanceRow]);

  const uniqueDays = React.useMemo(() => {
    const daySet = new Set();
    scopedAttendanceRows.forEach((row) => {
      const label = formatAttendanceDate(row);
      if (label) daySet.add(label);
    });
    return Array.from(daySet);
  }, [scopedAttendanceRows]);

  const attendanceEmployeeOptions = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    (attendanceRows || []).forEach((row) => {
      const raw = String(
        row.employee_no ||
        row.employeeid ||
        row.employeeNo ||
        row.employee_id ||
        row.userId ||
        ''
      ).replace(/\D/g, '');
      if (!raw) return;
      const padded = raw.padStart(8, '0');
      if (seen.has(padded)) return;
      seen.add(padded);
      out.push(padded);
    });
    return out.sort((a, b) => a.localeCompare(b));
  }, [attendanceRows]);

  const filteredAttendanceRows = React.useMemo(() => {
    const todayKey = (() => {
      try {
        return new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Asia/Manila',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date());
      } catch (e) {
        return toDateKey(new Date());
      }
    })();
    const withDay = selectedDay === 'all'
      ? scopedAttendanceRows
      : selectedDay === 'today'
        ? scopedAttendanceRows.filter((row) => toDateKey(row?.log_date || row?.date) === todayKey)
        : scopedAttendanceRows.filter((row) => formatAttendanceDate(row) === selectedDay);

    const typedDigits = String(attendanceEmployeeFilter || '').replace(/\D/g, '');
    const typedCanonical = typedDigits ? String(parseInt(typedDigits, 10)) : '';
    const withEmployee = (!typedDigits || !typedCanonical || typedCanonical === 'NaN') ? withDay : withDay.filter((row) => {
      const rowDigits = String(
        row.employee_no ||
        row.employeeid ||
        row.employeeNo ||
        row.employee_id ||
        row.userId ||
        ''
      ).replace(/\D/g, '');
      if (!rowDigits) return false;
      return String(parseInt(rowDigits, 10)) === typedCanonical;
    });

    const statusFilter = String(attendanceStatusFilter || 'all').toLowerCase();
    const filteredByStatus = statusFilter === 'all' ? withEmployee : withEmployee.filter((row) => {
      const status = String(getAttendanceStatus(row) || '').toLowerCase();
      if (statusFilter === 'present') return status.includes('present');
      if (statusFilter === 'absent') return status.includes('absent');
      if (statusFilter === 'leave') return status.includes('leave');
      if (statusFilter === 'pending_leave') return status.includes('pending') && status.includes('leave');
      if (statusFilter === 'paid_leave') return status.includes('paid leave') && !status.includes('pending');
      if (statusFilter === 'unpaid_leave') return status.includes('unpaid leave') && !status.includes('pending');
      if (statusFilter === 'half_day') return status.includes('half');
      return true;
    });

    const methodFilter = String(attendanceMethodFilter || 'all').toLowerCase();
    const filtered = methodFilter === 'all' ? filteredByStatus : filteredByStatus.filter((row) => {
      const method = String(getAttendanceMethod(row) || '').toLowerCase();
      if (methodFilter === 'face') return method === 'face';
      if (methodFilter === 'manual') return method.includes('manual');
      if (methodFilter === 'leave') return method.includes('leave');
      return true;
    });

    return [...filtered].sort((a, b) => {
      const aDate = toDateKey(a?.log_date || a?.date) || '';
      const bDate = toDateKey(b?.log_date || b?.date) || '';
      if (aDate !== bDate) return bDate.localeCompare(aDate);
      const aTime = String(a?.log_time || a?.time_in || a?.time_out || a?.timeOut || '00:00:00').slice(0, 8);
      const bTime = String(b?.log_time || b?.time_in || b?.time_out || b?.timeOut || '00:00:00').slice(0, 8);
      if (aTime !== bTime) return bTime.localeCompare(aTime);
      return getAttendanceRowEmployeeId(a).localeCompare(getAttendanceRowEmployeeId(b));
    });
  }, [scopedAttendanceRows, selectedDay, attendanceEmployeeFilter, attendanceStatusFilter, attendanceMethodFilter, getAttendanceRowEmployeeId]);
  // Live attendance events for small realtime display (SSE)
  const [liveAttendance, setLiveAttendance] = useState([]);
  const [attendanceEditOpen, setAttendanceEditOpen] = useState(false);
  const [attendanceEditRow, setAttendanceEditRow] = useState(null);
  const [attendanceEditForm, setAttendanceEditForm] = useState({ log_time: '', time_out: '', status: 'present', is_absent: false, leave_approved: false, late_minutes: 0 });
  // Attendance detail modal state (view + auto-refresh)
  const [attendanceDetailOpen, setAttendanceDetailOpen] = useState(false);
  const [attendanceDetailRow, setAttendanceDetailRow] = useState(null);
  const [attendanceDetailLoading, setAttendanceDetailLoading] = useState(false);
  const [cutoffAttendanceOpen, setCutoffAttendanceOpen] = useState(false);
  const [cutoffAttendanceRows, setCutoffAttendanceRows] = useState([]);
  const [cutoffAttendanceLoading, setCutoffAttendanceLoading] = useState(false);
  const [cutoffAttendanceContext, setCutoffAttendanceContext] = useState(null);
  const attendanceDetailIntervalRef = useRef(null);
  const attendanceDetailLogRef = useRef(false);
  const initialHrLoadDoneRef = useRef(false);
  // Use filterMonth/filterYear as defaults to avoid referencing calMonth before it's initialized
  const fetchAttendance = useCallback(async (m = filterMonth, y = filterYear) => {
    if (!isHr) return [];
    try {
      // Use HR-specific endpoint when logged-in user is HR so we get linked employee info
      const endpoint = (normalizedRole === 'hr') ? `${API_BASE}/api/attendance/all?month=${m}&year=${y}` : `${API_BASE}/api/attendance?month=${m}&year=${y}`;
      const res = await axios.get(endpoint, { withCredentials: true });
      const rows = Array.isArray(res.data) ? res.data : [];
      setAttendanceRows(rows);
      return rows;
    } catch (err) {
      console.error('Failed to load attendance', err);
      setSnack({ open: true, message: 'Failed to load attendance', severity: 'error' });
      setAttendanceRows([]);
      setPageError('Failed to load attendance.');
      return [];
    }
  }, [filterMonth, filterYear, isHr, normalizedRole]);

  const fetchAttendanceRowsForRange = useCallback(async ({ employeeId, periodStart, periodEnd, employeeName, employeeRole }) => {
    if (!isHr || !employeeId || !periodStart || !periodEnd) return;
    setCutoffAttendanceLoading(true);
    try {
      const startKey = String(periodStart).slice(0, 10);
      const endKey = String(periodEnd).slice(0, 10);
      const start = new Date(`${startKey}T00:00:00`);
      const end = new Date(`${endKey}T00:00:00`);
      const monthKeys = new Set();
      const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      const stop = new Date(end.getFullYear(), end.getMonth(), 1);
      while (cursor <= stop) {
        monthKeys.add(`${cursor.getFullYear()}-${cursor.getMonth() + 1}`);
        cursor.setMonth(cursor.getMonth() + 1);
      }
      const responses = await Promise.all(
        Array.from(monthKeys).map((key) => {
          const [year, month] = key.split('-').map(Number);
          return axios.get(`${API_BASE}/api/attendance/all?month=${month}&year=${year}`, { withCredentials: true });
        })
      );
      const seen = new Set();
      const targetEmployeeId = normalizeEmployeeId(employeeId);
      const filteredRows = responses
        .flatMap((res) => (Array.isArray(res.data) ? res.data : []))
        .filter((row) => {
          const dedupeKey = row?.id
            ? `id:${row.id}`
            : `${getAttendanceRowEmployeeId(row)}:${toDateKey(row?.log_date || row?.date)}:${row?.log_time || row?.time_in || ''}:${row?.time_out || row?.timeOut || ''}`;
          if (seen.has(dedupeKey)) return false;
          seen.add(dedupeKey);
          const rowEmployeeId = getAttendanceRowEmployeeId(row);
          if (rowEmployeeId !== targetEmployeeId) return false;
          const dateKey = toDateKey(row?.log_date || row?.date);
          return Boolean(dateKey) && dateKey >= startKey && dateKey <= endKey;
        })
        .sort((a, b) => {
          const aKey = `${toDateKey(a?.log_date || a?.date) || ''} ${a?.log_time || a?.time_in || '00:00:00'}`;
          const bKey = `${toDateKey(b?.log_date || b?.date) || ''} ${b?.log_time || b?.time_in || '00:00:00'}`;
          return aKey.localeCompare(bKey);
        });
      setCutoffAttendanceContext({ employeeId: targetEmployeeId, periodStart: startKey, periodEnd: endKey, employeeName, employeeRole });
      setCutoffAttendanceRows(filteredRows);
      setCutoffAttendanceOpen(true);
    } catch (err) {
      console.error('Failed to load cutoff attendance records', err);
      setSnack({ open: true, message: 'Failed to load cutoff attendance records', severity: 'error' });
    } finally {
      setCutoffAttendanceLoading(false);
    }
  }, [API_BASE, getAttendanceRowEmployeeId, isHr, normalizeEmployeeId, toDateKey]);

  const refreshHomeAttendance = useCallback(async () => {
    setHomeAttendanceLoading(true);
    setHomeAttendanceError('');
    try {
      const now = new Date();
      const rows = await fetchAttendance(now.getMonth() + 1, now.getFullYear());
      const mine = (rows || []).filter((r) => isCurrentUserAttendanceRow(r));
      mine.sort((a, b) => {
        const ad = new Date(`${String(a?.log_date || a?.date || '').slice(0, 10)}T${a?.log_time || a?.time_in || '00:00:00'}`);
        const bd = new Date(`${String(b?.log_date || b?.date || '').slice(0, 10)}T${b?.log_time || b?.time_in || '00:00:00'}`);
        return bd - ad;
      });
      setHomeAttendanceRows(mine.slice(0, 7));
      const isOpenAttendanceRow = (r) => {
        const hasTimeIn = Boolean(r?.log_time || r?.time_in);
        const hasTimeOut = Boolean(r?.time_out || r?.timeOut);
        const isAbsent = Number(r?.is_absent || 0) === 1;
        const isLeave = Number(r?.leave_approved || 0) === 1 || String(r?.status || '').toLowerCase() === 'leave';
        return hasTimeIn && !hasTimeOut && !isAbsent && !isLeave;
      };
      const openRow = mine.find(isOpenAttendanceRow) || null;
      const activeBreakRow = mine.find((r) => isOpenAttendanceRow(r) && r?.break_start && !r?.break_end) || null;
      setHomeHasOpenShift(Boolean(openRow));
      setHomeIsOnBreak(Boolean(activeBreakRow));
      setHomeOpenShiftRecord(openRow);
    } catch (e) {
      setHomeAttendanceError('Failed to load your attendance.');
    } finally {
      setHomeAttendanceLoading(false);
    }
  }, [fetchAttendance, isCurrentUserAttendanceRow]);

  useEffect(() => {
    const activeBreakStart = homeOpenShiftRecord?.break_start;
    if (!homeIsOnBreak || !activeBreakStart) {
      setHomeBreakCountdownMs(0);
      return undefined;
    }

    const breakStartedAt = buildAttendanceDateTime(homeOpenShiftRecord?.log_date || new Date(), activeBreakStart);
    if (!breakStartedAt) {
      setHomeBreakCountdownMs(0);
      return undefined;
    }

    const breakEndsAt = new Date(breakStartedAt.getTime() + (60 * 60 * 1000));
    const updateCountdown = () => {
      setHomeBreakCountdownMs(Math.max(0, breakEndsAt.getTime() - Date.now()));
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [buildAttendanceDateTime, homeIsOnBreak, homeOpenShiftRecord]);

  const homeActiveBreakStartAt = React.useMemo(() => {
    if (!homeIsOnBreak || !homeOpenShiftRecord?.break_start) return null;
    return buildAttendanceDateTime(homeOpenShiftRecord?.log_date || new Date(), homeOpenShiftRecord.break_start);
  }, [buildAttendanceDateTime, homeIsOnBreak, homeOpenShiftRecord]);

  const homeActiveBreakEndAt = React.useMemo(() => {
    if (!homeActiveBreakStartAt) return null;
    return new Date(homeActiveBreakStartAt.getTime() + (60 * 60 * 1000));
  }, [homeActiveBreakStartAt]);

  const homeBreakStartLabel = homeActiveBreakStartAt ? formatDateTimeToTime12h(homeActiveBreakStartAt) : '-';
  const homeBreakReturnLabel = homeActiveBreakEndAt ? formatDateTimeToTime12h(homeActiveBreakEndAt) : '-';
  const homeBreakCountdownLabel = formatCountdownLabel(homeBreakCountdownMs);

  const markHrTimeIn = async () => {
    if (homeHasOpenShift) {
      setHomeAttendanceError('You must time out first before timing in again.');
      return;
    }
    setHomeAttendanceLoading(true);
    setHomeAttendanceError('');
    try {
      await axios.post(`${API_BASE}/api/attendance/employee/mark-time-in`, {}, { withCredentials: true });
      setHomeHasOpenShift(true);
      setHomeIsOnBreak(false);
      setHomeOpenShiftRecord(null);
      try { window.dispatchEvent(new Event('payrollUpdated')); } catch (e) {}
      setSnack({ open: true, message: 'Time-in recorded', severity: 'success' });
      await refreshHomeAttendance();
      try { await fetchPayroll(filterMonth, filterYear); } catch (e) {}
    } catch (e) {
      setHomeAttendanceError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to record time-in');
    } finally {
      setHomeAttendanceLoading(false);
    }
  };

  const markHrTimeOut = async () => {
    if (!homeHasOpenShift) {
      setHomeAttendanceError('No open shift found. Please time in first.');
      return;
    }
    if (homeIsOnBreak) {
      setHomeAttendanceError('End break before timing out.');
      return;
    }
    setHomeAttendanceLoading(true);
    setHomeAttendanceError('');
    try {
      console.info('[attendance][frontend][hr-timeout] request', {});
      const res = await axios.post(`${API_BASE}/api/attendance/employee/mark-time-out`, {}, { withCredentials: true });
      console.info('[attendance][frontend][hr-timeout] response', res?.data || null);
      setHomeHasOpenShift(false);
      setHomeIsOnBreak(false);
      setHomeOpenShiftRecord(null);
      try { window.dispatchEvent(new Event('payrollUpdated')); } catch (e) {}
      setSnack({ open: true, message: 'Time-out recorded', severity: 'success' });
      await refreshHomeAttendance();
      try { await fetchPayroll(filterMonth, filterYear); } catch (e) {}
      console.info('[attendance][frontend][hr-timeout] refreshed');
    } catch (e) {
      setHomeAttendanceError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to record time-out');
    } finally {
      setHomeAttendanceLoading(false);
    }
  };

  const toPayslipCardModel = useCallback((row) => {
    if (!row) return null;
    if (row.employee && row.period && row.earnings && row.deductions && row.totals) {
      return row;
    }
    const month = Number(row?.month || filterMonth);
    const year = Number(row?.year || filterYear);
    const base = Number(row?.base_salary || 0);
    const net = Number(row?.net_salary || row?.net_pay || 0);
    const abs = Number(row?.absences || 0);
    const periodStart = new Date(year, Math.max(0, month - 1), 1);
    const periodEnd = new Date(year, Math.max(0, month), 0);
    const daysInMonth = periodEnd.getDate();
    const daysWorked = Math.max(0, daysInMonth - Math.max(0, abs));
    const dailyRate = daysWorked > 0 ? (base / daysWorked) : Number(user?.baseSalary || user?.base_salary || user?.base_salary_per_day || 0);
    return {
      employee: {
        full_name: formatName(user, { lastFirst: false, middle: 'full', capitalize: true }) || 'HR Account',
        employee_no: String(user?.employeeid || user?.employee_no || user?.username || '').padStart(8, '0'),
        position_type: 'HR'
      },
      period: {
        cutoff_label: `${periodStart.toLocaleString('en-US', { month: 'long' })} ${year}`,
        start: formatDateOnly(periodStart),
        end: formatDateOnly(periodEnd)
      },
      earnings: {
        basic_pay: base,
        days_worked: daysWorked,
        daily_rate: Number.isFinite(dailyRate) ? dailyRate : 0,
        overtime_pay: 0,
        holiday_pay: 0,
        allowances: 0,
        gross_pay: base
      },
      deductions: {
        sss: 0,
        philhealth: 0,
        pagibig: 0,
        tax: 0,
        late: 0,
        absences: { days: abs, amount: 0 },
        others: 0,
        total_deductions: Math.max(0, base - net)
      },
      totals: {
        net_pay: net
      }
    };
  }, [filterMonth, filterYear, user, formatDateOnly]);

  const fetchMyPayslip = useCallback(async (month = filterMonth, year = filterYear, cutoffId = selectedMyPayslipCutoffId) => {
    setMyPayslipLoading(true);
    setMyPayslipError('');
    try {
      const row = await fetchPayslipRow(API_BASE, { month, year, cutoffId });
      if (!row) {
        setMyPayslip(null);
        return;
      }
      setMyPayslip(toPayslipCardModel(row));
    } catch (e) {
      if (e?.response?.status === 404) {
        setMyPayslip(null);
      } else {
        setMyPayslip(null);
        setMyPayslipError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to load payslip.');
      }
    } finally {
      setMyPayslipLoading(false);
    }
  }, [filterMonth, filterYear, selectedMyPayslipCutoffId, toPayslipCardModel]);

  const fetchMyPayslipCutoffs = useCallback(async () => {
    if (!isHr) return;
    try {
      const res = await axios.get(`${API_BASE}/api/payroll/cutoffs/me`, { withCredentials: true });
      const rows = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.rows) ? res.data.rows : []);
      setMyPayslipCutoffs(rows);
      setSelectedMyPayslipCutoffId((prev) => prev || (rows[0]?.id ? String(rows[0].id) : ''));
    } catch (error) {
      console.error('Failed to load HR payslip cutoffs:', error);
      setMyPayslipCutoffs([]);
    }
  }, [isHr]);

  const fetchManualRequests = useCallback(async () => {
    if (!isHr) return;
    setManualRequestsLoading(true);
    setManualRequestsError('');
    try {
      const res = await axios.get(`${API_BASE}/api/attendance/manual-requests?status=pending`, { withCredentials: true });
      const payload = res?.data;
      let rows = [];
      if (payload && payload.ok === true && Array.isArray(payload.rows)) {
        rows = payload.rows;
      } else if (Array.isArray(payload)) {
        rows = payload;
      } else {
        const msg = payload?.message || payload?.error || 'Failed to load manual requests.';
        setManualRequests([]);
        setManualRequestsError(msg);
        setSnack({ open: true, message: 'Failed to load manual requests', severity: 'error' });
        return;
      }
      // Fallback: if manual endpoint returns empty but attendance stream has pending manual rows,
      // derive requests from attendance/all so HR can still approve/reject.
      if (!rows || rows.length === 0) {
        try {
          const now = new Date();
          const month = Number(filterMonth) || (now.getMonth() + 1);
          const year = Number(filterYear) || now.getFullYear();
          const attRes = await axios.get(`${API_BASE}/api/attendance/all?month=${month}&year=${year}`, { withCredentials: true });
          const attRows = Array.isArray(attRes?.data) ? attRes.data : [];
          const map = new Map();
          attRows.forEach((r) => {
            const status = String(r?.manual_request_status || '').trim().toLowerCase();
            const id = r?.manual_request_id;
            if (status !== 'pending' || !id) return;
            if (!map.has(id)) {
              map.set(id, {
                id,
                employee_no: r.employee_no || '',
                employee_name: r.employee_name || '',
                requested_date: r.log_date || '',
                requested_time: r.log_time || '',
                created_at: r.created_at || null,
                reason: r.reason || '',
                status: 'pending',
              });
            }
          });
          rows = Array.from(map.values());
        } catch (fallbackErr) {
          // keep primary empty result if fallback fails
        }
      }
      setManualRequests(rows);
      setManualPendingCount(Array.isArray(rows) ? rows.length : 0);
      setManualRequestsError('');
    } catch (err) {
      setManualRequests([]);
      setManualPendingCount(0);
      setManualRequestsError('Failed to load manual requests.');
      setSnack({ open: true, message: 'Failed to load manual requests', severity: 'error' });
    } finally {
      setManualRequestsLoading(false);
    }
  }, [isHr, filterMonth, filterYear]);

  const fetchManualPendingCount = useCallback(async () => {
    if (!isHr) return;
    try {
      const res = await axios.get(`${API_BASE}/api/attendance/manual-requests?status=pending`, { withCredentials: true });
      const payload = res?.data;
      let rows = [];
      if (payload && payload.ok === true && Array.isArray(payload.rows)) rows = payload.rows;
      else if (Array.isArray(payload)) rows = payload;
      setManualPendingCount(Array.isArray(rows) ? rows.length : 0);
    } catch (e) {
      // keep previous count when transient error happens
    }
  }, [isHr]);

  const fetchDtrEvidence = useCallback(async () => {
    if (!isHr) return;
    setDtrEvidenceLoading(true);
    setDtrEvidenceError('');
    try {
      const res = await axios.get(`${API_BASE}/api/attendance/dtr-evidence`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        params: {
          employee_id: dtrEvidenceFilters.employeeId || undefined,
          month: dtrEvidenceFilters.month || undefined,
          year: dtrEvidenceFilters.year || undefined,
        },
      });
      const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      setDtrEvidenceRows(rows);
    } catch (e) {
      setDtrEvidenceRows([]);
      setDtrEvidenceError(e?.response?.data?.error || 'Failed to load DTR photos.');
    } finally {
      setDtrEvidenceLoading(false);
    }
  }, [isHr, dtrEvidenceFilters]);

  const approveManualRequest = async (row) => {
    if (!row?.id) return;
    try {
      await axios.post(`${API_BASE}/api/attendance/manual-requests/${row.id}/approve`, {}, { withCredentials: true });
      setManualRequests((prev) => prev.filter(r => r.id !== row.id));
      setManualPendingCount((prev) => Math.max(0, Number(prev || 0) - 1));
      setSnack({ open: true, message: 'Request approved; attendance recorded.', severity: 'success' });
    } catch (e) {
      setSnack({ open: true, message: 'Failed to approve request', severity: 'error' });
    }
  };

  const openRejectDialog = (row) => {
    setManualRejectTarget(row || null);
    setManualRejectReason('');
    setManualRejectResolution('absent');
    setManualRejectOpen(true);
  };

  const submitRejectManualRequest = async () => {
    if (!manualRejectTarget?.id) return;
    try {
      await axios.post(
        `${API_BASE}/api/attendance/manual-requests/${manualRejectTarget.id}/reject`,
        { reason: manualRejectReason || null, resolution: manualRejectResolution },
        { withCredentials: true }
      );
      setManualRequests((prev) => prev.filter(r => r.id !== manualRejectTarget.id));
      setManualPendingCount((prev) => Math.max(0, Number(prev || 0) - 1));
      setSnack({ open: true, message: `Request rejected and marked as ${manualRejectResolution}.`, severity: 'success' });
      try { await fetchAttendance(filterMonth, filterYear); } catch (e) {}
    } catch (e) {
      setSnack({ open: true, message: 'Failed to reject request', severity: 'error' });
    } finally {
      setManualRejectOpen(false);
      setManualRejectTarget(null);
      setManualRejectReason('');
      setManualRejectResolution('absent');
    }
  };

  const startHrBreak = async () => {
    if (!homeHasOpenShift) {
      setHomeAttendanceError('Time in first before starting your break.');
      return;
    }
    setHomeAttendanceLoading(true);
    setHomeAttendanceError('');
    try {
      const res = await axios.post(`${API_BASE}/api/attendance/break/start`, {}, { withCredentials: true });
      setHomeIsOnBreak(true);
      setSnack({ open: true, message: res?.data?.message || 'Break started.', severity: 'success' });
      await refreshHomeAttendance();
    } catch (e) {
      setHomeAttendanceError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to start break');
    } finally {
      setHomeAttendanceLoading(false);
    }
  };

  const endHrBreak = async () => {
    if (!homeHasOpenShift) {
      setHomeAttendanceError('No open shift found.');
      return;
    }
    setHomeAttendanceLoading(true);
    setHomeAttendanceError('');
    try {
      const res = await axios.post(`${API_BASE}/api/attendance/break/end`, {}, { withCredentials: true });
      setHomeIsOnBreak(false);
      setHomeOpenShiftRecord((prev) => prev ? { ...prev, break_end: res?.data?.break_end || prev.break_end || 'ended' } : prev);
      setSnack({ open: true, message: res?.data?.message || 'Break ended.', severity: 'success' });
      await refreshHomeAttendance();
    } catch (e) {
      setHomeAttendanceError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to end break');
    } finally {
      setHomeAttendanceLoading(false);
    }
  };

  // Fetch payroll with month/year
  const fetchPayroll = useCallback(async (m = filterMonth, y = filterYear) => {
    if (!isHr) return;
    try {
      const res = await axios.get(`${API_BASE}/api/payroll?month=${m}&year=${y}`, { withCredentials: true });
      const rows = Array.isArray(res.data) ? res.data : [];
      setPayrollRows(rows);
      if (rows.length === 0) {
        setSnack({ open: true, message: `No payroll rows found for ${m}/${y}.`, severity: 'warning' });
      }
    } catch (err) {
      setSnack({ open: true, message: 'Failed to load payroll', severity: 'error' });
      setPayrollRows([]);
      setPageError('Failed to load payroll.');
    }
  }, [filterMonth, filterYear]);

  const fetchPayrollCutoffs = useCallback(async () => {
    if (!isHr) return;
    setPayrollCutoffsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/payroll/cutoffs`, { withCredentials: true });
      const rows = Array.isArray(res.data) ? res.data : [];
      setPayrollCutoffs(rows);
      setSelectedCutoffId((prev) => {
        if (prev && rows.some((row) => String(row.id) === String(prev))) return prev;
        return rows[0] ? String(rows[0].id) : '';
      });
    } catch (err) {
      console.error('Failed to load payroll cutoffs', err);
      setPayrollCutoffs([]);
    } finally {
      setPayrollCutoffsLoading(false);
    }
  }, [isHr]);

  const fetchCutoffRecords = useCallback(async (cutoffId) => {
    if (!isHr || !cutoffId) {
      setCutoffRecords([]);
      return;
    }
    setCutoffRecordsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/payroll/cutoffs/${cutoffId}/records`, { withCredentials: true });
      setCutoffRecords(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load cutoff records', err);
      setCutoffRecords([]);
    } finally {
      setCutoffRecordsLoading(false);
    }
  }, [isHr]);

  const createPayrollCutoff = async () => {
    if (!cutoffForm.period_start || !cutoffForm.period_end || !cutoffForm.pay_date) {
      setSnack({ open: true, message: 'Please complete the cutoff start date, cutoff end date, and pay date before saving.', severity: 'error' });
      return;
    }
    try {
      await axios.post(`${API_BASE}/api/payroll/cutoffs`, cutoffForm, { withCredentials: true });
      setSnack({ open: true, message: 'Payroll cutoff created.', severity: 'success' });
      await fetchPayrollCutoffs();
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || 'Failed to create payroll cutoff', severity: 'error' });
    }
  };

  const runSelectedCutoffPayrollSync = async ({
    successMessage = 'Cutoff payroll generated.',
    errorMessage = 'Failed to generate cutoff payroll',
  } = {}) => {
    if (!selectedCutoffId) {
      setSnack({ open: true, message: 'Select a cutoff first.', severity: 'warning' });
      return false;
    }
    try {
      await axios.post(`${API_BASE}/api/payroll/cutoffs/${selectedCutoffId}/generate`, {}, { withCredentials: true });
      setSnack({ open: true, message: successMessage, severity: 'success' });
      await fetchPayrollCutoffs();
      await fetchCutoffRecords(selectedCutoffId);
      return true;
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || errorMessage, severity: 'error' });
      return false;
    }
  };

  const generateSelectedCutoffPayroll = async () => runSelectedCutoffPayrollSync();

  const recomputeSelectedCutoffPayroll = async () => runSelectedCutoffPayrollSync({
    successMessage: 'Selected cutoff payroll was recomputed from the latest attendance and leave data.',
    errorMessage: 'Failed to recompute the selected cutoff payroll',
  });

  const updateSelectedCutoffStatus = async (status) => {
    if (!selectedCutoffId) {
      setSnack({ open: true, message: 'Select a cutoff first.', severity: 'warning' });
      return;
    }
    try {
      await axios.put(`${API_BASE}/api/payroll/cutoffs/${selectedCutoffId}/status`, { status }, { withCredentials: true });
      setSnack({ open: true, message: `Cutoff marked ${status}.`, severity: 'success' });
      await fetchPayrollCutoffs();
      await fetchCutoffRecords(selectedCutoffId);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || `Failed to set status to ${status}`, severity: 'error' });
    }
  };

  const validateHistoryFilters = useCallback(() => {
    const emp = String(historyFilters.employeeId || '').trim();
    const month = String(historyFilters.month || '').trim();
    const year = String(historyFilters.year || '').trim();
    if (emp && !/^\d{1,8}$/.test(emp)) return 'Employee ID must be numeric (up to 8 digits).';
    if ((month && !year) || (!month && year)) return 'Select both month and year together, or leave both empty.';
    return '';
  }, [historyFilters]);

  // Fetch payroll history (HR can view per account via employeeId filter; employees can view self)
  const fetchPayrollHistory = useCallback(async (page = 1) => {
    if (!isHr) return;
    try {
      const invalidMsg = validateHistoryFilters();
      if (invalidMsg) {
        setHistoryRows([]);
        setHistoryPages(1);
        setHistoryPage(1);
        setHistoryNotice(invalidMsg);
        return;
      }
      setHistoryLoading(true);
      setHistoryNotice('');
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '10');
      if (historyFilters.year) params.set('year', String(historyFilters.year));
      if (historyFilters.month) params.set('month', String(historyFilters.month));
      if (historyFilters.employeeId && String(historyFilters.employeeId).trim()) {
        params.set('employee_id', String(historyFilters.employeeId).trim());
      }
      const baseUrl = `${API_BASE}/api/payroll/history`;
      const url = `${baseUrl}?${params.toString()}`;
      const res = await axios.get(url, { withCredentials: true });
      const data = res?.data || {};
      if (data.error) {
        setHistoryRows([]);
        setSnack({ open: true, message: data.error, severity: 'error' });
        return;
      }
      if (data.warning && data.warning.message) {
        setHistoryNotice(String(data.warning.message));
      }
      const rows = Array.isArray(data.rows) ? data.rows : (Array.isArray(data.data) ? data.data : []);
      const limit = Number(data.limit || 10);
      const total = Number(data.total || 0);
      const pages = Math.max(1, Math.ceil(total / (limit || 1)));
      setHistoryRows(rows);
      setHistoryPages(pages);
      setHistoryPage(Number(data.page || page || 1));
    } catch (e) {
      console.error('Failed to load payroll history', e);
      setHistoryRows([]);
      setSnack({ open: true, message: 'Failed to load payroll history', severity: 'error' });
      setPageError('Failed to load payroll history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilters, isHr, validateHistoryFilters]);

  const fetchPayrollHistoryYears = useCallback(async () => {
    if (!isHr) return;
    try {
      const res = await axios.get(`${API_BASE}/api/payroll/history-years`, { withCredentials: true });
      const years = Array.isArray(res?.data?.years) ? res.data.years : [];
      setPayrollHistoryYears(years.map((year) => Number(year)).filter((year) => Number.isFinite(year) && year > 0));
    } catch (err) {
      console.error('Failed to load payroll history years', err);
      setPayrollHistoryYears([new Date().getFullYear()]);
    }
  }, [isHr]);

  // Open salary history modal for an employee (emp can be employee object or payroll row)
  const openSalaryHistory = async (emp) => {
    if (!emp) return;
    const id = emp.id || emp.userId || emp.employeeid || emp.employee_id || emp.username;
    if (!id) return;
    setSalaryHistoryEmployee(emp);
    setSalaryHistoryLoading(true);
    setSalaryHistoryOpen(true);
    try {
      const res = await axios.get(`${API_BASE}/api/salary-history/${encodeURIComponent(id)}`, { withCredentials: true });
      setSalaryHistoryRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load salary history', e);
      setSalaryHistoryRows([]);
      setSnack({ open: true, message: 'Failed to load salary history', severity: 'error' });
      setPageError('Failed to load salary history.');
    } finally {
      setSalaryHistoryLoading(false);
    }
  };

  // Fetch employees
  const fetchEmployees = useCallback(async () => {
    if (!isHr) return;
    try {
      const includeInactive = employeeStatusView !== 'active';
      const res = await axios.get(
        `${API_BASE}/api/employees${includeInactive ? '?include_inactive=1' : ''}`,
        { withCredentials: true }
      );
      const list = Array.isArray(res.data) ? res.data : [];
      setEmployees(list);
    } catch (err) {
      setSnack({ open: true, message: 'Failed to load employees', severity: 'error' });
      setEmployees([]);
      setPageError('Failed to load employees.');
    }
  }, [isHr, employeeStatusView]);

  const handleOpenEmployeeDetail = async (emp, options = {}) => {
    const includePhotos = options?.includePhotos !== false;
    const requestKey = String(
      emp?.id ||
      emp?.userId ||
      emp?.employeeid ||
      emp?.employee_no ||
      emp?.username ||
      ''
    );
    employeePhotoRequestRef.current = requestKey;
    setSelectedEmployee(emp);
    setEmployeeDetailOpen(true);
    setEmployeeDetailShowPhotos(includePhotos);
    if (!includePhotos) {
      setLoadingEmployeePhotos(false);
      setEmployeePhotos([]);
      return;
    }
    setLoadingEmployeePhotos(true);
    setEmployeePhotos([]);
    try {
      if (emp.employeeid || emp.employee_no) {
        const employeeNo = emp.employeeid || emp.employee_no;
        const res = await axios.get(`${API_BASE}/api/photos_blob/list/${employeeNo}`, { withCredentials: true });

        // Normalize response into a flat array
        let photos = res.data;
        if (!Array.isArray(photos)) {
          // Try common shapes like { photos: [...] } or { data: [...] }
          if (photos && Array.isArray(photos.photos)) {
            photos = photos.photos;
          } else if (photos && Array.isArray(photos.data)) {
            photos = photos.data;
          } else {
            photos = [];
          }
        }

        if (employeePhotoRequestRef.current === requestKey) {
          setEmployeePhotos(photos);
        }
      }
    } catch (err) {
      console.error('Failed fetching employee photos', err);
    } finally {
      if (employeePhotoRequestRef.current === requestKey) {
        setLoadingEmployeePhotos(false);
      }
    }
  };

  const handleCloseEmployeeDetail = () => {
    employeePhotoRequestRef.current = '';
    setEmployeeDetailOpen(false);
    setSelectedEmployee(null);
    setManualRequestDetail(null);
    setManualRequestEvidence([]);
    setManualRequestEvidenceLoading(false);
    setEmployeeDetailShowPhotos(true);
    setEmployeePhotos([]);
  };

  const fetchGeneralMonthlyReport = useCallback(async (month = filterMonth, year = filterYear) => {
    setGeneralMonthlyReportLoading(true);
    setGeneralMonthlyReportError('');
    try {
      const res = await axios.get(`${API_BASE}/api/reports/general-monthly`, {
        params: { month, year },
        withCredentials: true,
      });
      setGeneralMonthlyReport(res?.data?.report || null);
    } catch (err) {
      setGeneralMonthlyReport(null);
      setGeneralMonthlyReportError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to load general monthly report.');
    } finally {
      setGeneralMonthlyReportLoading(false);
    }
  }, [filterMonth, filterYear]);

  const fetchIndividualTranscript = useCallback(async (employeeId = selectedTranscriptEmployeeId, month = filterMonth, year = filterYear, cutoffId = reportCutoffId, userId = selectedTranscriptUserId) => {
    const normalizedEmployeeId = String(employeeId || '').trim();
    const normalizedUserId = String(userId || '').trim();
    const requestId = transcriptRequestRef.current + 1;
    transcriptRequestRef.current = requestId;
    if (!normalizedEmployeeId) {
      setIndividualTranscriptReport(null);
      setIndividualTranscriptError('');
      return;
    }
    setIndividualTranscriptLoading(true);
    setIndividualTranscriptError('');
    setIndividualTranscriptReport(null);
    try {
      const res = await axios.get(`${API_BASE}/api/reports/individual-transcript/${encodeURIComponent(normalizedEmployeeId)}`, {
        params: { month, year, cutoff_id: cutoffId || undefined, user_id: normalizedUserId || undefined },
        withCredentials: true,
      });
      if (requestId !== transcriptRequestRef.current) return;
      setIndividualTranscriptReport(res?.data?.report || null);
    } catch (err) {
      if (requestId !== transcriptRequestRef.current) return;
      setIndividualTranscriptReport(null);
      setIndividualTranscriptError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to load individual transcript.');
    } finally {
      if (requestId === transcriptRequestRef.current) setIndividualTranscriptLoading(false);
    }
  }, [selectedTranscriptEmployeeId, selectedTranscriptUserId, filterMonth, filterYear, reportCutoffId]);

  const openTranscriptFromReportRow = useCallback((row) => {
    const employeeId = String(row?.employeeid || '').trim();
    const userId = String(row?.user_id || row?.userId || '').trim();
    if (!employeeId) return;
    setSelectedTranscriptEmployeeId(employeeId);
    setSelectedTranscriptUserId(userId);
    fetchIndividualTranscript(employeeId, filterMonth, filterYear, reportCutoffId, userId);
    window.setTimeout(() => {
      try {
        reportTranscriptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_) {}
    }, 75);
  }, [fetchIndividualTranscript, filterMonth, filterYear, reportCutoffId]);

  const clearSelectedTranscript = useCallback(() => {
    setSelectedTranscriptEmployeeId('');
    setSelectedTranscriptUserId('');
    setIndividualTranscriptReport(null);
    setIndividualTranscriptError('');
  }, []);

  const printReportNode = useCallback((selector, title) => {
    const node = document.querySelector(selector);
    if (!node) return window.print();
    const popup = window.open('', '_blank', 'width=960,height=1200');
    if (!popup) return window.print();
    popup.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <meta charset="utf-8" />
          <style>
            @page { size: A4 landscape; margin: 8mm; }
            body { font-family: Arial, sans-serif; margin: 8px; color: #111; font-size: 10px; }
            * { box-sizing: border-box; }
            table { width: 100% !important; border-collapse: collapse; table-layout: auto; font-size: 9.5px; }
            th, td { border: 1px solid #d0d0d0; padding: 3px 4px; vertical-align: top; word-break: normal; overflow-wrap: break-word; }
            th { white-space: nowrap; }
            .report-shell { width: 100%; }
            .report-shell .MuiButton-root,
            .report-shell button { display: none !important; }
            .report-shell .MuiAlert-root { margin-bottom: 12px; }
            .general-report-print table th:nth-child(1),
            .general-report-print table td:nth-child(1) { width: 88px; white-space: nowrap; }
            .general-report-print table th:nth-child(2),
            .general-report-print table td:nth-child(2) { width: 180px; }
            .general-report-print table th:nth-child(3),
            .general-report-print table td:nth-child(3) { width: 86px; white-space: nowrap; }
            .general-report-print table th:nth-child(n+4),
            .general-report-print table td:nth-child(n+4) { text-align: right; white-space: nowrap; }
            .individual-transcript-print { padding: 0 !important; border: 0 !important; max-height: none !important; overflow: visible !important; }
            .individual-transcript-print .transcript-header {
              display: flex;
              justify-content: space-between;
              gap: 10px;
              padding: 10px 12px;
              border: 1px solid #d7deea;
              border-radius: 8px;
              background: #fff;
              margin-bottom: 8px;
            }
            .individual-transcript-print .transcript-header-meta { text-align: right; min-width: 200px; font-size: 9.5px; }
            .individual-transcript-print .transcript-cutoff-summary { margin-bottom: 8px; }
            .individual-transcript-print .transcript-section {
              border: 1px solid #d7deea;
              border-radius: 8px;
              padding: 8px;
              margin-bottom: 8px;
              page-break-inside: avoid;
            }
            .individual-transcript-print .transcript-section-title {
              font-size: 11px;
              font-weight: 700;
              margin: 0 0 6px 0;
            }
            .individual-transcript-print .MuiChip-root {
              border: 0 !important;
              padding: 1px 6px !important;
              height: auto !important;
              font-size: 9px !important;
            }
            .individual-transcript-print table { font-size: 9.25px; }
            .individual-transcript-print table th:nth-child(1),
            .individual-transcript-print table td:nth-child(1) { width: 118px; }
            .individual-transcript-print table th:nth-child(2),
            .individual-transcript-print table td:nth-child(2),
            .individual-transcript-print table th:nth-child(3),
            .individual-transcript-print table td:nth-child(3),
            .individual-transcript-print table th:nth-child(4),
            .individual-transcript-print table td:nth-child(4),
            .individual-transcript-print table th:nth-child(5),
            .individual-transcript-print table td:nth-child(5) { width: 58px; white-space: nowrap; }
            .individual-transcript-print table th:nth-child(8),
            .individual-transcript-print table td:nth-child(8),
            .individual-transcript-print table th:nth-child(9),
            .individual-transcript-print table td:nth-child(9) { text-align: right; white-space: nowrap; }
            .inventory-report-print {
              padding: 0 !important;
              max-height: none !important;
              overflow: visible !important;
              font-size: 9px !important;
            }
            .inventory-report-print .inventory-print-header {
              display: flex !important;
              justify-content: space-between !important;
              gap: 12px !important;
              margin-bottom: 8px !important;
              border-bottom: 1px solid #d0d0d0 !important;
              padding-bottom: 6px !important;
            }
            .inventory-report-print .inventory-print-title {
              font-size: 13px !important;
              font-weight: 800 !important;
              margin: 0 0 2px 0 !important;
            }
            .inventory-report-print .inventory-print-meta {
              text-align: right !important;
              font-size: 8.75px !important;
            }
            .inventory-report-print .inventory-kpi-grid,
            .inventory-report-print .inventory-chart-grid {
              display: grid !important;
              grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
              gap: 6px !important;
              margin-bottom: 8px !important;
            }
            .inventory-report-print .inventory-chart-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            .inventory-report-print .inventory-print-card {
              border: 1px solid #d7deea !important;
              border-radius: 6px !important;
              padding: 6px !important;
              box-shadow: none !important;
              page-break-inside: avoid !important;
            }
            .inventory-report-print .inventory-print-card h6,
            .inventory-report-print .inventory-print-card .MuiTypography-h6 {
              font-size: 10px !important;
              margin: 0 0 4px 0 !important;
            }
            .inventory-report-print .inventory-print-kpi-label {
              color: #555 !important;
              font-size: 8px !important;
              display: block !important;
            }
            .inventory-report-print .inventory-print-kpi-value {
              font-size: 15px !important;
              font-weight: 800 !important;
              line-height: 1.1 !important;
            }
            .inventory-report-print .inventory-print-row {
              display: grid !important;
              grid-template-columns: 110px 1fr 70px !important;
              gap: 6px !important;
              align-items: center !important;
              margin-bottom: 3px !important;
              break-inside: avoid !important;
            }
            .inventory-report-print table {
              font-size: 8.5px !important;
            }
            .inventory-report-print table th,
            .inventory-report-print table td {
              padding: 2px 4px !important;
            }
          </style>
        </head>
        <body><div class="report-shell">${node.outerHTML}</div></body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
    popup.close();
  }, []);

  const openReportEmailDialog = useCallback((type) => {
    if (type === 'individual_transcript' && !individualTranscriptReport?.employee?.employeeid) {
      setSnack({ open: true, message: 'Select an employee transcript first.', severity: 'warning' });
      return;
    }
    const defaultEmail = type === 'individual_transcript'
      ? String(individualTranscriptReport?.employee?.email || '').trim()
      : '';
    setReportEmailType(type);
    setReportEmailTarget(defaultEmail);
    setReportEmailOpen(true);
  }, [individualTranscriptReport]);

  const sendReportEmail = useCallback(async () => {
    setReportEmailSending(true);
    try {
      const res = await axios.post(`${API_BASE}/api/reports/email`, {
        report_type: reportEmailType,
        sent_to: reportEmailTarget,
        month: filterMonth,
        year: filterYear,
        cutoff_id: reportEmailType === 'individual_transcript' ? (reportCutoffId || undefined) : undefined,
        employee_id: reportEmailType === 'individual_transcript'
          ? (individualTranscriptReport?.employee?.employeeid || selectedTranscriptEmployeeId || '')
          : undefined,
        user_id: reportEmailType === 'individual_transcript'
          ? (individualTranscriptReport?.employee?.user_id || selectedTranscriptUserId || undefined)
          : undefined,
      }, { withCredentials: true });
      setSnack({ open: true, message: res?.data?.message || 'Report emailed successfully.', severity: 'success' });
      setReportEmailOpen(false);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || err?.response?.data?.message || 'Failed to email report.', severity: 'error' });
    } finally {
      setReportEmailSending(false);
    }
  }, [reportEmailType, reportEmailTarget, filterMonth, filterYear, reportCutoffId, individualTranscriptReport, selectedTranscriptEmployeeId, selectedTranscriptUserId]);

  const fetchInventoryRequests = useCallback(async (options = {}) => {
    if (!isHr) return;
    try {
      const params = new URLSearchParams();
      params.set('scope', String(options.scope || 'current_month'));
      if (options.status) params.set('status', String(options.status));
      if (options.month) params.set('month', String(options.month));
      if (options.year) params.set('year', String(options.year));
      const res = await axios.get(`${API_BASE}/api/inventory/requests?${params.toString()}`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });
      if (res.status === 304) return;
      setInventoryRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load inventory requests', err);
      setSnack({ open: true, message: 'Failed to load inventory requests', severity: 'error' });
      setInventoryRequests([]);
      setPageError('Failed to load inventory requests.');
    }
  }, [isHr, inventoryReportMonth, inventoryReportYear]);

  const fetchInventoryHistory = useCallback(async (filters = inventoryHistoryFilters) => {
    if (!isHr) return;
    setInventoryHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('scope', 'history');
      if (filters?.status) params.set('status', String(filters.status));
      if (filters?.month) params.set('month', String(filters.month));
      if (filters?.year) params.set('year', String(filters.year));
      const res = await axios.get(`${API_BASE}/api/inventory/requests?${params.toString()}`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });
      if (res.status === 304) return;
      setInventoryHistoryRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load inventory request history', err);
      setSnack({ open: true, message: 'Failed to load inventory request history', severity: 'error' });
      setInventoryHistoryRows([]);
    } finally {
      setInventoryHistoryLoading(false);
    }
  }, [inventoryHistoryFilters, isHr]);

  const fetchInventoryHistoryYearOptions = useCallback(async () => {
    if (!isHr) return;
    try {
      const res = await axios.get(`${API_BASE}/api/inventory/requests/history-years`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });
      if (res.status === 304) return;
      const years = Array.isArray(res?.data?.years)
        ? res.data.years.map((year) => Number(year)).filter((year) => Number.isInteger(year) && year > 0)
        : [];
      setInventoryHistoryYearOptions(years);
    } catch (err) {
      console.error('Failed to load inventory request history years', err);
      setInventoryHistoryYearOptions([]);
    }
  }, [isHr]);

  const openInventoryHistory = useCallback(() => {
    setInventoryHistoryOpen(true);
    fetchInventoryHistoryYearOptions();
    fetchInventoryHistory(inventoryHistoryFilters);
  }, [fetchInventoryHistory, fetchInventoryHistoryYearOptions, inventoryHistoryFilters]);

  const fetchInventoryItems = useCallback(async () => {
    if (!isHr) return;
    try {
      const res = await axios.get(`${API_BASE}/api/inventory/stocks/names`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });
      if (res.status === 304) return;
      const items = Array.isArray(res.data)
        ? res.data.map(i => ({
            id: i?.id || i?.item_id || null,
            item_name: (typeof i === 'string' ? i : (i?.item_name || i?.name || i?.itemName)),
            quantity: (typeof i?.quantity === 'number' ? i.quantity : 0)
          })).filter(i => i.item_name)
        : [];
      setInventoryItems(items);
    } catch (err) {
      console.error('Failed to load inventory items', err);
      setSnack({ open: true, message: 'Failed to load inventory items', severity: 'error' });
      setInventoryItems([]);
      setPageError('Failed to load inventory items.');
    }
  }, []);

  const fetchInventoryAnalytics = useCallback(async () => {
    if (!isHr) return;
    setInventoryAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/inventory/analytics/summary`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        params: {
          month: inventoryReportMonth || undefined,
          year: inventoryReportYear || undefined,
        },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });
      if (res.status === 304) return;
      const data = res?.data?.data || {};
      const lowStockItems = Array.isArray(data?.lowStockItems) ? data.lowStockItems : [];
      setInventoryAnalytics({
        statusCounts: data?.statusCounts || { pending: 0, approved: 0, rejected: 0, cancelled: 0 },
        lowStockItems: lowStockItems
          .map((item) => ({ ...item, quantity: Number(item?.quantity || 0) }))
          .filter((item) => item.quantity <= INVENTORY_WARNING_STOCK_THRESHOLD)
          .sort((a, b) => a.quantity - b.quantity || String(a.item_name || '').localeCompare(String(b.item_name || ''))),
        topRequestedItems: Array.isArray(data?.topRequestedItems) ? data.topRequestedItems : [],
        monthlyTrend: Array.isArray(data?.monthlyTrend) ? data.monthlyTrend : [],
        hourlyTrend: Array.isArray(data?.hourlyTrend) ? data.hourlyTrend : [],
        reportPeriod: data?.reportPeriod || null,
      });
    } catch (err) {
      console.error('Failed to load inventory analytics', err);
      setInventoryAnalytics({
        statusCounts: { pending: 0, approved: 0, rejected: 0, cancelled: 0 },
        lowStockItems: [],
        topRequestedItems: [],
        monthlyTrend: [],
        hourlyTrend: [],
        reportPeriod: null,
      });
    } finally {
      setInventoryAnalyticsLoading(false);
    }
  }, [isHr]);

  const resetBranchForm = () => {
    setBranchForm({
      branch_code: '',
      branch_name: '',
      address: '',
      city: '',
      province: '',
      min_headcount: 3,
      max_headcount: 5,
      is_active: true,
    });
    setEditingBranchId(null);
  };

  const fetchBranches = useCallback(async () => {
    if (!isHr) return;
    setBranchesLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/branches`, { withCredentials: true });
      const rows = Array.isArray(res?.data?.branches) ? res.data.branches : (Array.isArray(res?.data) ? res.data : []);
      setBranches(rows);
    } catch (err) {
      console.error('Failed to load branches', err);
      setSnack({ open: true, message: 'Failed to load branches', severity: 'error' });
      setBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  }, [isHr]);

  const fetchStaffingWarnings = useCallback(async () => {
    if (!isHr) return;
    setWarningsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/warnings/staffing`, { withCredentials: true });
      const data = res?.data?.data || {};
      setWarningData({
        understaffedBranches: Array.isArray(data.understaffedBranches) ? data.understaffedBranches : [],
        contractsEndingSoon: Array.isArray(data.contractsEndingSoon) ? data.contractsEndingSoon : [],
        counts: {
          critical: Number(data?.counts?.critical || 0),
          warning: Number(data?.counts?.warning || 0),
        },
      });
    } catch (err) {
      console.error('Failed to load staffing warnings', err);
      setWarningData({ understaffedBranches: [], contractsEndingSoon: [], counts: { critical: 0, warning: 0 } });
    } finally {
      setWarningsLoading(false);
    }
  }, [isHr]);

  const saveBranch = async () => {
    const headcountValidation = getBranchHeadcountValidation(branchForm.min_headcount, branchForm.max_headcount);
    const payload = {
      branch_code: String(branchForm.branch_code || '').trim().toUpperCase(),
      branch_name: String(branchForm.branch_name || '').trim(),
      address: String(branchForm.address || '').trim(),
      city: String(branchForm.city || '').trim(),
      province: String(branchForm.province || '').trim(),
      min_headcount: headcountValidation.minHeadcount,
      max_headcount: headcountValidation.maxHeadcount,
      is_active: !!branchForm.is_active,
    };
    if (!payload.branch_code || !payload.branch_name) {
      setSnack({ open: true, message: 'Branch code and branch name are required.', severity: 'warning' });
      return;
    }
    if (!headcountValidation.isValid) {
      const branchErrorMessage = headcountValidation.minError || headcountValidation.maxError || headcountValidation.rangeError;
      setSnack({ open: true, message: branchErrorMessage, severity: 'warning' });
      return;
    }
    try {
      if (editingBranchId) {
        await axios.put(`${API_BASE}/api/branches/${editingBranchId}`, payload, { withCredentials: true });
        setSnack({ open: true, message: 'Branch updated.', severity: 'success' });
      } else {
        await axios.post(`${API_BASE}/api/branches`, payload, { withCredentials: true });
        setSnack({ open: true, message: 'Branch created.', severity: 'success' });
      }
      resetBranchForm();
      await fetchBranches();
      await fetchStaffingWarnings();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save branch';
      setSnack({ open: true, message: msg, severity: 'error' });
    }
  };

  const resetDeploymentForm = () => {
    setDeploymentForm({
      user_id: '',
      branch_id: '',
      start_date: '',
      end_date: '',
      shift_start: '',
      shift_end: '',
      notes: '',
      is_primary: true,
    });
  };

  const deploymentTodayDateKey = toDateKey(new Date());
  const getDeploymentAssigneeId = (row) => Number(row?.user_id || row?.employee_user_id || row?.userId || row?.id || 0);
  const normalizeDeploymentDateKey = (value, fallback = '') => toDateKey(value) || fallback;
  const deploymentRangesOverlap = (existingStart, existingEnd, proposedStart, proposedEnd) => {
    const existingStartKey = normalizeDeploymentDateKey(existingStart, '0001-01-01');
    const existingEndKey = normalizeDeploymentDateKey(existingEnd, '9999-12-31');
    const proposedStartKey = normalizeDeploymentDateKey(proposedStart, deploymentTodayDateKey);
    const proposedEndKey = normalizeDeploymentDateKey(proposedEnd, '9999-12-31');
    return !(existingEndKey < proposedStartKey || existingStartKey > proposedEndKey);
  };
  const isDeploymentActiveOnDate = (row, dateKey = deploymentTodayDateKey) => {
    const startKey = normalizeDeploymentDateKey(row?.start_date, '0001-01-01');
    const endKey = normalizeDeploymentDateKey(row?.end_date, '9999-12-31');
    return startKey <= dateKey && endKey >= dateKey;
  };
  const normalizeDeploymentTimeKey = (value, fallback = '') => {
    const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return fallback;
    return `${String(match[1]).padStart(2, '0')}:${match[2]}:${String(match[3] || '00').padStart(2, '0')}`;
  };
  const getManilaTimeKey = () => new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Manila',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date());
  const getDeploymentStatusLabel = (row) => {
    const nowTimeKey = getManilaTimeKey();
    const startKey = normalizeDeploymentDateKey(row?.start_date, '0001-01-01');
    const endKey = normalizeDeploymentDateKey(row?.end_date, '9999-12-31');
    const shiftStartKey = normalizeDeploymentTimeKey(row?.shift_start);
    const shiftEndKey = normalizeDeploymentTimeKey(row?.shift_end);
    if (endKey < deploymentTodayDateKey) return 'Ended';
    if (endKey === deploymentTodayDateKey && shiftEndKey && shiftEndKey <= nowTimeKey) return 'Ended';
    if (startKey > deploymentTodayDateKey) return 'Upcoming';
    if (startKey === deploymentTodayDateKey && shiftStartKey && shiftStartKey > nowTimeKey) return 'Upcoming';
    const apiStatus = String(row?.deployment_status || '').trim().toLowerCase();
    if (apiStatus === 'active') return 'Active';
    if (apiStatus === 'upcoming') return 'Upcoming';
    if (apiStatus === 'ended') return 'Ended';
    return 'Active';
  };
  const isDeploymentNotEnded = (row) => getDeploymentStatusLabel(row) !== 'Ended';

  const unavailableDeploymentByUserId = React.useMemo(() => {
    const map = {};
    (deployments || []).forEach((row) => {
      const assigneeId = getDeploymentAssigneeId(row);
      if (!assigneeId || map[assigneeId]) return;
      if (isDeploymentNotEnded(row)) map[assigneeId] = row;
    });
    return map;
  }, [deployments, deploymentTodayDateKey]);

  const deploymentSelectionConflict = React.useMemo(() => {
    const selectedUserId = Number(deploymentForm.user_id) || 0;
    if (!selectedUserId) return null;

    const activeDeployment = unavailableDeploymentByUserId[selectedUserId];
    if (activeDeployment) {
      const statusLabel = getDeploymentStatusLabel(activeDeployment).toLowerCase();
      if (deploymentForm.is_primary && Number(activeDeployment.is_primary || 0) === 1) {
        return { type: 'active_primary', message: `This custodian already has a ${statusLabel} primary deployment.` };
      }
      return { type: 'active', message: `This custodian already has a ${statusLabel} deployment.` };
    }

    if (!deploymentForm.start_date) return null;

    const overlappingDeployment = (deployments || []).find((row) => {
      return getDeploymentAssigneeId(row) === selectedUserId
        && deploymentRangesOverlap(row?.start_date, row?.end_date, deploymentForm.start_date, deploymentForm.end_date || null);
    });

    if (overlappingDeployment) {
      return { type: 'overlap', message: 'Deployment dates overlap an existing assignment.' };
    }

    return null;
  }, [deploymentForm.user_id, deploymentForm.is_primary, deploymentForm.start_date, deploymentForm.end_date, deployments, unavailableDeploymentByUserId]);

  const fetchDeployments = useCallback(async () => {
    if (!isHr) return;
    setDeploymentsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/deployments`, { withCredentials: true });
      const rows = Array.isArray(res?.data?.rows) ? res.data.rows : (Array.isArray(res?.data) ? res.data : []);
      setDeployments(rows);
    } catch (err) {
      console.error('Failed to load deployments', err);
      setSnack({ open: true, message: 'Failed to load deployments', severity: 'error' });
      setDeployments([]);
    } finally {
      setDeploymentsLoading(false);
    }
  }, [isHr]);

  const saveDeployment = async () => {
    const todayDateKey = toDateKey(new Date());
    const payload = {
      user_id: Number(deploymentForm.user_id) || 0,
      branch_id: Number(deploymentForm.branch_id) || 0,
      start_date: deploymentForm.start_date || '',
      end_date: deploymentForm.end_date || null,
      shift_start: deploymentForm.shift_start || null,
      shift_end: deploymentForm.shift_end || null,
      notes: deploymentForm.notes || null,
      is_primary: !!deploymentForm.is_primary,
    };
    if (!payload.user_id || !payload.branch_id || !payload.start_date) {
      setSnack({ open: true, message: 'Custodian, branch, and start date are required.', severity: 'warning' });
      return;
    }
    if (!payload.shift_start || !payload.shift_end) {
      setSnack({ open: true, message: 'Shift start and shift end are required.', severity: 'warning' });
      return;
    }
    if (isEarlierDateKey(payload.start_date, todayDateKey)) {
      setSnack({ open: true, message: 'Start date cannot be earlier than today.', severity: 'warning' });
      return;
    }
    if (payload.end_date && isEarlierDateKey(payload.end_date, payload.start_date)) {
      setSnack({ open: true, message: 'End date cannot be earlier than start date.', severity: 'warning' });
      return;
    }
    if (payload.shift_start && payload.shift_end && payload.shift_end <= payload.shift_start) {
      setSnack({ open: true, message: 'Shift end must be later than shift start.', severity: 'warning' });
      return;
    }
    if (deploymentSelectionConflict?.message) {
      setSnack({ open: true, message: deploymentSelectionConflict.message, severity: 'warning' });
      return;
    }
    try {
      await axios.post(`${API_BASE}/api/deployments/assign`, payload, { withCredentials: true });
      setSnack({ open: true, message: 'Deployment assigned.', severity: 'success' });
      resetDeploymentForm();
      await fetchDeployments();
      await fetchStaffingWarnings();
      await fetchStaffingWarnings();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save deployment';
      setSnack({ open: true, message: msg, severity: 'error' });
    }
  };

  const endDeployment = async (id) => {
    if (!id) return;
    try {
      await axios.post(`${API_BASE}/api/deployments/${id}/end`, {}, { withCredentials: true });
      setSnack({ open: true, message: 'Deployment ended.', severity: 'info' });
      await fetchDeployments();
      await fetchStaffingWarnings();
      await fetchStaffingWarnings();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to end deployment';
      setSnack({ open: true, message: msg, severity: 'error' });
    }
  };

  const editBranch = (row) => {
    if (!row) return;
    setEditingBranchId(row.id);
    setBranchForm({
      branch_code: row.branch_code || '',
      branch_name: row.branch_name || '',
      address: row.address || '',
      city: row.city || '',
      province: row.province || '',
      min_headcount: Number(row.min_headcount || 3),
      max_headcount: Number(row.max_headcount || 5),
      is_active: Number(row.is_active) !== 0,
    });
  };

  const deleteBranch = async (id) => {
    if (!id) return;
    try {
      await axios.delete(`${API_BASE}/api/branches/${id}`, { withCredentials: true });
      setSnack({ open: true, message: 'Branch deactivated.', severity: 'info' });
      if (editingBranchId && Number(editingBranchId) === Number(id)) resetBranchForm();
      await fetchBranches();
      await fetchStaffingWarnings();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to delete branch';
      setSnack({ open: true, message: msg, severity: 'error' });
    }
  };

  const toggleBranchActive = async (row) => {
    if (!row || !row.id) return;
    const nextActive = Number(row.is_active) === 0;
    try {
      await axios.put(`${API_BASE}/api/branches/${row.id}`, {
        branch_code: row.branch_code,
        branch_name: row.branch_name,
        address: row.address || '',
        city: row.city || '',
        province: row.province || '',
        min_headcount: Number(row.min_headcount || 3),
        max_headcount: Number(row.max_headcount || 5),
        is_active: nextActive,
      }, { withCredentials: true });
      setSnack({ open: true, message: nextActive ? 'Branch activated.' : 'Branch deactivated.', severity: 'success' });
      if (editingBranchId && Number(editingBranchId) === Number(row.id)) {
        setBranchForm((prev) => ({ ...prev, is_active: nextActive }));
      }
      await fetchBranches();
      await fetchStaffingWarnings();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to update branch status';
      setSnack({ open: true, message: msg, severity: 'error' });
    }
  };

  const fetchApplicantCount = useCallback(async () => {
    if (!isHr) return;
    try {
      const res = await axios.get(`${API_BASE}/api/applicants/count`, { withCredentials: true });
      const c = res && res.data && (Number(res.data.count) || 0);
      setApplicantCount(c);
    } catch (err) {
      // ignore errors but keep count as-is
      console.debug('Failed to fetch applicant count', err?.message || err);
      setApplicantCount(0);
      setPageError('Failed to load applicant count.');
    }
  }, []);

  // Fetch leave requests for HR leave management
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [myLeaveRequests, setMyLeaveRequests] = useState([]);
  const [myLeaveLoading, setMyLeaveLoading] = useState(false);
  const [myLeaveError, setMyLeaveError] = useState('');
  const [allowedLeaveTypes, setAllowedLeaveTypes] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState({});
  const [leaveFilterMonth, setLeaveFilterMonth] = useState(String(defaultReportPeriod.month));
  const [leaveFilterYear, setLeaveFilterYear] = useState(String(defaultReportPeriod.year));
  const [leaveStatusFilter, setLeaveStatusFilter] = useState('all');
  const [leaveFilterStartDate, setLeaveFilterStartDate] = useState('');
  const [leaveFilterEndDate, setLeaveFilterEndDate] = useState('');
  useEffect(() => {
    const next = clampReportPeriod(filterMonth, filterYear);
    if (next.month !== Number(filterMonth)) setFilterMonth(next.month);
    if (next.year !== Number(filterYear)) setFilterYear(next.year);
  }, [filterMonth, filterYear]);
  useEffect(() => {
    const next = clampReportPeriod(inventoryReportMonth, inventoryReportYear);
    if (String(next.month) !== String(inventoryReportMonth)) setInventoryReportMonth(String(next.month));
    if (String(next.year) !== String(inventoryReportYear)) setInventoryReportYear(String(next.year));
  }, [inventoryReportMonth, inventoryReportYear]);
  useEffect(() => {
    const next = clampReportPeriod(dtrEvidenceFilters.month, dtrEvidenceFilters.year);
    if (String(next.month) !== String(dtrEvidenceFilters.month) || String(next.year) !== String(dtrEvidenceFilters.year)) {
      setDtrEvidenceFilters((prev) => ({ ...prev, month: String(next.month), year: String(next.year) }));
    }
  }, [dtrEvidenceFilters.month, dtrEvidenceFilters.year]);
  useEffect(() => {
    const next = clampReportPeriod(leaveFilterMonth, leaveFilterYear);
    if (String(next.month) !== String(leaveFilterMonth)) setLeaveFilterMonth(String(next.month));
    if (String(next.year) !== String(leaveFilterYear)) setLeaveFilterYear(String(next.year));
  }, [leaveFilterMonth, leaveFilterYear]);
  useEffect(() => {
    const next = clampReportPeriod(deploymentFilterMonth, deploymentFilterYear);
    if (String(next.month) !== String(deploymentFilterMonth)) setDeploymentFilterMonth(String(next.month));
    if (String(next.year) !== String(deploymentFilterYear)) setDeploymentFilterYear(String(next.year));
  }, [deploymentFilterMonth, deploymentFilterYear]);
  const isPendingLeaveRequest = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    return normalized === 'pending' || normalized === 'pending_approval' || normalized === 'pending_proof';
  };
  const formatLeaveStatusLabel = useCallback((status) => {
    const normalized = String(status || 'pending')
      .trim()
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .toLowerCase();
    if (!normalized) return 'Pending';
    return normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
  }, []);
  const handleMyLeaveMetaLoaded = useCallback((meta = {}) => {
    setAllowedLeaveTypes(Array.isArray(meta?.types) ? meta.types : []);
    setLeaveBalances(meta?.balances && typeof meta.balances === 'object' ? meta.balances : {});
  }, []);
  const fetchLeaveRequests = useCallback(async () => {
    if (!isHr) return;
    try {
      const res = await axios.get(`${API_BASE}/api/leave-requests`, { withCredentials: true });
      const rows = Array.isArray(res.data)
        ? res.data
        : (Array.isArray(res?.data?.rows) ? res.data.rows : []);
      setLeaveRequests(rows);
    } catch (err) {
      console.error('Failed to load leave requests', err);
      setSnack({ open: true, message: 'Failed to load leave requests', severity: 'error' });
      setLeaveRequests([]);
      setPageError('Failed to load leave requests.');
    }
  }, [isHr, inventoryReportMonth, inventoryReportYear]);

  const fetchMyLeaveRequests = useCallback(async () => {
    setMyLeaveLoading(true);
    setMyLeaveError('');
    try {
      const res = await axios.get(`${API_BASE}/api/leave-requests/me`, { withCredentials: true });
      const rows = Array.isArray(res?.data?.rows)
        ? res.data.rows
        : (Array.isArray(res?.data) ? res.data : []);
      setMyLeaveRequests(rows);
    } catch (err) {
      console.error('Failed to load my leave requests', err);
      setMyLeaveRequests([]);
      setMyLeaveError('Failed to load your leave requests.');
    } finally {
      setMyLeaveLoading(false);
    }
  }, []);

  const updateLeaveRequestStatus = async (leaveId, status) => {
    try {
      await axios.put(`${API_BASE}/api/leave-requests/${leaveId}`, { status }, { withCredentials: true });
      setSnack({ open: true, message: `Leave request ${status}.`, severity: 'success' });
      await fetchLeaveRequests();
    } catch (err) {
      const msg = err?.response?.data?.reason || err?.response?.data?.error || `Failed to ${status} leave request`;
      setSnack({ open: true, message: msg, severity: 'error' });
    }
  };

  const filteredLeaveRequests = React.useMemo(() => {
    const monthNum = Number(leaveFilterMonth || 0);
    const yearNum = Number(leaveFilterYear || 0);
    const statusFilter = String(leaveStatusFilter || 'all').trim().toLowerCase();
    const startBound = toDateKey(leaveFilterStartDate);
    const endBound = toDateKey(leaveFilterEndDate);
    return (leaveRequests || []).filter((request) => {
      const dateRequested = toDateKey(request?.date_submitted || request?.date_requested || request?.created_at);
      const startDate = toDateKey(request?.start_date);
      const endDate = toDateKey(request?.end_date);
      const filterDate = dateRequested || startDate || endDate;
      const requestStatus = String(request?.status || 'pending').trim().toLowerCase();
      const requestStatusGroup = isPendingLeaveRequest(requestStatus) ? 'pending' : requestStatus;

      if (monthNum && filterDate) {
        const requestMonth = Number(String(filterDate).slice(5, 7));
        if (requestMonth !== monthNum) return false;
      }
      if (yearNum && filterDate) {
        const requestYear = Number(String(filterDate).slice(0, 4));
        if (requestYear !== yearNum) return false;
      }
      if (statusFilter !== 'all' && requestStatusGroup !== statusFilter) return false;
      if (startBound && endDate && endDate < startBound) return false;
      if (endBound && startDate && startDate > endBound) return false;
      return true;
    });
  }, [leaveRequests, leaveFilterMonth, leaveFilterYear, leaveStatusFilter, leaveFilterStartDate, leaveFilterEndDate]);

  // Refresh leave requests when other parts of the app signal notification refresh
  useEffect(() => {
    const handler = () => {
      try {
        if (activeSection === 'leave-management') fetchLeaveRequests();
        if (activeSection === 'file-leave') fetchMyLeaveRequests();
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('refreshNotifications', handler);
    return () => window.removeEventListener('refreshNotifications', handler);
  }, [activeSection, fetchLeaveRequests, fetchMyLeaveRequests]);

  // Helper to produce friendly, non-numeric error labels for UI snacks.
  // We intentionally avoid exposing raw HTTP codes (e.g. 404/403) per UX request.
  const shortErrorLabel = (err, context) => {
    try {
      const status = err?.response?.status;
      if (status === 401 || status === 403) return 'Not authorized';
      if (status === 404) return (context === 'photos') ? 'No photos found' : 'Not found';
      if (status >= 500) return 'Server error';

      const serverErr = err?.response?.data?.error || err?.response?.data?.message;
      if (serverErr) {
        const s = String(serverErr).trim();
        if (s.length === 0) return 'Request failed';
        // if it's short and helpful, show it; otherwise fall back to a generic label
        if (s.length <= 60) return s;
        return 'Request failed';
      }

      if (err && err.message) return String(err.message).slice(0, 60);
    } catch (e) {}
    return 'Request failed';
  };

  const buildEmpKey = (emp) => (emp && (emp.id || emp.userId || emp.employeeid || emp.username)) || '';

  // All detailed photo management functions removed.

  // Initial load (without holidays to avoid TDZ on fetchHolidays)
  useEffect(() => {
    let mounted = true;
    if (!isHr) {
      initialHrLoadDoneRef.current = false;
      setLoading(false);
      return () => { mounted = false; };
    }
    if (initialHrLoadDoneRef.current) {
      return () => { mounted = false; };
    }
    initialHrLoadDoneRef.current = true;
    setLoading(true);
    (async () => {
      await Promise.allSettled([
        fetchEmployees(),
        fetchPayroll(filterMonth, filterYear),
        fetchAttendance(filterMonth, filterYear),
        fetchApplicantCount(),
        fetchStaffingWarnings(),
      ]);
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [isHr, fetchEmployees, fetchPayroll, fetchAttendance, fetchApplicantCount, fetchStaffingWarnings, filterMonth, filterYear]);

  // When switching to attendance, refresh attendance data
  useEffect(() => {
    if (activeSection === 'attendance') fetchAttendance();
  }, [activeSection, fetchAttendance]);

  useEffect(() => {
    if (activeSection === 'overview') {
      refreshHomeAttendance();
    }
  }, [activeSection, refreshHomeAttendance]);

  useEffect(() => {
    if (activeSection === 'my-payslip') {
      fetchMyPayslip(filterMonth, filterYear);
    }
  }, [activeSection, fetchMyPayslip, filterMonth, filterYear, selectedMyPayslipCutoffId]);

  useEffect(() => {
    if (activeSection === 'my-payslip') {
      fetchMyPayslipCutoffs();
    }
  }, [activeSection, fetchMyPayslipCutoffs]);

  useEffect(() => {
    if (activeSection === 'payroll' || activeSection === 'reports') {
      fetchPayrollCutoffs();
    }
  }, [activeSection, fetchPayrollCutoffs]);

  useEffect(() => {
    if (activeSection === 'payroll' && payrollMode === 'history') {
      fetchPayrollHistoryYears();
    }
  }, [activeSection, payrollMode, fetchPayrollHistoryYears]);

  useEffect(() => {
    if (activeSection === 'payroll' && selectedCutoffId) {
      fetchCutoffRecords(selectedCutoffId);
    }
  }, [activeSection, selectedCutoffId, fetchCutoffRecords]);

  useEffect(() => {
    if (activeSection === 'manual-requests') fetchManualRequests();
  }, [activeSection, fetchManualRequests]);
  useEffect(() => {
    if (activeSection === 'dtr-photos') fetchDtrEvidence();
  }, [activeSection, fetchDtrEvidence]);

  useEffect(() => {
    if (activeSection === 'leave-management') fetchLeaveRequests();
    if (activeSection === 'file-leave') fetchMyLeaveRequests();
  }, [activeSection, fetchLeaveRequests, fetchMyLeaveRequests]);

  useEffect(() => {
    if (!isHr) return;
    fetchManualPendingCount();
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      fetchManualPendingCount();
    }, 30000);
    return () => clearInterval(t);
  }, [isHr, fetchManualPendingCount]);

  // Load history when switching to payroll section in history mode (handled in-place)

  // When switching to applicants, refresh applicants list
  useEffect(() => {
    if (activeSection === 'applicants') fetchApplicants();
  }, [activeSection, fetchApplicants]);

  // When switching to branch management, refresh branches
  useEffect(() => {
    if (activeSection === 'branches') fetchBranches();
  }, [activeSection, fetchBranches]);

  // When switching to deployments, refresh deployment rows + dependencies
  useEffect(() => {
    if (activeSection === 'deployments') {
      fetchDeployments();
      fetchBranches();
      fetchEmployees();
    }
  }, [activeSection, fetchDeployments, fetchBranches, fetchEmployees]);

  useEffect(() => {
    if (activeSection === 'inventory') {
      fetchInventoryItems();
      fetchInventoryRequests();
      fetchInventoryAnalytics();
    }
  }, [activeSection, fetchInventoryItems, fetchInventoryRequests, fetchInventoryAnalytics]);

  useEffect(() => {
    if (activeSection === 'overview') {
      fetchStaffingWarnings();
      fetchInventoryAnalytics();
    }
  }, [activeSection, fetchStaffingWarnings, fetchInventoryAnalytics]);

  useEffect(() => {
    if (activeSection !== 'reports') return;
    fetchGeneralMonthlyReport(filterMonth, filterYear);
    if (selectedTranscriptEmployeeId) {
      fetchIndividualTranscript(selectedTranscriptEmployeeId, filterMonth, filterYear, reportCutoffId, selectedTranscriptUserId);
    }
  }, [activeSection, filterMonth, filterYear, selectedTranscriptEmployeeId, selectedTranscriptUserId, reportCutoffId, fetchGeneralMonthlyReport, fetchIndividualTranscript]);

  // listen for global applicant changes so quick stats can update
  useEffect(() => {
    const h = () => { try { fetchApplicantCount(); } catch (e) {} };
    window.addEventListener && window.addEventListener('applicantsChanged', h);
    return () => { try { window.removeEventListener && window.removeEventListener('applicantsChanged', h); } catch (e) {} };
  }, [fetchApplicantCount]);

  // Listen for payrollUpdated events (dispatched after attendance actions) to refresh payroll in real-time
  useEffect(() => {
    const handler = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (activeSection !== 'payroll' && activeSection !== 'overview') return;
      try { fetchPayroll(filterMonth, filterYear); } catch (e) {}
    };
    window.addEventListener && window.addEventListener('payrollUpdated', handler);
    return () => { try { window.removeEventListener && window.removeEventListener('payrollUpdated', handler); } catch (e) {} };
  }, [fetchPayroll, filterMonth, filterYear, activeSection]);

  // SSE for payroll updates (active for HR role and payroll routes)
  useEffect(() => {
    if (!user || normalizedRole !== 'hr') return undefined;
    if (activeSection !== 'payroll') return undefined;
    if (typeof window === 'undefined') return undefined;

    const runPayrollRefresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try { fetchPayroll(filterMonth, filterYear); } catch (e) {}
    };

    let fallbackInterval = null;
    const startFallbackPolling = () => {
      if (fallbackInterval) return;
      fallbackInterval = setInterval(runPayrollRefresh, 30000);
    };

    if (!window.EventSource) {
      startFallbackPolling();
      return () => { try { if (fallbackInterval) clearInterval(fallbackInterval); } catch (e) {} };
    }

    const url = `${API_BASE}/api/payroll/stream`;
    let resolvedUrl = null;
    try {
      resolvedUrl = new URL(url, window.location.origin);
    } catch (e) {}
    const sameOrigin = !!resolvedUrl && resolvedUrl.origin === window.location.origin;
    if (!sameOrigin) {
      startFallbackPolling();
      return () => { try { if (fallbackInterval) clearInterval(fallbackInterval); } catch (e) {} };
    }

    let es;
    try {
      es = new EventSource(url);
      es.onmessage = (evt) => {
        try {
          if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
          const payload = JSON.parse(evt.data);
          if (payload?.type === 'payroll_update') fetchPayroll();
        } catch (e) {
          // ignore malformed
        }
      };
      es.onerror = () => {
        try { es.close(); } catch (e) {}
        startFallbackPolling();
      };
      esRef.current = es;
    } catch (e) {
      startFallbackPolling();
    }

    return () => {
      try { if (esRef.current) esRef.current.close(); } catch (e) {}
      try { if (fallbackInterval) clearInterval(fallbackInterval); } catch (e) {}
    };
  }, [user, fetchPayroll, normalizedRole, activeSection]);

  // SSE for attendance updates (HR clients)
  const attendanceSseThrottleRef = useRef(0);
  useEffect(() => {
    if (!user || normalizedRole !== 'hr') return undefined;
    if (typeof window === 'undefined') return undefined;
    const shouldLiveRefresh = activeSection === 'overview' || activeSection === 'attendance' || activeSection === 'payroll';
    if (!shouldLiveRefresh) return undefined;

    const runAttendanceRefresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (activeSection === 'overview' || activeSection === 'attendance') {
        try { fetchAttendance(); } catch (e) {}
      }
      if (activeSection === 'payroll' || activeSection === 'overview') {
        try { fetchPayroll(filterMonth, filterYear); } catch (e) {}
      }
    };

    let fallbackInterval = null;
    const startFallbackPolling = () => {
      if (fallbackInterval) return;
      fallbackInterval = setInterval(runAttendanceRefresh, 30000);
    };

    if (!window.EventSource) {
      startFallbackPolling();
      return () => { try { if (fallbackInterval) clearInterval(fallbackInterval); } catch (e) {} };
    }

    const url = `${API_BASE}/api/attendance/stream`;
    let resolvedUrl = null;
    try {
      resolvedUrl = new URL(url, window.location.origin);
    } catch (e) {}
    const sameOrigin = !!resolvedUrl && resolvedUrl.origin === window.location.origin;
    if (!sameOrigin) {
      startFallbackPolling();
      return () => { try { if (fallbackInterval) clearInterval(fallbackInterval); } catch (e) {} };
    }

    let es;
    try {
      es = new EventSource(url);
      es.onmessage = (evt) => {
          try {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            const payload = JSON.parse(evt.data);
            if (payload?.type === 'attendance_update') {
              const now = Date.now();
              // Avoid request storms when multiple attendance events arrive quickly.
              if (now - attendanceSseThrottleRef.current >= 2500) {
                attendanceSseThrottleRef.current = now;
                if (activeSection === 'overview' || activeSection === 'attendance') {
                  fetchAttendance();
                }
                if (activeSection === 'payroll' || activeSection === 'overview') {
                  try { fetchPayroll(filterMonth, filterYear); } catch (e) {}
                }
              }
              // keep a short live list for UI (most recent first)
              try {
                setLiveAttendance(prev => {
                  const next = [payload].concat(prev || []);
                  return next.slice(0, 8);
                });
              } catch (e) {}
            }
          } catch (e) {}
        };
      es.onerror = () => {
        try { es.close(); } catch (e) {}
        startFallbackPolling();
      };
    } catch (e) {
      startFallbackPolling();
    }
    return () => {
      try { if (es) es.close(); } catch (e) {}
      try { if (fallbackInterval) clearInterval(fallbackInterval); } catch (e) {}
    };
  }, [user, fetchAttendance, fetchPayroll, filterMonth, filterYear, normalizedRole, activeSection]);

  // When attendance detail modal is open, poll for updates every 30s to refresh detail row.
  useEffect(() => {
    if (!attendanceDetailOpen) {
      try { if (attendanceDetailIntervalRef.current) { clearInterval(attendanceDetailIntervalRef.current); attendanceDetailIntervalRef.current = null; } } catch (e) {}
      return;
    }
    if (!attendanceDetailLogRef.current) {
      console.log('[AttendanceDetail] polling started');
      attendanceDetailLogRef.current = true;
    }
    let mounted = true;
    const refresh = async () => {
      try {
        setAttendanceDetailLoading(true);
        console.log(`[Polling] Refreshing attendance details (modal open) for selected id=${attendanceDetailRow?.id || attendanceDetailRow?.employeeid || ''}`);
        const rows = await fetchAttendance(filterMonth, filterYear);
        if (!mounted) return;
        const targetId = attendanceDetailRow?.id || attendanceDetailRow?.employeeid;
        if (targetId) {
          const found = (rows || []).find(r => String(r.id || r.employeeid) === String(targetId));
          if (found) setAttendanceDetailRow(found);
        }
      } catch (e) {
        console.debug('Attendance detail refresh failed', e);
      } finally {
        try { if (mounted) setAttendanceDetailLoading(false); } catch (e) {}
      }
    };
    // initial refresh
    refresh();
    attendanceDetailIntervalRef.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      refresh();
    }, 60000);
    return () => { mounted = false; try { if (attendanceDetailIntervalRef.current) { clearInterval(attendanceDetailIntervalRef.current); attendanceDetailIntervalRef.current = null; } } catch (e) {} };
  }, [attendanceDetailOpen, attendanceDetailRow?.id, attendanceDetailRow?.employeeid, fetchAttendance, filterMonth, filterYear]);

  // Build a simple calendar grid for a given month/year
  const buildCalendar = (month, year) => {
    // month is 1-12
    const first = new Date(year, month - 1, 1);
    const startDay = first.getDay(); // 0=Sun
    const daysInMonth = new Date(year, month, 0).getDate();
    const weeks = [];
    let week = new Array(7).fill(null);
    let day = 1;

    // fill first week
    for (let i = startDay; i < 7 && day <= daysInMonth; i++) {
      week[i] = day++;
    }
    weeks.push(week);

    while (day <= daysInMonth) {
      const w = new Array(7).fill(null);
      for (let i = 0; i < 7 && day <= daysInMonth; i++) {
        w[i] = day++;
      }
      weeks.push(w);
    }
    return weeks;
  };

  // compute next employee id based on current employees or provided last id
  const computeNextEmployeeId = (lastId) => {
    const pad = (n) => String(n).padStart(8, '0');
    if (lastId && String(lastId).trim()) {
      // only accept lastId if it's numeric and up to 8 digits
      const s = String(lastId).trim();
      if (/^\d{1,8}$/.test(s)) {
        const n = parseInt(s.replace(/^0+/, '') || '0', 10) + 1;
        return pad(n);
      }
      // otherwise ignore and fall through to compute from employees
    }
    if (employees && employees.length > 0) {
      // find numeric max among IDs that are 1-8 digits (ignore server-generated long ids)
      let max = 0;
      employees.forEach(e => {
        const raw = String(e.employeeid || e.employee_id || e.userId || '').trim();
        if (/^\d{1,8}$/.test(raw)) {
          const n = parseInt(raw.replace(/^0+/, '') || '0', 10);
          if (!isNaN(n) && n > max) max = n;
        }
      });
      if (max === 0) return pad(23);
      return pad(max + 1);
    }
    return pad(23);
  };

  // helpers for employee -> payroll linkage and sorting
  const parseEmployeeNumericId = (e) => {
    const raw = String(e.employeeid || e.employee_id || e.userId || e.username || '').trim();
    const digits = raw.replace(/^0+/, '') || '0';
    const n = parseInt(digits, 10);
    return isNaN(n) ? Infinity : n;
  };

  const toTitleName = useCallback((s) => String(s || '')
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .replace(/\s+/g, ' ')
    .trim(), []);

  const formatNameHr = useCallback(
    (obj) => formatName(obj, { lastFirst: false, middle: 'initial', capitalize: true }),
    []
  );
  const formatNameFull = useCallback(
    (obj) => formatName(obj, { lastFirst: false, middle: 'full', capitalize: true }),
    []
  );

  const getAttendanceRowEmployeeName = useCallback((row) => {
    const directName = formatNameHr(row);
    if (String(directName || '').trim() && String(directName).trim() !== '-') return directName;
    const matchedEmployee = resolveAttendanceRowEmployee(row);
    const matchedName = matchedEmployee ? formatNameHr(matchedEmployee) : '';
    return String(matchedName || '').trim() || '-';
  }, [formatNameHr, resolveAttendanceRowEmployee]);

  const canonicalEmployeeDigits = useCallback((value) => {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (!digits) return '';
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? String(n) : '';
  }, []);

  const matchesSmartEmployeeSearch = useCallback((row, searchTerm) => {
    const q = String(searchTerm || '').trim().toLowerCase();
    if (!q) return true;
    const rowIdRaw = String(row?.employeeid || row?.employee_id || row?.employee_no || row?.userId || row?.username || '');
    const rowIdCanonical = canonicalEmployeeDigits(rowIdRaw);
    const qCanonical = canonicalEmployeeDigits(q);
    if (qCanonical && rowIdCanonical && qCanonical === rowIdCanonical) return true;

    const haystack = [
      formatNameFull(row),
      rowIdRaw,
      row?.role,
      row?.email,
      row?.phone,
      row?.contact,
    ]
      .map((v) => String(v || '').toLowerCase())
      .join(' ');

    return haystack.includes(q);
  }, [canonicalEmployeeDigits]);

  const findLatestPayrollFor = (emp) => {
    if (!payrollRows || payrollRows.length === 0) return null;
    const key = String(emp.employeeid || emp.employee_id || emp.userId || emp.username || '').trim();
    if (!key) return null;
    // find rows matching employee id or userId
    const matches = payrollRows.filter(r => {
      const rk = String(r.employeeid || r.employee_id || r.userId || '').trim();
      return rk && rk === key;
    });
    if (!matches || matches.length === 0) return null;
    // pick latest by year then month
    matches.sort((a,b) => {
      const ay = Number(a.year || 0), by = Number(b.year || 0);
      if (ay !== by) return by - ay;
      const am = Number(a.month || 0), bm = Number(b.month || 0);
      return bm - am;
    });
    return matches[0];
  };

  const statusFilteredEmployees = React.useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    const statusOf = (e) => String(e?.status || 'active').toLowerCase();
    return employeeStatusView === 'terminated'
      ? list.filter((e) => statusOf(e) === 'terminated')
      : employeeStatusView === 'all'
        ? list
        : list.filter((e) => statusOf(e) === 'active');
  }, [employees, employeeStatusView]);

  const searchedEmployees = React.useMemo(() => (
    statusFilteredEmployees.filter((e) => matchesSmartEmployeeSearch(e, employeeSearchTerm))
  ), [statusFilteredEmployees, matchesSmartEmployeeSearch, employeeSearchTerm]);

  const employeeRoleCounts = React.useMemo(() => (
    searchedEmployees.reduce((acc, employee) => {
      const role = String(employee?.role || '').trim().toLowerCase() || 'unknown';
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    }, {})
  ), [searchedEmployees]);

  const employeeRoleOptions = React.useMemo(() => (
    Object.keys(employeeRoleCounts)
      .sort((a, b) => a.localeCompare(b))
      .map((role) => ({
        value: role,
        label: role === 'unknown' ? 'Unknown' : role.charAt(0).toUpperCase() + role.slice(1),
        count: employeeRoleCounts[role] || 0,
      }))
  ), [employeeRoleCounts]);

  const filteredEmployees = React.useMemo(() => {
    if (employeeRoleView === 'all') return searchedEmployees;
    return searchedEmployees.filter((employee) => String(employee?.role || '').trim().toLowerCase() === employeeRoleView);
  }, [searchedEmployees, employeeRoleView]);

  const sortedEmployees = (filteredEmployees || []).slice().sort((a,b) => parseEmployeeNumericId(a) - parseEmployeeNumericId(b));

  const getManilaNow = () => {
    try {
      const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Manila',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const [y, m, d] = fmt.format(new Date()).split('-').map(Number);
      return new Date(y, m - 1, d);
    } catch (e) {
      return new Date();
    }
  };
  // Decide month/year to render: show previous month if current is start of month (to match screenshot showing Oct 2025 on Nov 1)
  const manilaToday = React.useMemo(() => getManilaNow(), []);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = getManilaNow();
    if (d.getDate() === 1) {
      return d.getMonth() === 0 ? 11 : d.getMonth() - 1;
    }
    return d.getMonth();
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const d = getManilaNow();
    if (d.getDate() === 1 && d.getMonth() === 0) {
      return d.getFullYear() - 1;
    }
    return d.getFullYear();
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
  const [pickerMonth, setPickerMonth] = useState(selectedMonth);
  const [pickerYear, setPickerYear] = useState(selectedYear);
  const calendarWeeks = buildCalendar(selectedMonth + 1, selectedYear);
  const monthNames = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  const yearOptions = [];
  for (let y = selectedYear - 5; y <= selectedYear + 5; y++) yearOptions.push(y);

  const handlePrevMonth = () => {
    let m = selectedMonth - 1;
    let y = selectedYear;
    if (m < 0) { m = 11; y -= 1; }
    setSelectedMonth(m);
    setSelectedYear(y);
  };

  const handleNextMonth = () => {
    let m = selectedMonth + 1;
    let y = selectedYear;
    if (m > 11) { m = 0; y += 1; }
    setSelectedMonth(m);
    setSelectedYear(y);
  };

  const handleOpenPicker = (e) => {
    setPickerAnchorEl(e.currentTarget);
    setPickerMonth(selectedMonth);
    setPickerYear(selectedYear);
    setPickerOpen(true);
  };

  const handleClosePicker = () => {
    setPickerOpen(false);
    setPickerAnchorEl(null);
  };

  const handleApplyPicker = () => {
    setSelectedMonth(pickerMonth);
    setSelectedYear(pickerYear);
    handleClosePicker();
  };

  useEffect(() => {
    setBranchForm((prev) => {
      const nextCity = prev.province && availableCities.length > 0 && !availableCities.includes(prev.city)
        ? ''
        : prev.city;
      const recalculatedCode = nextCity ? buildBranchCode(nextCity, editingBranchId) : '';
      if (prev.branch_code === recalculatedCode && prev.city === nextCity) return prev;
      return { ...prev, city: nextCity, branch_code: recalculatedCode };
    });
  }, [branchForm.province, branchForm.city, editingBranchId, availableCities, buildBranchCode]);
  // summary counts for quick stats
  const totalEmployees = employees ? employees.length : 0;
  const employeeCount = employees ? employees.filter(e => String((e.role||'')).toLowerCase() === 'employee').length : 0;
  const custodianCount = employees ? employees.filter(e => String((e.role||'')).toLowerCase() === 'custodian').length : 0;
  // holidays state
  const [holidays, setHolidays] = useState([]);
  const holidaysByDateRef = useRef({});
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [selectedHolidayDate, setSelectedHolidayDate] = useState('');
  const [holidayReason, setHolidayReason] = useState('');
  const [selectedDateIsHoliday, setSelectedDateIsHoliday] = useState(false);
  const [selectedHolidayId, setSelectedHolidayId] = useState(null);

  const fetchHolidays = useCallback(async (month, year) => {
    if (!isHr) return;
    try {
      const q = (Number.isFinite(month) && Number.isFinite(year))
        ? `?month=${String(month + 1)}&year=${String(year)}`
        : '';
      const res = await axios.get(`${API_BASE}/api/holidays${q}`, { withCredentials: true });
      // Support both array and {holidays: [...]} shapes from backend
      const rows = Array.isArray(res.data)
        ? res.data
        : (Array.isArray(res.data?.holidays) ? res.data.holidays : []);
      const normalized = (rows || []).map(h => {
        const dateRaw = h.date || h.holiday_date || h.holidayDate;
        const dateStr = toDateKey(dateRaw);
        const desc = h.description || h.reason || h.title || h.name || h.remarks || h.notes || '';
        const id = h.id || h.holiday_id || h.holidayId || null;
        const typeRaw = h.holiday_type || h.holidayType || h.type || h.kind || h.category || '';
        const typeStr = String(typeRaw || '').toLowerCase();
        const inferred = typeStr === 'special' ? 'special' : 'regular';
        return { id, date: dateStr, description: desc, type: inferred };
      }).filter(h => h.date);
      setHolidays(normalized);
      const map = {};
      normalized.forEach(h => { map[h.date] = { id: h.id, description: h.description, type: h.type }; });
      holidaysByDateRef.current = map;
    } catch (e) {
      console.error('Failed to load holidays', e);
      setHolidays([]);
      holidaysByDateRef.current = {};
    }
  }, [isHr]);

  const handleCalendarMonthChange = useCallback((y, m) => {
    setSelectedYear((prev) => (prev === y ? prev : y));
    setSelectedMonth((prev) => (prev === m ? prev : m));
  }, []);

  const openHolidayDialog = (dateStr) => {
    const holidayObj = dateStr ? (holidaysByDateRef.current && holidaysByDateRef.current[dateStr]) : null;
    setSelectedHolidayDate(dateStr || '');
    setHolidayReason(holidayObj?.description || '');
    setSelectedDateIsHoliday(!!holidayObj);
    setSelectedHolidayId(holidayObj?.id || null);
    setHolidayDialogOpen(true);
  };

  const closeHolidayDialog = () => {
    setHolidayDialogOpen(false);
    setSelectedHolidayDate('');
    setHolidayReason('');
    setSelectedDateIsHoliday(false);
    setSelectedHolidayId(null);
  };

  const handleHolidaySave = async () => {
    if (!selectedHolidayDate) return;
    try {
      const reason = String(holidayReason || '').trim();
      await axios.post(`${API_BASE}/api/holidays`, { date: selectedHolidayDate, description: reason || null }, { withCredentials: true });
      await fetchHolidays();
      try { await fetchAttendance(filterMonth, filterYear); } catch (e) {}
      try { await fetchPayroll(filterMonth, filterYear); } catch (e) {}
      setSnack({ open: true, message: selectedDateIsHoliday ? 'Holiday updated' : 'Holiday added', severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: 'Failed to add holiday', severity: 'error' });
    } finally {
      closeHolidayDialog();
    }
  };

  const handleHolidayRemove = async () => {
    if (!selectedHolidayId) return;
    try {
      await axios.delete(`${API_BASE}/api/holidays/${selectedHolidayId}`, { withCredentials: true });
      await fetchHolidays();
      try { await fetchAttendance(filterMonth, filterYear); } catch (e) {}
      try { await fetchPayroll(filterMonth, filterYear); } catch (e) {}
      setSnack({ open: true, message: 'Holiday removed', severity: 'info' });
    } catch (err) {
      setSnack({ open: true, message: 'Failed to remove holiday', severity: 'error' });
    } finally {
      closeHolidayDialog();
    }
  };

  // Fetch holidays after fetchHolidays is defined
  useEffect(() => {
    try { fetchHolidays(selectedMonth, selectedYear); } catch (e) {}
  }, [fetchHolidays, selectedMonth, selectedYear]);

  const handleSectionChange = (section, opts = {}) => {
    setActiveSection(section);
    if (opts.navigate !== false) {
      try { navigate(`${basePath}?section=${section}`); } catch (e) {}
    }
  };

  const openHrPersonalPayslip = () => {
    setAttendancePersonalOnly(false);
    handleSectionChange('my-payslip');
    setTimeout(() => {
      try { fetchMyPayslipCutoffs(); } catch (e) {}
      try { fetchMyPayslip(filterMonth, filterYear); } catch (e) {}
    }, 0);
  };

  const resolveBranchForDeployment = useCallback((deploymentRow) => {
    if (!deploymentRow) return null;
    const rowBranchId = Number(deploymentRow.branch_id || deploymentRow.branchId || 0);
    if (rowBranchId) {
      const byId = (branches || []).find((b) => Number(b.id || 0) === rowBranchId);
      if (byId) return byId;
    }
    const rowCode = String(deploymentRow.branch_code || '').trim().toUpperCase();
    if (rowCode) {
      const byCode = (branches || []).find((b) => String(b.branch_code || '').trim().toUpperCase() === rowCode);
      if (byCode) return byCode;
    }
    const rowName = String(deploymentRow.branch_name || '').trim().toLowerCase();
    if (rowName) {
      const byName = (branches || []).find((b) => String(b.branch_name || '').trim().toLowerCase() === rowName);
      if (byName) return byName;
    }
    return null;
  }, [branches]);

  const openDeploymentEmployeeDetail = useCallback((deploymentRow) => {
    const depEmpNoRaw = deploymentRow?.employeeid || deploymentRow?.user_employee_no || deploymentRow?.employee_no || '';
    const depEmpNo = String(depEmpNoRaw).replace(/\D/g, '').padStart(8, '0');
    const depUserId = Number(deploymentRow?.user_id || deploymentRow?.userId || 0);
    const linked = (employees || []).find((e) => {
      const eNo = String(e.employeeid || e.employee_no || '').replace(/\D/g, '').padStart(8, '0');
      const eId = Number(e.id || e.userId || 0);
      if (depEmpNo && depEmpNo !== '00000000' && eNo === depEmpNo) return true;
      if (depUserId && eId === depUserId) return true;
      return false;
    });
    const branch = resolveBranchForDeployment(deploymentRow);
    const detail = {
      ...(deploymentRow || {}),
      ...(linked || {}),
      employeeid: depEmpNo,
      role: (linked?.role || deploymentRow?.role || '').toString(),
      branch_code: deploymentRow?.branch_code || branch?.branch_code || '',
      branch_name: deploymentRow?.branch_name || branch?.branch_name || '',
      branch_city: branch?.city || '',
      branch_province: branch?.province || '',
    };
    handleOpenEmployeeDetail(detail, { includePhotos: false });
  }, [employees, resolveBranchForDeployment, handleOpenEmployeeDetail]);

  const openBranchDeploymentDialog = useCallback(async (branch) => {
    if (!branch) return;
    setBranchDeploymentDialog({ open: true, branch });
    if ((!deployments || deployments.length === 0) && isHr) {
      try { await fetchDeployments(); } catch (e) {}
    }
  }, [deployments, isHr, fetchDeployments]);

  const selectedBranchDeploymentRows = React.useMemo(() => {
    const branch = branchDeploymentDialog.branch;
    if (!branch) return [];
    return (deployments || []).filter((row) => {
      const resolved = resolveBranchForDeployment(row);
      if (Number(row?.branch_id || 0) && Number(row.branch_id) === Number(branch.id)) return true;
      if (resolved && Number(resolved.id || 0) === Number(branch.id)) return true;
      return false;
    }).sort((a, b) => {
      const statusOrder = { active: 0, upcoming: 1, ended: 2 };
      const aStatus = String(getDeploymentStatusLabel(a) || '').toLowerCase();
      const bStatus = String(getDeploymentStatusLabel(b) || '').toLowerCase();
      const statusDiff = (statusOrder[aStatus] ?? 9) - (statusOrder[bStatus] ?? 9);
      if (statusDiff !== 0) return statusDiff;
      return String(b.start_date || '').localeCompare(String(a.start_date || ''));
    });
  }, [branchDeploymentDialog.branch, deployments, resolveBranchForDeployment]);

  const openManualRequestDetail = useCallback(async (requestRow) => {
    const reqEmpNoRaw = requestRow?.employee_no || '';
    const reqEmpNo = String(reqEmpNoRaw).replace(/\D/g, '').padStart(8, '0');
    let linked = (employees || []).find((e) => {
      const eNo = String(e.employeeid || e.employee_no || '').replace(/\D/g, '').padStart(8, '0');
      return reqEmpNo && reqEmpNo !== '00000000' && eNo === reqEmpNo;
    });
    if (!linked) {
      try {
        await fetchEmployees();
      } catch (e) {}
      linked = (employees || []).find((e) => {
        const eNo = String(e.employeeid || e.employee_no || '').replace(/\D/g, '').padStart(8, '0');
        return reqEmpNo && reqEmpNo !== '00000000' && eNo === reqEmpNo;
      });
    }
    if ((!deployments || deployments.length === 0) && isHr) {
      try { await fetchDeployments(); } catch (e) {}
    }
    const dep = (deployments || []).find((d) => {
      const dNo = String(d.employeeid || d.user_employee_no || d.employee_no || '').replace(/\D/g, '').padStart(8, '0');
      return reqEmpNo && reqEmpNo !== '00000000' && dNo === reqEmpNo;
    }) || null;
    const branch = resolveBranchForDeployment(dep || {});
    const detail = {
      ...(requestRow || {}),
      ...(dep || {}),
      ...(linked || {}),
      employeeid: reqEmpNo,
      employee_no: requestRow?.employee_no || linked?.employee_no || '',
      employee_name: requestRow?.employee_name || linked?.username || '',
      branch_code: dep?.branch_code || branch?.branch_code || linked?.branch_code || '',
      branch_name: dep?.branch_name || branch?.branch_name || linked?.branch_name || '',
      branch_city: branch?.city || linked?.branch_city || '',
      branch_province: branch?.province || linked?.branch_province || '',
      shift_start: dep?.shift_start || linked?.shift_start || '',
      shift_end: dep?.shift_end || linked?.shift_end || '',
    };
    setManualRequestEvidenceLoading(true);
    setManualRequestEvidence([]);
    try {
      const evidenceRes = await axios.get(`${API_BASE}/api/attendance/manual-requests/${requestRow.id}/evidence`, { withCredentials: true });
      const evidenceRows = Array.isArray(evidenceRes?.data?.rows) ? evidenceRes.data.rows : [];
      setManualRequestEvidence(evidenceRows);
    } catch (e) {
      setManualRequestEvidence([]);
    } finally {
      setManualRequestEvidenceLoading(false);
    }
    setManualRequestDetail({
      reason: String(requestRow?.reason || '').trim(),
      requested_date: requestRow?.requested_date || '',
      requested_time: requestRow?.requested_time || '',
      status: String(requestRow?.status || 'pending').trim().toLowerCase(),
    });
    handleOpenEmployeeDetail(detail, { includePhotos: true });
  }, [employees, deployments, isHr, fetchEmployees, fetchDeployments, resolveBranchForDeployment, handleOpenEmployeeDetail]);

  const filteredDeployments = React.useMemo(() => {
    const q = String(deploymentSearchTerm || '').trim().toLowerCase();
    const qDigits = q.replace(/\D/g, '');
    const qNum = qDigits ? Number(qDigits) : NaN;
    const selectedMonth = Number(deploymentFilterMonth);
    const selectedYear = Number(deploymentFilterYear);
    const monthStart = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
    const monthEndDay = new Date(selectedYear, selectedMonth, 0).getDate();
    const monthEnd = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(monthEndDay).padStart(2, '0')}`;
    const statusFilter = String(deploymentStatusFilter || 'active').toLowerCase();

    return (deployments || []).filter((d) => {
      const statusLabel = String(getDeploymentStatusLabel(d) || '').toLowerCase();
      if (statusFilter !== 'all' && statusLabel !== statusFilter) return false;

      const startKey = normalizeDeploymentDateKey(d?.start_date, '0001-01-01');
      const endKey = normalizeDeploymentDateKey(d?.end_date, startKey || '9999-12-31');
      if (startKey > monthEnd || endKey < monthStart) return false;

      if (!q) return true;

      const branch = resolveBranchForDeployment(d);
      const branchCode = String(d.branch_code || branch?.branch_code || '').toUpperCase();
      const branchName = String(d.branch_name || branch?.branch_name || '').toLowerCase();
      const branchCity = String(branch?.city || '').toLowerCase();
      const branchProvince = String(branch?.province || '').toLowerCase();
      const displayName = (formatNameFull(d) || '').toLowerCase();
      const empNo = String(d.employeeid || d.user_employee_no || d.employee_no || '').padStart(8, '0');
      const haystack = `${branchCode} ${branchName} ${branchCity} ${branchProvince} ${displayName} ${empNo}`.toLowerCase();
      if (haystack.includes(q)) return true;

      // Numeric query like "01" should match all branch codes ending in "-01"
      if (Number.isFinite(qNum) && branchCode) {
        const m = branchCode.match(/-(\d+)\s*$/);
        if (m) {
          const seq = Number(m[1]);
          if (Number.isFinite(seq) && seq === qNum) return true;
        }
      }
      return false;
    });
  }, [
    deployments,
    deploymentFilterMonth,
    deploymentFilterYear,
    deploymentSearchTerm,
    deploymentStatusFilter,
    resolveBranchForDeployment,
  ]);
  const handleMyPayslipPrev = () => {
    setFilterMonth((prevMonth) => {
      if (prevMonth === 1) {
        setFilterYear((y) => y - 1);
        return 12;
      }
      return prevMonth - 1;
    });
  };
  const handleMyPayslipNext = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    if (filterYear > currentYear || (filterYear === currentYear && filterMonth >= currentMonth)) return;
    setFilterMonth((prevMonth) => {
      if (prevMonth === 12) {
        setFilterYear((y) => y + 1);
        return 1;
      }
      return prevMonth + 1;
    });
  };
  const printPayslipOnly = () => {
    const node = document.querySelector('.payslip-print');
    if (!node) return window.print();
    const popup = window.open('', '_blank', 'width=900,height=1200');
    if (!popup) return window.print();
    popup.document.write(`
      <html>
        <head>
          <title>Payslip</title>
          <meta charset="utf-8" />
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #111; }
            * { box-sizing: border-box; }
            table { width: 100% !important; border-collapse: collapse; table-layout: fixed; }
            th, td { border: 1px solid #d0d0d0; padding: 6px; vertical-align: top; word-break: break-word; }
          </style>
        </head>
        <body>${node.outerHTML}</body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
    popup.close();
  };

  useEffect(() => {
    const section = new URLSearchParams(location.search || '').get('section');
    const nextSection = normalizeVisibleSection(section || activeSection);
    if (nextSection !== activeSection) setActiveSection(nextSection);
    if (section && nextSection !== section) {
      try { navigate(`${basePath}?section=${nextSection}`, { replace: true }); } catch (e) {}
    }
  }, [location.search, activeSection, normalizeVisibleSection, navigate, basePath]);

  useEffect(() => {
    const target = `${basePath}?section=${activeSection}`;
    const current = `${location.pathname}${location.search || ''}`;
    if (!location.search && location.pathname === basePath) {
      if (current !== target) {
        try { navigate(target, { replace: true }); } catch (e) {}
      }
    }
  }, [location.search, location.pathname, activeSection, navigate]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  // Export CSV
  // Build visible payroll rows for the UI (current mode):
  // - If server returned rows for the period, filter out HR
  // - If none, synthesize placeholder rows from the employees list so accounts are visible
  const getVisiblePayrollRows = useCallback(() => {
    const excludeHR = (role) => String(role || '').toLowerCase() === 'hr';
    const search = String(payrollSearchTerm || '').trim();
    if (Array.isArray(payrollRows) && payrollRows.length > 0) {
      return payrollRows.filter(r => {
        const key = String(r.employeeid || r.employee_id || r.userId || r.username || '');
        const emp = (employees || []).find(e => String(e.employeeid || e.employee_id || e.userId || e.username || '') === key);
        const role = emp ? (emp.role || r.role) : (r.role || '');
        if (excludeHR(role)) return false;
        if (!search) return true;
        return matchesSmartEmployeeSearch({ ...(r || {}), ...(emp || {}) }, search);
      });
    }
    // placeholders from employees
    return (employees || [])
      .filter(e => !excludeHR(e.role))
      .filter(e => !search || matchesSmartEmployeeSearch(e, search))
      .map(e => ({
      _placeholder: true,
      employeeid: e.employeeid || e.employee_id || e.userId || e.username || '',
      firstname: e.firstname,
      lastname: e.lastname,
      middlename: e.middlename,
      role: e.role,
      baseSalary: e.baseSalary || e.base_salary_per_day || e.base_salary || '',
      month: filterMonth,
      year: filterYear,
      absences: 0,
      half_days: 0,
      holidays: 0,
      overtime_hours: 0,
      bonuses: 0,
      net: 0,
    }));
  }, [payrollRows, employees, filterMonth, filterYear, payrollSearchTerm, matchesSmartEmployeeSearch]);

  const exportCSV = useCallback(() => {
    const visible = getVisiblePayrollRows();
    if (!visible || visible.length === 0) {
      setSnack({ open: true, message: 'No payroll rows to export', severity: 'info' });
      return;
    }
    const headers = ['Employee ID','Name','Month','Year','Role','Absences','Hours','Overtime','Gross','SSS','PhilHealth','Pag-IBIG','Net'];
    const rows = visible.map(r => [
      r.employeeid || r.userId || '',
      (formatNameHr(r) || '').replace(/\r?\n|,/g, ' '),
      r.month || filterMonth,
      r.year || filterYear,
      r.role || '',
      r.absences || 0,
      r.totalHours || 0,
      r.overtimeHours || r.overtime_hours || 0,
      r.gross || 0,
      r.sss_no || (r.sss ? 'Yes' : 'No') || '',
      r.philhealth_no || (r.philhealth ? 'Yes' : 'No') || '',
      r.pagibig_no || (r.pagibig ? 'Yes' : 'No') || '',
      r.netSalary || r.net || 0,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const name = `payroll-${filterYear}-${String(filterMonth).padStart(2,'0')}-${new Date().toISOString().slice(0,10)}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    setSnack({ open: true, message: `Exported ${visible.length} rows`, severity: 'success' });
  }, [getVisiblePayrollRows, filterMonth, filterYear]);

  const todayManilaIso = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const visiblePayrollCutoffs = React.useMemo(() => {
    const wanted = String(cutoffStatusFilter || 'all').toLowerCase();
    const rows = Array.isArray(payrollCutoffs) ? payrollCutoffs : [];
    if (wanted === 'all') return rows;
    return rows.filter((row) => String(row?.status || '').toLowerCase() === wanted);
  }, [payrollCutoffs, cutoffStatusFilter]);

  useEffect(() => {
    if (!selectedCutoffId) return;
    if (visiblePayrollCutoffs.some((row) => String(row.id) === String(selectedCutoffId))) return;
    setSelectedCutoffId(visiblePayrollCutoffs[0] ? String(visiblePayrollCutoffs[0].id) : '');
  }, [visiblePayrollCutoffs, selectedCutoffId]);

  const selectedCutoff = (payrollCutoffs || []).find((row) => String(row.id) === String(selectedCutoffId)) || null;
  const selectedReportCutoff = (payrollCutoffs || []).find((row) => String(row.id) === String(reportCutoffId)) || null;
  const selectedCutoffEnded = Boolean(selectedCutoff?.period_end) && String(selectedCutoff.period_end) <= todayManilaIso;
  const selectedCutoffStatus = String(selectedCutoff?.status || 'Open');
  const canGenerateSelectedCutoff = Boolean(selectedCutoffId) && !['Approved', 'Released'].includes(selectedCutoffStatus);
  const canRecomputeSelectedCutoff = canGenerateSelectedCutoff;
  const canApproveSelectedCutoff = Boolean(selectedCutoffId) && selectedCutoffStatus === 'Processing';
  const filteredMonthlyReportRows = React.useMemo(() => {
    const rows = Array.isArray(generalMonthlyReport?.rows) ? [...generalMonthlyReport.rows] : [];
    const filtered = rows.filter((row) => {
      if (reportRoleFilter === 'all') return true;
      return String(row?.role || '').toLowerCase() === reportRoleFilter;
    });
    filtered.sort((left, right) => {
      const leftId = String(left?.employeeid || '').trim();
      const rightId = String(right?.employeeid || '').trim();
      const leftIsNumeric = /^\d+$/.test(leftId);
      const rightIsNumeric = /^\d+$/.test(rightId);
      if (leftIsNumeric && rightIsNumeric) {
        const diff = Number(leftId) - Number(rightId);
        if (diff !== 0) return diff;
      } else {
        const byId = leftId.localeCompare(rightId);
        if (byId !== 0) return byId;
      }
      return String(left?.employee_name || '').localeCompare(String(right?.employee_name || ''));
    });
    return filtered;
  }, [generalMonthlyReport, reportRoleFilter]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', overflow: 'hidden' }}>
      {/* Left navigation similar to EmployeeDashboard (blue sidebar) */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, width: 260, flex: '0 0 260px', backgroundColor: '#3b63b8', color: 'white', flexDirection: 'column', p: 2, overflowY: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ width: 44, height: 44, bgcolor: 'white', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: '#2f74d0', fontWeight: 'bold' }}>EZ</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>EZRA GENERAL</Typography>
              <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>SERVICES</Typography>
            </Box>
          </Box>

          <List sx={{ color: 'white', px: { xs: 0, md: 0 } }}>
            <Typography variant="overline" sx={{ color: '#dbe6ff', px: 2, opacity: 0.9 }}>HR Work</Typography>
            <SidebarNavItem label="Home" icon={<HomeIcon />} to={`${basePath}?section=overview`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('overview'); }} />
            <SidebarNavItem label="Employee Records" icon={<PersonIcon />} to={`${basePath}?section=employees`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('employees'); }} />
            <SidebarNavItem label="Attendance and Schedule Reports" icon={<AssignmentIcon />} to={`${basePath}?section=reports`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('reports'); }} />
            <SidebarNavItem label="Attendance Monitoring" icon={<HistoryIcon />} to={`${basePath}?section=attendance`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('attendance'); }} />
            <SidebarNavItem
              label={
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <span>Manual Attendance Requests</span>
                  <Badge color="error" badgeContent={manualPendingCount} max={99} invisible={!manualPendingCount} />
                </Box>
              }
              icon={<AssignmentIcon />}
              to={`${basePath}?section=manual-requests`}
              exact
              onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('manual-requests'); }}
            />
            <SidebarNavItem label="Attendance Evidence / DTR Photos" icon={<AssignmentIcon />} to={`${basePath}?section=dtr-photos`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('dtr-photos'); }} />
            <SidebarNavItem label="Work Site Scheduling" icon={<AssignmentIcon />} to={`${basePath}?section=branches`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('branches'); }} />
            <SidebarNavItem label="Employee Schedules" icon={<AssignmentIcon />} to={`${basePath}?section=deployments`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('deployments'); }} />
          </List>
        </Box>

          <Box sx={{ mt: 'auto', p: 2 }}>
            <Divider sx={{ bgcolor: '#ffffff33', mb: 2 }} />
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }}
              onClick={() => setProfileOpen(true)}
            >
              <Avatar sx={{ bgcolor: '#e9eef9', color: '#2f74d0' }}>{(user && user.firstname ? user.firstname[0] : 'H')}</Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{user ? (formatNameFull(user) || 'HR') : 'HR'}</Typography>
                <Typography variant="caption">{user && (user.employeeid || user.username) ? String(user.employeeid || user.username).padStart(8,'0') : ''}</Typography>
              </Box>
          </Box>
          <Button variant="contained" color="error" startIcon={<LogoutIcon />} onClick={() => { try { onLogout && onLogout(); } catch (e) {} }} fullWidth>Logout</Button>
        </Box>
      </Box>

        {/* Mobile Drawer: same sidebar content for small screens */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 260 } }}
        >
          <Box sx={{ width: 260, backgroundColor: '#3b63b8', color: 'white', height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ width: 44, height: 44, bgcolor: 'white', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#2f74d0', fontWeight: 'bold' }}>EZ</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>EZRA GENERAL</Typography>
                  <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>SERVICES</Typography>
                </Box>
              </Box>

              <List sx={{ color: 'white' }}>
                <Typography variant="overline" sx={{ color: '#dbe6ff', px: 2, opacity: 0.9 }}>HR Work</Typography>
                <SidebarNavItem label="Home" icon={<HomeIcon />} to={`${basePath}?section=overview`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('overview'); handleDrawerToggle(); }} />
                <SidebarNavItem label="Employee Records" icon={<PersonIcon />} to={`${basePath}?section=employees`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('employees'); handleDrawerToggle(); }} />
                <SidebarNavItem label="Attendance and Schedule Reports" icon={<AssignmentIcon />} to={`${basePath}?section=reports`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('reports'); handleDrawerToggle(); }} />
                <SidebarNavItem label="Attendance Monitoring" icon={<HistoryIcon />} to={`${basePath}?section=attendance`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('attendance'); handleDrawerToggle(); }} />
                <SidebarNavItem
                  label={
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      <span>Manual Attendance Requests</span>
                      <Badge color="error" badgeContent={manualPendingCount} max={99} invisible={!manualPendingCount} />
                    </Box>
                  }
                  icon={<AssignmentIcon />}
                  to={`${basePath}?section=manual-requests`}
                  exact
                  onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('manual-requests'); handleDrawerToggle(); }}
                />
                <SidebarNavItem label="Attendance Evidence / DTR Photos" icon={<AssignmentIcon />} to={`${basePath}?section=dtr-photos`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('dtr-photos'); handleDrawerToggle(); }} />
                <SidebarNavItem label="Work Site Scheduling" icon={<AssignmentIcon />} to={`${basePath}?section=branches`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('branches'); handleDrawerToggle(); }} />
                <SidebarNavItem label="Employee Schedules" icon={<AssignmentIcon />} to={`${basePath}?section=deployments`} exact onClick={() => { setAttendancePersonalOnly(false); handleSectionChange('deployments'); handleDrawerToggle(); }} />
              </List>
            </Box>

            <Box sx={{ mt: 'auto', p: 2 }}>
              <Divider sx={{ bgcolor: '#ffffff33', mb: 2 }} />
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }}
                onClick={() => setProfileOpen(true)}
              >
                <Avatar sx={{ bgcolor: '#e9eef9', color: '#2f74d0' }}>{(user && user.firstname ? user.firstname[0] : 'H')}</Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{user ? (formatNameFull(user) || 'HR') : 'HR'}</Typography>
                  <Typography variant="caption">{user && (user.employeeid || user.username) ? String(user.employeeid || user.username).padStart(8,'0') : ''}</Typography>
                </Box>
              </Box>
              <Button variant="contained" color="error" startIcon={<LogoutIcon />} onClick={() => { try { onLogout && onLogout(); } catch (e) {} }} fullWidth>Logout</Button>
            </Box>
          </Box>
        </Drawer>

      {/* Main content area */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Mobile top bar with hamburger */}
        <AppBar position="static" sx={{ display: { md: 'none' }, bgcolor: 'white', color: '#183152', boxShadow: 'none', borderBottom: '1px solid #e2e8f0' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>EZRA</Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 } }}>
          {pageError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {pageError}
            </Alert>
          )}
          {/* Overview: header, stats, calendar */}
          {activeSection === 'overview' && (
            <>
              <Grid container spacing={2}>
              <Grid xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2, backgroundColor: 'white', height: '100%', minHeight: 170, display: 'flex', flexDirection: 'column', justifyContent: 'center' }} elevation={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#183152' }}>Hi HR Account</Typography>
                      <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>Welcome to your HR Dashboard</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 1 }}>
                      <LiveDateTimeWidget />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" color={homeIsOnBreak ? 'info' : 'secondary'} onClick={homeIsOnBreak ? endHrBreak : startHrBreak} disabled={homeAttendanceLoading || !homeHasOpenShift}>
                          {homeIsOnBreak ? 'END BREAK' : 'START BREAK'}
                        </Button>
                        <Button variant="outlined" color="error" onClick={markHrTimeOut} disabled={homeAttendanceLoading || !homeHasOpenShift || homeIsOnBreak}>
                          TIME OUT
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                  {homeAttendanceError && (
                    <Alert severity="error" sx={{ mt: 2 }}>{homeAttendanceError}</Alert>
                  )}
                </Paper>
              </Grid>

              <Grid xs={12} md={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Quick Stats</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Paper sx={{ flex: 1, p: 2, textAlign: 'center', borderRadius: 1 }} elevation={1}>
                      <Typography variant="h4" sx={{ color: '#2f74d0', fontWeight: 700 }}>{totalEmployees}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Total Employees</Typography>
                    </Paper>
                    <Paper sx={{ flex: 1, p: 2, textAlign: 'center', borderRadius: 1 }} elevation={1}>
                      <Typography variant="h4" sx={{ color: '#2fb86a', fontWeight: 700 }}>{employeeCount}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Employees</Typography>
                    </Paper>
                    <Paper sx={{ flex: 1, p: 2, textAlign: 'center', borderRadius: 1 }} elevation={1}>
                      <Typography variant="h4" sx={{ color: '#a24bd6', fontWeight: 700 }}>{custodianCount}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Custodians</Typography>
                    </Paper>
                    <Paper sx={{ flex: 1, p: 2, textAlign: 'center', borderRadius: 1 }} elevation={1}>
                      <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 700 }}>{manualPendingCount}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Pending Manual Requests</Typography>
                    </Paper>
                  </Box>
                </Paper>
              </Grid>

              <Grid xs={12} md={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Operational Warnings</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip size="small" color="error" label={`Critical: ${Number(warningData?.counts?.critical || 0)}`} />
                      <Chip size="small" color="warning" label={`Warning: ${Number(warningData?.counts?.warning || 0)}`} />
                    </Box>
                  </Box>
                  {warningsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      {warningData.understaffedBranches.slice(0, 3).map((w) => (
                        <Alert key={`under-${w.id}`} severity={w.severity === 'critical' ? 'error' : 'warning'}>
                          {`${w.branch_code || '-'} - ${w.branch_name || '-'}: assigned ${Number(w.active_assigned || 0)} / minimum ${Number(w.min_headcount || 0)} (missing ${Number(w.missing_count || 0)})`}
                        </Alert>
                      ))}
                      {warningData.contractsEndingSoon.slice(0, 3).map((w) => (
                        <Alert key={`contract-${w.id}`} severity={Number(w.days_left || 0) <= 7 ? 'error' : 'warning'}>
                          {`${String(w.employeeid || '').padStart(8, '0')} - ${formatNameHr(w)} contract ends in ${Number(w.days_left || 0)} day(s)`}
                        </Alert>
                      ))}
                      {warningData.understaffedBranches.length === 0 && warningData.contractsEndingSoon.length === 0 && (
                        <Alert severity="success">No active staffing or contract warnings.</Alert>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid xs={12} md={6}>
                <Paper sx={{ p: 2, borderRadius: 2, height: '100%', minHeight: 170 }} elevation={0}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Attendance Review Queue</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Chip
                        size="small"
                        color="error"
                        label={`Needs Review: ${manualPendingCount}`}
                      />
                      <Chip
                        size="small"
                        color="warning"
                        label={`Schedule Warnings: ${Number(warningData?.counts?.warning || 0)}`}
                      />
                    </Box>
                  </Box>
                  {warningsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : manualPendingCount === 0 && warningData.contractsEndingSoon.length === 0 && warningData.understaffedBranches.length === 0 ? (
                    <Alert severity="success">No attendance or schedule records need review.</Alert>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 1, maxHeight: 255, overflowY: 'auto', pr: 0.5 }}>
                      {manualPendingCount > 0 && (
                        <Alert severity="warning">Manual attendance requests pending HR validation: {manualPendingCount}</Alert>
                      )}
                      {warningData.understaffedBranches.slice(0, 4).map((w) => (
                        <Alert key={`review-site-${w.id}`} severity={w.severity === 'critical' ? 'error' : 'warning'}>
                          {`${w.branch_code || '-'} - ${w.branch_name || '-'} schedule staffing needs review.`}
                        </Alert>
                      ))}
                      {warningData.contractsEndingSoon.slice(0, 4).map((w) => (
                        <Alert key={`review-schedule-${w.id}`} severity="warning">
                          {`${String(w.employeeid || '').padStart(8, '0')} - ${formatNameHr(w)} schedule ends soon.`}
                        </Alert>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid xs={12} lg={7}>
                <Paper className="bubble-card" sx={{ p: 2.5, borderRadius: 3, height: '100%', overflow: 'hidden' }} elevation={0}>
                  <Typography variant="h6" gutterBottom>Personal Calendar</Typography>
                  <Box sx={{ maxHeight: { xs: 'none', lg: 620 }, overflow: 'auto', pr: 0.5 }}>
                    <PersonalCalendar
                      employeeId={user?.employeeid || user?.employee_no || user?.id}
                      holidays={holidays}
                      initialMonth={{ year: selectedYear, monthIndex: selectedMonth }}
                      today={manilaToday}
                      onMonthChange={handleCalendarMonthChange}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid xs={12} lg={5}>
                <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }} elevation={0}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Attendance Recorder</Typography>
                  {homeAttendanceLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Time In</TableCell>
                          <TableCell>Time Out</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {homeAttendanceRows.length === 0 ? (
                          <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 2 }}>No attendance records</TableCell></TableRow>
                        ) : homeAttendanceRows.map((row, idx) => (
                          <TableRow key={row.id || idx}>
                            <TableCell>{formatDateOnly(row.log_date || row.date)}</TableCell>
                            <TableCell>{formatTime(row.log_time || row.time_in || row.timeIn)}</TableCell>
                            <TableCell>{formatTime(row.time_out || row.timeOut)}</TableCell>
                            <TableCell>{renderAttendanceMethodChip(getAttendanceMethod(row))}</TableCell>
                            <TableCell>{renderAttendanceStatusChip(getAttendanceStatus(row))}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              </Grid>
              </Grid>
            </>
          )}

          {activeSection === 'my-payslip' && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>My Payslip</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button variant="outlined" onClick={printPayslipOnly}>PRINT</Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      fetchMyPayslipCutoffs();
                      fetchMyPayslip(filterMonth, filterYear, selectedMyPayslipCutoffId);
                    }}
                  >
                    REFRESH
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {myPayslipCutoffs.length > 0 ? (
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 430 }, maxWidth: { xs: '100%', sm: 560 } }}>
                    <InputLabel id="hr-my-payslip-cutoff-label">Pay Period</InputLabel>
                    <Select
                      labelId="hr-my-payslip-cutoff-label"
                      label="Pay Period"
                      value={selectedMyPayslipCutoffId}
                      onChange={(e) => setSelectedMyPayslipCutoffId(String(e.target.value || ''))}
                    >
                      {myPayslipCutoffs.map((cutoff) => (
                        <MenuItem key={cutoff.id} value={String(cutoff.id)}>
                          {`${formatDateOnly(cutoff.period_start)} - ${formatDateOnly(cutoff.period_end)} | Pay Date ${formatDateOnly(cutoff.pay_date)} | ${cutoff.status || ''}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <>
                  <TextField
                    select
                    label="Month"
                    size="small"
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(Number(e.target.value || defaultReportPeriod.month))}
                    sx={{ minWidth: 140 }}
                  >
                    {getMonthOptionsForYear(filterYear).map((month) => (
                      <MenuItem key={month} value={month}>{APP_MONTH_NAMES[month - 1]}</MenuItem>
                    ))}
                  </TextField>
                <TextField
                  select
                  label="Year"
                  size="small"
                  value={filterYear}
                  onChange={(e) => {
                    const next = clampReportPeriod(filterMonth, e.target.value);
                    setFilterYear(next.year);
                    setFilterMonth(next.month);
                  }}
                  sx={{ width: 120 }}
                >
                  {reportYearOptions.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={() => fetchMyPayslip(filterMonth, filterYear)}>APPLY</Button>
                  </>
                )}
              </Box>
              {myPayslipLoading && <CircularProgress size={22} />}
              {!myPayslipLoading && myPayslipError && <Alert severity="error">{myPayslipError}</Alert>}
              {!myPayslipLoading && !myPayslipError && !myPayslip && (
                <Typography color="text.secondary">No payslip found for this period.</Typography>
              )}
              {!myPayslipLoading && myPayslip && <PayslipCard payslip={myPayslip} />}
            </Paper>
          )}

          {/* Employees section */}
          {activeSection === 'employees' && (
            <Paper sx={{ p: { xs: 2, md: 3 }, width: '100%', maxWidth: '100%', height: { xs: 'auto', md: 'calc(100vh - 120px)' }, overflow: 'hidden', borderRadius: 2, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>Employee List</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                  <Button startIcon={<AddIcon />} variant="contained" color="primary" onClick={openAdd} sx={{ textTransform: 'none', width: { xs: '100%', md: 'auto' } }}>ADD EMPLOYEE</Button>
                </Box>
              </Box>

              <Box
                sx={{
                  mb: 2,
                  display: 'grid',
                  gap: 1.5,
                  alignItems: 'center',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    lg: 'minmax(260px, 1fr) 200px 200px auto',
                  },
                }}
              >
                <TextField
                  placeholder="Search employees (name, role, ID)"
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{ sx: { backgroundColor: '#fafafa', borderRadius: 1, py: 1 } }}
                />
                <FormControl size="small" sx={{ minWidth: 0 }}>
                  <InputLabel>Account View</InputLabel>
                  <Select
                    value={employeeStatusView}
                    label="Account View"
                    onChange={(e) => setEmployeeStatusView(e.target.value)}
                  >
                    <MenuItem value="active">Active only</MenuItem>
                    <MenuItem value="terminated">Terminated only (Archive)</MenuItem>
                    <MenuItem value="all">Active + Terminated</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 0 }}>
                  <InputLabel>Role Filter</InputLabel>
                  <Select
                    value={employeeRoleView}
                    label="Role Filter"
                    onChange={(e) => setEmployeeRoleView(e.target.value)}
                  >
                    <MenuItem value="all">{`All roles (${searchedEmployees.length})`}</MenuItem>
                    {employeeRoleOptions.map((roleOption) => (
                      <MenuItem key={roleOption.value} value={roleOption.value}>
                        {`${roleOption.label} (${roleOption.count})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" size="small" onClick={() => fetchEmployees()} sx={{ width: { xs: '100%', lg: 'auto' } }}>REFRESH</Button>
              </Box>

              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip color="primary" variant="outlined" label={`Total shown: ${sortedEmployees.length}`} />
                <Chip variant="outlined" label={`HR: ${employeeRoleCounts.hr || 0}`} />
                <Chip variant="outlined" label={`Custodian: ${employeeRoleCounts.custodian || 0}`} />
                <Chip variant="outlined" label={`Employee: ${employeeRoleCounts.employee || 0}`} />
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
                {(!sortedEmployees || sortedEmployees.length === 0) ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, color: 'text.secondary' }}>
                    <Avatar sx={{ bgcolor: '#e9eef9', color: '#2f74d0', width: 56, height: 56, mb: 2 }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ color: '#183152', fontWeight: 700, mb: 1 }}>No employees yet</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 420, textAlign: 'center' }}>Add employees using the "ADD EMPLOYEE" button. Employees will appear in this list once created.</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 1100 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Age</TableCell>
                        <TableCell>Gender</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Base Salary</TableCell>
                        <TableCell>Date Hired</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedEmployees.map(emp => {
                        const latest = findLatestPayrollFor(emp);
                        const base = latest ? (latest.baseSalary || latest.base_salary) : (emp.baseSalary || emp.base_salary_per_day || emp.base_salary || '');
                        const dateHired = emp.date_hired || emp.dateHired || emp.hired_at || emp.created_at || '';
                        const isTerminated = String(emp.status || '').toLowerCase() === 'terminated';
                        return (
                          <TableRow
                            key={emp.id || emp.userId || emp.employeeid || emp.username}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleOpenEmployeeDetail(emp)}
                          >
                            <TableCell>{String(emp.employeeid || emp.employee_id || emp.userId || '').padStart(8, '0')}</TableCell>
                            <TableCell>{formatNameHr(emp)}</TableCell>
                            <TableCell>{emp.age || ''}</TableCell>
                            <TableCell>{emp.gender || ''}</TableCell>
                            <TableCell>{emp.role || ''}</TableCell>
                            <TableCell>{emp.phone || emp.contact || ''}</TableCell>
                            <TableCell>{formatPeso(base, true)}</TableCell>
                            <TableCell>{dateHired ? (new Date(dateHired).toLocaleDateString()) : ''}</TableCell>
                            <TableCell>
                              {String(emp.status || 'active').toLowerCase() === 'active' ? (
                                <Chip size="small" color="success" label="Active" />
                              ) : (
                                <Chip size="small" color="default" label={String(emp.status || '').toUpperCase() || 'INACTIVE'} />
                              )}
                            </TableCell>
                            <TableCell>
                              {isTerminated ? (
                                <Typography variant="caption" color="text.secondary">Archived</Typography>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <IconButton size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditEmployee(emp); }}><EditIcon fontSize="small" /></IconButton>
                                  <IconButton size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget(emp); setDeleteDialogOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
                                  {(() => {
                                    const normalizedRole = String(emp.role || '').toLowerCase();
                                    const employeeId = String(emp.employeeid || emp.employee_id || '').trim();
                                    const canManageFacePhotos = ['employee', 'custodian', 'hr'].includes(normalizedRole) && employeeId !== '00000002';
                                    return canManageFacePhotos;
                                  })() && (
                                    <Button size="small" variant="outlined" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPhotoModal(emp); }}>
                                      UPLOAD PHOTOS
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </TableContainer>
                )}
              </Box>
            </Paper>
          )}

          {/* Attendance edit dialog for HR */}
          <Dialog open={attendanceEditOpen} onClose={() => { setAttendanceEditOpen(false); setAttendanceEditRow(null); }} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Attendance</DialogTitle>
            <DialogContent>
                {attendanceEditRow && (
                  <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimePicker
                        label="Time In"
                        ampm
                        value={parseTimeToDate(attendanceEditForm.log_time)}
                        onChange={(newValue) => setAttendanceEditForm(f => ({ ...f, log_time: timePickerValueTo24hString(newValue) || '' }))}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                      <TimePicker
                        label="Time Out"
                        ampm
                        value={parseTimeToDate(attendanceEditForm.time_out)}
                        onChange={(newValue) => setAttendanceEditForm(f => ({ ...f, time_out: timePickerValueTo24hString(newValue) || '' }))}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  <TextField
                    select
                    label="Status"
                    value={attendanceEditForm.status || 'present'}
                    onChange={(e) => setAttendanceEditForm((f) => applyAttendanceStatusToForm(f, e.target.value))}
                    fullWidth
                  >
                    <MenuItem value="present">Present</MenuItem>
                    <MenuItem value="late">Late</MenuItem>
                    <MenuItem value="leave">Leave</MenuItem>
                    <MenuItem value="absent">Absent</MenuItem>
                  </TextField>
                  <TextField label="Late Minutes" type="number" value={attendanceEditForm.late_minutes || 0} onChange={(e) => setAttendanceEditForm(f => ({ ...f, late_minutes: Number(e.target.value || 0) }))} fullWidth />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setAttendanceEditOpen(false); setAttendanceEditRow(null); }}>Cancel</Button>
              <Button variant="contained" onClick={async () => {
                if (!attendanceEditRow) return;
                try {
                  const payload = {
                    log_time: attendanceEditForm.log_time || null,
                    time_out: attendanceEditForm.time_out || null,
                    is_absent: !!attendanceEditForm.is_absent,
                    leave_approved: !!attendanceEditForm.leave_approved,
                    late_minutes: Number(attendanceEditForm.late_minutes || 0),
                  };
                  await axios.put(`${API_BASE}/api/attendance/${attendanceEditRow.id}`, payload, { withCredentials: true });
                  setSnack({ open: true, message: 'Attendance updated', severity: 'success' });
                  setAttendanceEditOpen(false); setAttendanceEditRow(null);
                  try { await fetchAttendance(filterMonth, filterYear); } catch (e) {}
                  try { await fetchPayroll(filterMonth, filterYear); } catch (e) {}
                  try {
                    if (selectedCutoffId && selectedCutoffStatus === 'Processing') {
                      await axios.post(`${API_BASE}/api/payroll/cutoffs/${selectedCutoffId}/generate`, {}, { withCredentials: true });
                    }
                  } catch (e) {
                    console.error('Failed recomputing selected cutoff after attendance edit', e);
                  }
                  try { if (selectedCutoffId) await fetchCutoffRecords(selectedCutoffId); } catch (e) {}
                  try {
                    if (cutoffAttendanceOpen && cutoffAttendanceContext) {
                      await fetchAttendanceRowsForRange(cutoffAttendanceContext);
                    }
                  } catch (e) {}
                } catch (e) {
                  console.error('Failed updating attendance', e);
                  setSnack({ open: true, message: e?.response?.data?.error || 'Failed to update attendance', severity: 'error' });
                }
              }}>Save</Button>
            </DialogActions>
          </Dialog>

          {/* Attendance detail modal (view-only, auto-refresh while open) */}
          <Dialog open={attendanceDetailOpen} onClose={() => { setAttendanceDetailOpen(false); setAttendanceDetailRow(null); }} maxWidth="sm" fullWidth>
            <DialogTitle>Attendance Details</DialogTitle>
            <DialogContent>
              {attendanceDetailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
              ) : (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {attendanceDetailRow ? (
                    <>
                      <Typography variant="subtitle1">{getAttendanceRowEmployeeName(attendanceDetailRow)}</Typography>
                      <Typography variant="body2">Employee ID: {getAttendanceRowEmployeeId(attendanceDetailRow)}</Typography>
                      <Typography variant="body2">Role: {getAttendanceRoleLabel(attendanceDetailRow)}</Typography>
                      <Typography variant="body2">Date: {attendanceDetailRow.log_date || attendanceDetailRow.date || ''}</Typography>
                      <Typography variant="body2">Time In: {formatTime(attendanceDetailRow.log_time || attendanceDetailRow.time_in || attendanceDetailRow.timeIn)}</Typography>
                      <Typography variant="body2">Time Out: {formatTime(attendanceDetailRow.time_out || attendanceDetailRow.timeOut)}</Typography>
                      <Typography variant="body2">Method: {getAttendanceMethod(attendanceDetailRow) || '-'}</Typography>
                      <Typography variant="body2">Status: {getAttendanceStatus(attendanceDetailRow) || '-'}</Typography>
                      <Typography variant="body2">Late Minutes: {attendanceDetailRow.late_minutes || attendanceDetailRow.lateMinutes || 0}</Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No details</Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setAttendanceDetailOpen(false); setAttendanceDetailRow(null); }}>Close</Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={cutoffAttendanceOpen}
            onClose={() => {
              setCutoffAttendanceOpen(false);
              setCutoffAttendanceRows([]);
              setCutoffAttendanceContext(null);
            }}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>{`Edit Attendance${cutoffAttendanceContext ? ` - ${cutoffAttendanceContext.employeeName}` : ''}`}</DialogTitle>
            <DialogContent>
              {cutoffAttendanceContext && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {`${cutoffAttendanceContext.employeeId} • ${cutoffAttendanceContext.employeeRole} • ${formatDatePH(cutoffAttendanceContext.periodStart)} - ${formatDatePH(cutoffAttendanceContext.periodEnd)}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Correct wrong absences, leave tags, or time-in/time-out for this cutoff before final payroll approval.
                  </Typography>
                </Box>
              )}
              {selectedCutoffStatus !== 'Processing' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Attendance edits from payroll are only allowed while this cutoff is in Processing status.
                </Alert>
              )}
              {cutoffAttendanceLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 980 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Time In</TableCell>
                        <TableCell>Time Out</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cutoffAttendanceRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3 }}>
                            No attendance records found in this cutoff period.
                          </TableCell>
                        </TableRow>
                      ) : cutoffAttendanceRows.map((a, idx) => (
                        <TableRow key={a.id || idx}>
                          <TableCell>{formatAttendanceDate(a)}</TableCell>
                          <TableCell>{formatTime(a.log_time || a.time_in || a.timeIn)}</TableCell>
                          <TableCell>{formatTime(a.time_out || a.timeOut || a.time_out_display)}</TableCell>
                          <TableCell>{renderAttendanceMethodChip(getAttendanceMethod(a))}</TableCell>
                          <TableCell>{renderAttendanceStatusChip(getAttendanceStatus(a))}</TableCell>
                          <TableCell align="right">{(() => {
                            const rd = toDateKey(a.log_date || a.date) || toDateKey(new Date());
                            const inStr = a.log_time || a.time_in || a.timeIn;
                            const outStr = a.time_out || a.timeOut;
                            if (inStr && outStr) {
                              try {
                                const tIn = new Date(`${rd}T${inStr}`);
                                const tOut = new Date(`${rd}T${outStr}`);
                                return Math.max(0, (tOut - tIn) / (1000 * 60 * 60)).toFixed(2);
                              } catch (e) {
                                return '0.00';
                              }
                            }
                            if (typeof a.hours_worked !== 'undefined') return Number(a.hours_worked || 0).toFixed(2);
                            if (typeof a.hours !== 'undefined') return Number(a.hours || 0).toFixed(2);
                            return '0.00';
                          })()}</TableCell>
                          <TableCell>
                            {normalizedRole === 'hr' && String(a?.manual_request_status || '').toLowerCase() !== 'pending' ? (
                              <Button
                                size="small"
                                disabled={selectedCutoffStatus !== 'Processing'}
                                onClick={() => {
                                  setAttendanceEditRow(a);
                                  setAttendanceEditForm({
                                    log_time: a.log_time || a.time_in || '',
                                    time_out: a.time_out || a.timeOut || '',
                                    status: getAttendanceEditStatusValue(a),
                                    is_absent: !!a.is_absent,
                                    leave_approved: !!a.leave_approved,
                                    late_minutes: a.late_minutes || a.lateMinutes || 0,
                                  });
                                  setAttendanceEditOpen(true);
                                }}
                              >
                                Edit
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setCutoffAttendanceOpen(false);
                setCutoffAttendanceRows([]);
                setCutoffAttendanceContext(null);
              }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Photo management modal removed per request */}
          {/* Photo management modal (Custodian only) */}
          <Dialog open={photoModalOpen} onClose={closePhotoModal} maxWidth="md" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {photoModalEmployee ? `${formatNameFull(photoModalEmployee)}` : 'Employee'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    ID: {photoModalEmployee ? modalEmployeeId : ''}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              {photoModalEmployee && (
                <PhotoUploader
                  employeeId={modalEmployeeId}
                  employeeName={formatNameFull(photoModalEmployee)}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closePhotoModal}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Payroll section */}
          {activeSection === 'payroll' && (
            <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 'auto', md: '100%' }, overflow: 'hidden', minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-start' }, flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>Payroll</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{payrollMode === 'current' ? 'Manage current payroll calculations.' : 'Archived payroll snapshots.'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
                  <Button variant={payrollMode === 'current' ? 'contained' : 'outlined'} size="small" onClick={() => setPayrollMode('current')}>Current</Button>
                  <Button variant={payrollMode === 'history' ? 'contained' : 'outlined'} size="small" onClick={() => { setPayrollMode('history'); setHistoryNotice(''); fetchPayrollHistoryYears(); fetchPayrollHistory(1); }}>History</Button>
                  {payrollMode === 'current' && (
                    <>
                      <Button variant="outlined" size="small" onClick={() => setShowRecomputeDialog(true)}>RECOMPUTE</Button>
                      <Button variant="text" size="small" onClick={() => fetchPayroll(filterMonth, filterYear)}>REFRESH</Button>
                      <Button variant="text" size="small" onClick={exportCSV} startIcon={<DownloadIcon />}>EXPORT CSV</Button>
                    </>
                  )}
                </Box>
              </Box>

              {payrollMode === 'current' && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'grid',
                    gap: 1.5,
                    alignItems: 'center',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr 1fr',
                      lg: '160px 120px minmax(260px, 1fr) auto',
                    },
                  }}
                >
                  <TextField select label="Month" value={filterMonth} onChange={(e) => setFilterMonth(Number(e.target.value || defaultReportPeriod.month))} size="small" sx={{ width: '100%' }}>
                    {getMonthOptionsForYear(filterYear).map((month) => <MenuItem key={month} value={month}>{APP_MONTH_NAMES[month - 1]}</MenuItem>)}
                  </TextField>
                  <TextField select label="Year" value={filterYear} onChange={(e) => {
                    const next = clampReportPeriod(filterMonth, e.target.value);
                    setFilterYear(next.year);
                    setFilterMonth(next.month);
                  }} size="small" sx={{ width: '100%' }}>
                    {reportYearOptions.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                  </TextField>
                  <TextField
                    label="Search employee"
                    placeholder="name, role, 10 or 00000010"
                    value={payrollSearchTerm}
                    onChange={(e) => setPayrollSearchTerm(e.target.value)}
                    size="small"
                    sx={{ width: '100%', minWidth: 0 }}
                  />
                  <Button variant="outlined" size="small" onClick={() => fetchPayroll(filterMonth, filterYear)} sx={{ width: { xs: '100%', lg: 'auto' } }}>APPLY</Button>
                </Box>
              )}

              {payrollMode === 'current' && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Payroll Cutoff Management</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Create a cutoff, generate payroll from attendance inside that range, and track its payout status.
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid xs={12} md={3}>
                      <TextField
                        label="Period Start"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={cutoffForm.period_start}
                        onChange={(e) => setCutoffForm((prev) => ({ ...prev, period_start: e.target.value }))}
                      />
                    </Grid>
                    <Grid xs={12} md={3}>
                      <TextField
                        label="Period End"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={cutoffForm.period_end}
                        onChange={(e) => setCutoffForm((prev) => ({ ...prev, period_end: e.target.value }))}
                      />
                    </Grid>
                    <Grid xs={12} md={4}>
                      <TextField
                        label="Pay Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={cutoffForm.pay_date}
                        onChange={(e) => setCutoffForm((prev) => ({ ...prev, pay_date: e.target.value }))}
                      />
                    </Grid>
                    <Grid xs={12} md={2}>
                      <Button variant="contained" fullWidth sx={{ height: '100%' }} onClick={createPayrollCutoff}>Add</Button>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(420px, 560px) 170px minmax(320px, auto)',
                      },
                      gap: 2,
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <FormControl fullWidth>
                        <InputLabel>Select Cutoff</InputLabel>
                        <Select
                          value={selectedCutoffId}
                          label="Select Cutoff"
                          onChange={(e) => setSelectedCutoffId(String(e.target.value || ''))}
                        >
                          {(visiblePayrollCutoffs || []).map((cutoff) => (
                            <MenuItem key={cutoff.id} value={String(cutoff.id)}>
                              {formatPayrollCutoffLabel(cutoff)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ width: { xs: '100%', md: 170 } }}>
                      <TextField
                        select
                        fullWidth
                        label="Cutoff Status"
                        value={cutoffStatusFilter}
                        onChange={(e) => setCutoffStatusFilter(String(e.target.value || 'all'))}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="Open">Open</MenuItem>
                        <MenuItem value="Processing">Processing</MenuItem>
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Released">Released</MenuItem>
                      </TextField>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button
                          variant="contained"
                          onClick={generateSelectedCutoffPayroll}
                          disabled={!canGenerateSelectedCutoff}
                        >
                          Generate Payroll
                        </Button>
                        <Button variant="outlined" onClick={() => updateSelectedCutoffStatus('Approved')} disabled={!canApproveSelectedCutoff}>Approve</Button>
                        <Button variant="text" onClick={fetchPayrollCutoffs}>Refresh Cutoffs</Button>
                      </Box>
                      {selectedCutoffId && selectedCutoffStatus === 'Open' && Number(selectedCutoff?.record_count || 0) > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                          This cutoff has been generated and will move to Processing automatically 7 days before the pay date.
                        </Typography>
                      )}
                      {selectedCutoffId && selectedCutoffStatus === 'Processing' && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                          This cutoff is ready for approval.
                        </Typography>
                      )}
                      {selectedCutoffId && selectedCutoffStatus === 'Approved' && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                          This cutoff is approved and will be released automatically on the pay date.
                        </Typography>
                      )}
                      {selectedCutoffId && selectedCutoffStatus === 'Released' && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'success.main' }}>
                          This cutoff has been released automatically on its pay date.
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ maxHeight: 260, overflow: 'auto', mb: 2 }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 720 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Cutoff</TableCell>
                          <TableCell>Pay Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Records</TableCell>
                          <TableCell align="right">Total Net</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payrollCutoffsLoading ? (
                          <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 2 }}>Loading cutoffs...</TableCell></TableRow>
                        ) : visiblePayrollCutoffs.length === 0 ? (
                          <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 2 }}>No payroll cutoffs yet.</TableCell></TableRow>
                        ) : visiblePayrollCutoffs.map((cutoff) => (
                          <TableRow
                            key={cutoff.id}
                            hover
                            selected={String(cutoff.id) === String(selectedCutoffId)}
                            onClick={() => setSelectedCutoffId(String(cutoff.id))}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{formatDatePH(cutoff.period_start)} - {formatDatePH(cutoff.period_end)}</TableCell>
                            <TableCell>{formatDatePH(cutoff.pay_date)}</TableCell>
                            <TableCell>{cutoff.status}</TableCell>
                            <TableCell align="right">{Number(cutoff.record_count || 0)}</TableCell>
                            <TableCell align="right">{formatPeso(cutoff.total_net_pay || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </TableContainer>
                  </Box>

                  {selectedCutoffId && (
                    <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Selected Cutoff Payroll Summary</Typography>
                      <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 1100 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee ID</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell align="right">Worked</TableCell>
                            <TableCell align="right">Absences</TableCell>
                            <TableCell align="right">Late Min</TableCell>
                            <TableCell align="right">OT Hrs</TableCell>
                            <TableCell align="right">Gross</TableCell>
                            <TableCell align="right">Deductions</TableCell>
                            <TableCell align="right">Net</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cutoffRecordsLoading ? (
                            <TableRow><TableCell colSpan={12} sx={{ textAlign: 'center', py: 2 }}>Loading cutoff payroll...</TableCell></TableRow>
                          ) : cutoffRecords.length === 0 ? (
                            <TableRow><TableCell colSpan={12} sx={{ textAlign: 'center', py: 2 }}>No generated payroll records for this cutoff.</TableCell></TableRow>
                          ) : cutoffRecords.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>{String(row.employeeid || row.employee_no || '').padStart(8, '0')}</TableCell>
                              <TableCell>{[row.firstname, row.lastname].filter(Boolean).join(' ') || row.employee_no || '-'}</TableCell>
                              <TableCell>{row.role || '-'}</TableCell>
                              <TableCell align="right">{Number(row.days_worked || 0)}</TableCell>
                              <TableCell align="right">{Number(row.absences || 0)}</TableCell>
                              <TableCell align="right">{Number(row.late_minutes || 0)}</TableCell>
                              <TableCell align="right">{Number(row.overtime_hours || 0)}</TableCell>
                              <TableCell align="right">{formatPeso(row.gross_pay || 0)}</TableCell>
                              <TableCell align="right">{formatPeso(row.total_deductions || 0)}</TableCell>
                              <TableCell align="right">{formatPeso(row.net_pay || 0)}</TableCell>
                              <TableCell>{row.status || '-'}</TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={selectedCutoffStatus !== 'Processing'}
                                  onClick={() => fetchAttendanceRowsForRange({
                                    employeeId: row.employeeid || row.employee_no,
                                    employeeName: [row.firstname, row.lastname].filter(Boolean).join(' ') || row.employee_no || '-',
                                    employeeRole: row.role || '-',
                                    periodStart: selectedCutoff?.period_start,
                                    periodEnd: selectedCutoff?.period_end,
                                  })}
                                >
                                  Edit Attendance
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Paper>
              )}

              {payrollMode === 'history' && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid xs={12} md={2}>
                      <TextField
                        label="Employee ID (optional)"
                        fullWidth
                        size="small"
                        value={historyFilters.employeeId}
                        onChange={(e) => setHistoryFilters(f => ({ ...f, employeeId: String(e.target.value || '').replace(/\D/g, '').slice(0, 8) }))}
                        helperText={historyFilters.employeeId ? 'Use 1-8 digits (e.g., 00000020).' : ' '}
                      />
                    </Grid>
                    <Grid xs={12} md={3}>
                      <TextField
                        select
                        label="History Month"
                        fullWidth
                        size="small"
                        value={historyFilters.month || ''}
                        onChange={(e) => setHistoryFilters(f => ({ ...f, month: String(e.target.value || '') }))}
                        helperText={historyFilters.month ? 'Showing the selected month only.' : 'Choose a month or leave this as All.'}
                      >
                        <MenuItem value="">All</MenuItem>
                        {getMonthOptionsForYear(historyFilters.year || defaultReportPeriod.year).map((month) => (
                          <MenuItem key={month} value={String(month)}>
                            {APP_MONTH_NAMES[month - 1]}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid xs={12} md={3}>
                      <TextField
                        select
                        label="History Year"
                        fullWidth
                        size="small"
                        value={historyFilters.year || ''}
                        onChange={(e) => setHistoryFilters(f => ({ ...f, year: String(e.target.value || '') }))}
                        helperText={historyFilters.year ? 'Showing the selected year only.' : 'Choose a year or leave this as All.'}
                      >
                        <MenuItem value="">All</MenuItem>
                        {(payrollHistoryYears.length ? payrollHistoryYears : reportYearOptions).filter((year) => reportYearOptions.includes(Number(year))).map((year) => (
                          <MenuItem key={year} value={String(year)}>{year}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid xs={12} md={2}>
                      <Button variant="contained" fullWidth sx={{ height: '100%' }} onClick={() => fetchPayrollHistory(1)}>APPLY</Button>
                    </Grid>
                    <Grid xs={12} md={2}>
                      <Button variant="text" color="secondary" fullWidth sx={{ height: '100%' }} onClick={() => { setHistoryFilters({ employeeId:'', month:'', year:'' }); setHistoryNotice(''); fetchPayrollHistory(1); }}>CLEAR</Button>
                    </Grid>
                  </Grid>
                  {historyNotice && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {historyNotice}
                    </Alert>
                  )}
                </Box>
              )}

              {false && payrollMode === 'current' && (
                <Box sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 300px)' }, overflow: 'auto' }}>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 1450 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Month/Year</TableCell>
                        <TableCell>Base Salary</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell align="right">Absences</TableCell>
                        <TableCell align="right">Half Days</TableCell>
                        <TableCell align="right">Holidays</TableCell>
                        <TableCell align="right">Overtime (hrs)</TableCell>
                        <TableCell>Gross</TableCell>
                        <TableCell>SSS</TableCell>
                        <TableCell>PhilHealth</TableCell>
                        <TableCell>Pag-IBIG</TableCell>
                        <TableCell align="right">Net Salary</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const visibleRows = getVisiblePayrollRows();
                        if (!visibleRows || visibleRows.length === 0) {
                          return (<TableRow><TableCell colSpan={15} sx={{ textAlign: 'center', py: 4 }}>No employees</TableCell></TableRow>);
                        }
                        return visibleRows.map((r, idx) => {
                          const key = String(r.employeeid || r.employee_id || r.userId || r.username || '');
                          const emp = (employees || []).find(e => String(e.employeeid || e.employee_id || e.userId || e.username || '') === key);
                          const displayId = emp ? (emp.employeeid || emp.employee_id || emp.userId || '') : (r.employeeid || r.employee_id || r.userId || '');
                          const displayName = emp ? formatNameHr(emp) : formatNameHr(r);
                          const displayBase = emp ? (emp.baseSalary || emp.base_salary_per_day || emp.base_salary || '') : (r.baseSalary || r.base_salary || '');
                          const displayRole = emp ? (emp.role || r.role) : (r.role || '');
                          return (
                            <TableRow key={r.id || `${r.userId || ''}-${idx}`}>
                              <TableCell>{String(displayId).padStart(8, '0')}</TableCell>
                              <TableCell>{displayName}</TableCell>
                              <TableCell>{(r.month || filterMonth) + '/' + (r.year || filterYear)}</TableCell>
                              <TableCell>{formatPeso(displayBase, true)}</TableCell>
                              <TableCell>{displayRole}</TableCell>
                              <TableCell align="right">{Number(r.absences || 0)}</TableCell>
                              <TableCell align="right">{Number(r.half_days || r.halfDays || 0)}</TableCell>
                              <TableCell align="right">{Number(r.holidays || 0)}</TableCell>
                              <TableCell align="right">{Number(r.overtimeHours || r.overtime_hours || 0)}</TableCell>
                              <TableCell>{formatPeso(getPayrollGrossDisplay(r))}</TableCell>
                              <TableCell>
                                {(() => {
                                  const sssNum = (emp && emp.sss_no) || r.sss_no || '';
                                  const sssAmt = (typeof r.deductionsSss !== 'undefined' ? r.deductionsSss : (r.deductions_sss ?? r.sss_amount ?? 0));
                                  if (!hasValidGovNumber(sssNum)) return formatPeso(0);
                                  return (
                                    <Box sx={{ display: 'inline-block', textAlign: 'center', px: 1, py: 0.5, borderRadius: 1, bgcolor: '#f5f5f5' }}>
                                      <Typography variant="caption" sx={{ display: 'block' }}>{formatPeso(sssAmt)}</Typography>
                                    </Box>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const philNum = (emp && emp.philhealth_no) || r.philhealth_no || '';
                                  const philAmt = (typeof r.deductionsPhilhealth !== 'undefined' ? r.deductionsPhilhealth : (r.deductions_philhealth ?? r.philhealth_amount ?? 0));
                                  if (!hasValidGovNumber(philNum)) return formatPeso(0);
                                  return (
                                    <Box sx={{ display: 'inline-block', textAlign: 'center', px: 1, py: 0.5, borderRadius: 1, bgcolor: '#f5f5f5' }}>
                                      <Typography variant="caption" sx={{ display: 'block' }}>{formatPeso(philAmt)}</Typography>
                                    </Box>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const pagNum = (emp && emp.pagibig_no) || r.pagibig_no || '';
                                  const pagAmt = (typeof r.deductionsPagibig !== 'undefined' ? r.deductionsPagibig : (r.deductions_pagibig ?? r.pagibig_amount ?? 0));
                                  if (!hasValidGovNumber(pagNum)) return formatPeso(0);
                                  return (
                                    <Box sx={{ display: 'inline-block', textAlign: 'center', px: 1, py: 0.5, borderRadius: 1, bgcolor: '#f5f5f5' }}>
                                      <Typography variant="caption" sx={{ display: 'block' }}>{formatPeso(pagAmt)}</Typography>
                                    </Box>
                                  );
                                })()}
                              </TableCell>
                              <TableCell align="right">{formatPeso(getPayrollNetDisplay(r, emp))}</TableCell>
                              <TableCell>
                                {r._placeholder && (
                                  <Chip label="placeholder" size="small" sx={{ mr: 1 }} />
                                )}
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); const empKey = String(r.employeeid || r.employee_id || r.userId || r.username || ''); const empObj = (employees || []).find(e => String(e.employeeid || e.employee_id || e.userId || e.username || '') === empKey); openSalaryHistory(empObj || r); }} title="Salary history"><HistoryIcon fontSize="small" /></IconButton>
                                {!r._placeholder && (
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingPayrollRow(r); setShowEditPayrollDialog(true); }}><EditIcon fontSize="small" /></IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                  </TableContainer>
                </Box>
              )}

              {payrollMode === 'history' && (
                <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                  {historyLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee ID</TableCell>
                          <TableCell>Month</TableCell>
                          <TableCell>Year</TableCell>
                          <TableCell>Base Salary</TableCell>
                          <TableCell>Net Salary</TableCell>
                          <TableCell>Absences</TableCell>
                          <TableCell>Total Hours</TableCell>
                          <TableCell>Overtime (hrs)</TableCell>
                          <TableCell>Archived At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyRows && historyRows.length > 0 ? historyRows.map((r, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{String(r.employee_no || r.userId || r.employee_id || '').padStart(8,'0')}</TableCell>
                            <TableCell>{r.month || ''}</TableCell>
                            <TableCell>{r.year || ''}</TableCell>
                            <TableCell>{formatPeso(r.base_salary || 0)}</TableCell>
                            <TableCell>{formatPeso(r.net_salary || 0)}</TableCell>
                            <TableCell>{Number(r.absences || 0)}</TableCell>
                            <TableCell>{Number(r.total_hours || 0)}</TableCell>
                            <TableCell>{Number(r.overtime_hours || 0)}</TableCell>
                            <TableCell>{(r.archived_at || r.date_archived || r.created_at) ? formatDateTime(r.archived_at || r.date_archived || r.created_at) : ''}</TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                              No archived payroll records.
                              <br />
                              <Typography variant="caption" color="text.secondary">
                                Generate and approve payroll in the CURRENT tab first, then history will appear here.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}

              {payrollMode === 'history' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="caption">Page {historyPage} of {historyPages}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" disabled={historyPage <= 1} onClick={() => { const p = Math.max(1, historyPage - 1); setHistoryPage(p); fetchPayrollHistory(p); }}>Prev</Button>
                    <Button size="small" disabled={historyPage >= historyPages} onClick={() => { const p = Math.min(historyPages, historyPage + 1); setHistoryPage(p); fetchPayrollHistory(p); }}>Next</Button>
                  </Box>
                </Box>
              )}

              {/* Payout dialog */}
              <Dialog open={showDisburseDialog} onClose={() => setShowDisburseDialog(false)}>
                <DialogTitle>Payout Salaries</DialogTitle>
                <DialogContent>
                  <Typography>Are you sure you want to payout payroll for all employees for the selected month? This will insert/update payroll records in the database.</Typography>
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel control={<input type="checkbox" checked={autoSendEmails} onChange={(e) => setAutoSendEmails(e.target.checked)} />} label="Send email notifications to employees" />
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Tip: you can toggle automatic emails via AUTO_SEND_PAYROLL environment variable.</Typography>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowDisburseDialog(false)}>CANCEL</Button>
                  <Button variant="contained" onClick={async () => {
                    try {
                      await axios.post(`${API_BASE}/api/payroll/disburse`, { month: filterMonth, year: filterYear, sendEmails: autoSendEmails }, { withCredentials: true });
                      setSnack({ open: true, message: 'Payroll payout completed', severity: 'success' });
                    } catch (e) { setSnack({ open: true, message: 'Failed to payout payroll', severity: 'error' }); }
                    setShowDisburseDialog(false);
                  }}>CONFIRM</Button>
                </DialogActions>
              </Dialog>
              {/* Salary history dialog */}
              <Dialog open={salaryHistoryOpen} onClose={() => { setSalaryHistoryOpen(false); setSalaryHistoryRows([]); setSalaryHistoryEmployee(null); }} maxWidth="sm" fullWidth>
                <DialogTitle>Salary History {salaryHistoryEmployee ? `for ${formatNameHr(salaryHistoryEmployee)}` : ''}</DialogTitle>
                <DialogContent>
                  {salaryHistoryLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
                  ) : (
                    <Box>
                      {(salaryHistoryRows && salaryHistoryRows.length > 0) ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Base Salary</TableCell>
                              <TableCell>Updated By</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {salaryHistoryRows.map(s => (
                              <TableRow key={s.id || (s.updated_at + s.base_salary)}>
                                <TableCell>{s.updated_at ? formatDateTime(s.updated_at) : ''}</TableCell>
                                <TableCell>{formatPeso(s.base_salary, true)}</TableCell>
                                <TableCell>{s.updated_by || s.updatedBy || ''}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No salary history available.</Typography>
                      )}
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setSalaryHistoryOpen(false); setSalaryHistoryRows([]); setSalaryHistoryEmployee(null); }}>Close</Button>
                </DialogActions>
              </Dialog>
              {/* Edit employee dialog (HR) */}
              <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditForm(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { minWidth: 480, maxWidth: 620 } }}>
                <DialogTitle>Edit Employee</DialogTitle>
                <DialogContent>
                  {editForm && (
                    <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
                      <TextField label="Firstname *" value={editForm.firstname || ''} onChange={(e) => setEditForm(f => ({ ...f, firstname: toTitleName(filterLetters(e.target.value)) }))} onKeyDown={lettersKeyDown} fullWidth />
                      <TextField label="Middlename" value={editForm.middlename || ''} onChange={(e) => setEditForm(f => ({ ...f, middlename: toTitleName(filterLetters(e.target.value)) }))} onKeyDown={lettersKeyDown} fullWidth />
                      <TextField label="Lastname *" value={editForm.lastname || ''} onChange={(e) => setEditForm(f => ({ ...f, lastname: toTitleName(filterLetters(e.target.value)) }))} onKeyDown={lettersKeyDown} fullWidth />
                      <Grid container spacing={2}>
                        <Grid xs={12} md={4}>
                          <TextField label="Birthdate" type="date" value={editForm.birthdate || ''} onChange={(e) => handleEditBirthdateChange(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                        </Grid>
                        <Grid xs={12} md={4}>
                          <TextField label="Age" value={editForm.age || ''} disabled fullWidth />
                        </Grid>
                        <Grid xs={12} md={4}>
                          <TextField label="Employee ID" value={String(editForm.employeeid || editForm.userId || editForm.username || '').padStart(8,'0')} disabled fullWidth />
                        </Grid>
                      </Grid>

                      <TextField label="Address *" value={editForm.address || ''} onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))} fullWidth />
                      <TextField label="Phone Number *" value={editForm.phone || ''} onChange={(e) => setEditForm(f => ({ ...f, phone: filterPhone(e.target.value) }))} onKeyDown={(e) => phoneKeyDown(e, editForm.phone)} fullWidth inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 11 }} />
                      <TextField label="Email *" value={editForm.email || ''} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} fullWidth />
                      <TextField label="Password" type="password" value={editForm.password || ''} onChange={(e) => setEditForm(f => ({ ...f, password: e.target.value }))} fullWidth helperText="Leave blank to keep current password. Minimum 8 characters if changed." inputProps={{ autoComplete: 'new-password' }} />

                      <Grid container spacing={2}>
                        <Grid xs={12} md={4}>
                          <TextField label="SSS Number" value={editForm.sss_no || ''} onChange={(e) => setEditForm(f => ({ ...f, sss_no: formatSssNumber(e.target.value) }))} onKeyDown={digitsKeyDown} fullWidth placeholder="12-3456789-0" error={!!getGovNumberError('sss_no', editForm.sss_no)} helperText={getGovNumberError('sss_no', editForm.sss_no) || 'Format: 12-3456789-0'} inputProps={{ inputMode: 'numeric', maxLength: 12 }} />
                        </Grid>
                        <Grid xs={12} md={4}>
                          <TextField label="PhilHealth Number" value={editForm.philhealth_no || ''} onChange={(e) => setEditForm(f => ({ ...f, philhealth_no: formatGov12Number(e.target.value) }))} onKeyDown={digitsKeyDown} fullWidth placeholder="1234-5678-9012" error={!!getGovNumberError('philhealth_no', editForm.philhealth_no)} helperText={getGovNumberError('philhealth_no', editForm.philhealth_no) || 'Format: 1234-5678-9012'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} />
                        </Grid>
                        <Grid xs={12} md={4}>
                          <TextField label="Pag-IBIG Number" value={editForm.pagibig_no || ''} onChange={(e) => setEditForm(f => ({ ...f, pagibig_no: formatGov12Number(e.target.value) }))} onKeyDown={digitsKeyDown} fullWidth placeholder="1234-5678-9012" error={!!getGovNumberError('pagibig_no', editForm.pagibig_no)} helperText={getGovNumberError('pagibig_no', editForm.pagibig_no) || 'Format: 1234-5678-9012'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} />
                        </Grid>
                      </Grid>

                      <FormControl fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select value={editForm.role || ''} label="Role" onChange={(e) => setEditForm(f => ({ ...f, role: e.target.value }))}>
                          <MenuItem value="HR">HR</MenuItem>
                          <MenuItem value="Employee">Employee</MenuItem>
                          {/* Removed Admin role (no such role in system) */}
                          <MenuItem value="Custodian">Custodian</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setEditOpen(false); setEditForm(null); }}>CANCEL</Button>
                  <Button variant="contained" onClick={async () => {
                    if (!editForm) return;
                    try {
                      const editErrors = [
                        getGovNumberError('sss_no', editForm.sss_no),
                        getGovNumberError('philhealth_no', editForm.philhealth_no),
                        getGovNumberError('pagibig_no', editForm.pagibig_no),
                        editForm.password && String(editForm.password).length < 8 ? 'Password must be at least 8 characters' : '',
                      ].filter(Boolean);
                      if (editErrors.length > 0) {
                        setSnack({ open: true, message: editErrors[0], severity: 'error' });
                        return;
                      }
                      // prepare payload similarly to AccountsPage
                      const payload = {};
                      ['firstname','middlename','lastname','email','phone','employeeid','role','birthdate','age','sss_no','philhealth_no','pagibig_no','salaryType','baseSalary','password'].forEach(k => {
                        if (Object.prototype.hasOwnProperty.call(editForm, k) && typeof editForm[k] !== 'undefined' && editForm[k] !== null) {
                          if (k === 'password' && editForm[k] === '') return;
                          payload[k === 'baseSalary' ? 'baseSalary' : k] = editForm[k];
                        }
                      });
                      if (payload.baseSalary !== undefined) payload.baseSalary = payload.baseSalary === '' ? null : Number(payload.baseSalary);
                      if (Object.prototype.hasOwnProperty.call(payload, 'sss_no')) payload.sss_no = normalizeGovNumberForPayload('sss_no', payload.sss_no);
                      if (Object.prototype.hasOwnProperty.call(payload, 'philhealth_no')) payload.philhealth_no = normalizeGovNumberForPayload('philhealth_no', payload.philhealth_no);
                      if (Object.prototype.hasOwnProperty.call(payload, 'pagibig_no')) payload.pagibig_no = normalizeGovNumberForPayload('pagibig_no', payload.pagibig_no);
                      const idParam = editForm.id || editForm.userId || editForm.employeeid || editForm.username;
                      await axios.put(`${API_BASE}/api/employees/${idParam}`, payload, { withCredentials: true });
                      setSnack({ open: true, message: 'Employee updated', severity: 'success' });
                      setEditOpen(false); setEditForm(null);
                      fetchEmployees();
                      try { fetchPayroll(filterMonth, filterYear); } catch (e) {}
                    } catch (e) {
                      console.error('Update failed', e);
                      const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to update';
                      if (e?.response?.data?.validationErrors) {
                        // show first validation error in snack
                        const ve = e.response.data.validationErrors;
                        const firstKey = Object.keys(ve)[0];
                        setSnack({ open: true, message: ve[firstKey], severity: 'error' });
                      } else {
                        setSnack({ open: true, message: msg, severity: 'error' });
                      }
                    }
                  }}>UPDATE EMPLOYEE</Button>
                </DialogActions>
              </Dialog>
              

              {/* Recompute dialog */}
              <Dialog open={showRecomputeDialog} onClose={() => setShowRecomputeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Recompute Selected Cutoff</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {!selectedCutoff ? (
                      <Alert severity="info">Select a payroll cutoff first. Recompute now works on the selected cutoff only.</Alert>
                    ) : (
                      <>
                        <Alert severity={canRecomputeSelectedCutoff ? 'warning' : 'info'}>
                          Recompute updates this cutoff payroll from the latest attendance, leave, and payroll inputs. Approved and released cutoffs stay locked.
                        </Alert>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                            gap: 1.5,
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            bgcolor: 'background.paper',
                          }}
                        >
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Cutoff Period</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatDatePH(selectedCutoff.period_start)} - {formatDatePH(selectedCutoff.period_end)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Pay Date</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatDatePH(selectedCutoff.pay_date)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Current Status</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {selectedCutoffStatus}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Generated Records</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Number(selectedCutoff.record_count || 0)}
                            </Typography>
                          </Box>
                        </Box>
                        {selectedCutoffEnded && ['Approved', 'Released'].includes(selectedCutoffStatus) && (
                          <Alert severity="info">This cutoff is already finalized. Recompute is disabled to protect posted payroll.</Alert>
                        )}
                        {['Open', 'Processing'].includes(selectedCutoffStatus) && (
                          <Alert severity="warning">Use recompute only after attendance, leave, or salary data changed for this cutoff.</Alert>
                        )}
                      </>
                    )}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowRecomputeDialog(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    disabled={!canRecomputeSelectedCutoff}
                    onClick={async () => {
                      const ok = await recomputeSelectedCutoffPayroll();
                      if (ok) setShowRecomputeDialog(false);
                    }}
                  >
                    RECOMPUTE SELECTED CUTOFF
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Edit Payroll modal (UI only) */}
              <Dialog open={showEditPayrollDialog} onClose={() => { setShowEditPayrollDialog(false); setEditingPayrollRow(null); }} maxWidth="md" fullWidth>
                <DialogTitle>Edit Payroll for {editingPayrollRow ? formatNameHr(editingPayrollRow) : ''}</DialogTitle>
                <DialogContent>
                  {/* Minimal edit form to match screenshot layout (fields read-only or editable as needed) */}
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid xs={12} md={3}><TextField label="Month" fullWidth value={payrollEditForm.month} disabled /></Grid>
                    <Grid xs={12} md={3}><TextField label="Year" fullWidth value={payrollEditForm.year} disabled /></Grid>
                    <Grid xs={12} md={6}><TextField label="Role" fullWidth value={payrollEditForm.role} disabled /></Grid>
                    <Grid xs={12} md={4}><TextField label="Base Salary" fullWidth value={payrollEditForm.baseSalary} disabled /></Grid>
                    <Grid xs={12} md={4}><TextField label="Overtime Hours" fullWidth value={payrollEditForm.overtimeHours} onChange={(e) => setPayrollEditForm(f => ({ ...f, overtimeHours: Number(e.target.value || 0) }))} /></Grid>
                    <Grid xs={12} md={4}><TextField label="Absences (days) *" fullWidth value={payrollEditForm.absences} onChange={(e) => setPayrollEditForm(f => ({ ...f, absences: Number(e.target.value || 0) }))} /></Grid>
                    <Grid xs={12} md={4}><TextField label="Holidays *" fullWidth value={payrollEditForm.holidays} onChange={(e) => setPayrollEditForm(f => ({ ...f, holidays: Number(e.target.value || 0) }))} /></Grid>
                    <Grid xs={12} md={4}><TextField label="Gross *" fullWidth value={payrollEditForm.bonuses} onChange={(e) => setPayrollEditForm(f => ({ ...f, bonuses: Number(e.target.value || 0) }))} /></Grid>
                    <Grid xs={12} md={4}><FormControl fullWidth><InputLabel>Salary Type</InputLabel><Select value={payrollEditForm.salaryType} label="Salary Type" onChange={(e) => setPayrollEditForm(f => ({ ...f, salaryType: e.target.value }))}><MenuItem value="Monthly">Monthly</MenuItem><MenuItem value="Daily">Daily</MenuItem><MenuItem value="Hourly">Hourly</MenuItem></Select></FormControl></Grid>
                    <Grid xs={12}>
                      <Grid container spacing={2}>
                        <Grid xs={12} md={4}>
                          <TextField label="SSS Number" fullWidth value={editingPayrollRow?.sss_no || editingPayrollRow?.employee_sss || editingPayrollRow?.sss || ''} disabled />
                        </Grid>
                        <Grid xs={12} md={4}>
                          <TextField label="PhilHealth Number" fullWidth value={editingPayrollRow?.philhealth_no || editingPayrollRow?.employee_philhealth || editingPayrollRow?.philhealth || ''} disabled />
                        </Grid>
                        <Grid xs={12} md={4}>
                          <TextField label="Pag-IBIG Number" fullWidth value={editingPayrollRow?.pagibig_no || editingPayrollRow?.employee_pagibig || editingPayrollRow?.pagibig || ''} disabled />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setShowEditPayrollDialog(false); setEditingPayrollRow(null); }}>CANCEL</Button>
                      <Button variant="contained" onClick={async () => {
                    // save changes by sending only editable fields; keep read-only fields as-is from the existing record
                    try {
                      if (!editingPayrollRow) throw new Error('No row');
                      // server expects camelCase keys for payroll update payload (baseSalary, overtimeHours, salaryType, sssDeduction, etc.)
                      const payload = {
                        month: editingPayrollRow.month || payrollEditForm.month,
                        year: editingPayrollRow.year || payrollEditForm.year,
                        role: editingPayrollRow.role || payrollEditForm.role,
                        baseSalary: (editingPayrollRow.baseSalary || editingPayrollRow.base_salary || payrollEditForm.baseSalary),
                        overtimeHours: Number(payrollEditForm.overtimeHours || 0),
                        absences: Number(payrollEditForm.absences || 0),
                        holidays: Number(payrollEditForm.holidays || 0),
                        bonuses: Number(payrollEditForm.bonuses || 0),
                        salaryType: payrollEditForm.salaryType,
                        sssDeduction: !!(editingPayrollRow.sss_deduct || editingPayrollRow.sss_deduction),
                        philhealthDeduction: !!(editingPayrollRow.philhealth_deduct || editingPayrollRow.philhealth_deduction),
                        pagibigDeduction: !!(editingPayrollRow.pagibig_deduct || editingPayrollRow.pagibig_deduction),
                        // include canonical ID values so server can persist them if needed
                        sss_no: editingPayrollRow?.sss_no || editingPayrollRow?.employee_sss || null,
                        philhealth_no: editingPayrollRow?.philhealth_no || editingPayrollRow?.employee_philhealth || null,
                        pagibig_no: editingPayrollRow?.pagibig_no || editingPayrollRow?.employee_pagibig || null,
                      };
                      await axios.put(`${API_BASE}/api/payroll/${editingPayrollRow.id || editingPayrollRow.payrollId}`, payload, { withCredentials: true });
                      setSnack({ open: true, message: 'Payroll updated', severity: 'success' });
                      fetchPayroll(payrollEditForm.month, payrollEditForm.year);
                    } catch (e) { console.error(e); setSnack({ open: true, message: 'Failed to update payroll', severity: 'error' }); }
                    setShowEditPayrollDialog(false); setEditingPayrollRow(null);
                  }}>UPDATE</Button>
                </DialogActions>
              </Dialog>
            </Paper>
          )}


            {activeSection === 'reports' && (
              <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 'auto', md: '100%' }, overflow: 'hidden', minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, mb: 2 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Reports</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Click any employee row to open the transcript.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      alignItems: 'center',
                      width: { xs: '100%', md: 'auto' },
                      gridTemplateColumns: {
                        xs: '1fr 1fr',
                        sm: 'repeat(6, minmax(0, 1fr))',
                        lg: '170px 140px 140px 120px auto auto',
                      },
                    }}
                  >
                    <TextField
                      select
                      label="Attendance Period"
                      value={reportCutoffId}
                      onChange={(e) => setReportCutoffId(String(e.target.value || ''))}
                      size="small"
                      sx={{ width: '100%' }}
                    >
                      <MenuItem value="">Monthly View</MenuItem>
                      {(payrollCutoffs || []).map((cutoff) => (
                        <MenuItem key={cutoff.id} value={String(cutoff.id)}>
                          {formatPayrollCutoffLabel(cutoff, { includeStatus: false })}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField select label="Month" value={filterMonth} onChange={(e) => setFilterMonth(Number(e.target.value || defaultReportPeriod.month))} size="small" sx={{ width: '100%' }}>
                      {getAvailableReportMonths(filterYear).map((month) => (
                        <MenuItem key={month} value={month}>{APP_MONTH_NAMES[month - 1]}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Role"
                      value={reportRoleFilter}
                      onChange={(e) => setReportRoleFilter(String(e.target.value || 'all').toLowerCase())}
                      size="small"
                      sx={{ width: '100%' }}
                    >
                      <MenuItem value="all">All Roles</MenuItem>
                      <MenuItem value="hr">HR</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                      <MenuItem value="custodian">Custodian</MenuItem>
                    </TextField>
                    <TextField select label="Year" value={filterYear} onChange={(e) => {
                      const next = clampReportPeriod(filterMonth, e.target.value);
                      setFilterYear(next.year);
                      setFilterMonth(next.month);
                    }} size="small" sx={{ width: '100%' }}>
                      {reportYearOptions.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                    </TextField>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        fetchGeneralMonthlyReport(filterMonth, filterYear);
                        if (selectedTranscriptEmployeeId) fetchIndividualTranscript(selectedTranscriptEmployeeId, filterMonth, filterYear, reportCutoffId, selectedTranscriptUserId);
                      }}
                      sx={{ width: '100%' }}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => printReportNode('.general-report-print', 'Attendance and Schedule Report')}
                      sx={{ width: '100%' }}
                    >
                      Print Report
                    </Button>
                  </Box>
                </Box>

                <Box className="general-report-print" sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 420px)' }, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, bgcolor: '#fff', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Daily Attendance and Schedule Monitoring Report</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {new Date(filterYear, filterMonth - 1, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' })} • {reportRoleFilter === 'all' ? 'showing all roles' : `showing ${reportRoleFilter}`} • click a row to open the individual transcript
                  </Typography>
                  {generalMonthlyReportLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                  ) : generalMonthlyReportError ? (
                    <Alert severity="error">{generalMonthlyReportError}</Alert>
                  ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table
                        size="small"
                        sx={{
                          minWidth: 860,
                          '& th': { whiteSpace: 'nowrap', px: 1, py: 0.75, fontSize: '0.78rem' },
                          '& td': { px: 1, py: 0.75, fontSize: '0.8rem', verticalAlign: 'top' },
                        }}
                      >
                        <TableHead>
                            <TableRow>
                              <TableCell sx={{ minWidth: 88 }}>Employee ID</TableCell>
                              <TableCell sx={{ minWidth: 170 }}>Name</TableCell>
                              <TableCell sx={{ minWidth: 86 }}>Role</TableCell>
                              <TableCell align="right">Present</TableCell>
                              <TableCell align="right">Absent</TableCell>
                              <TableCell align="right">Half Day</TableCell>
                              <TableCell align="right">Late</TableCell>
                              <TableCell align="right">Leave</TableCell>
                              <TableCell align="right">Worked Days</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredMonthlyReportRows.length > 0 ? filteredMonthlyReportRows.map((row) => {
                            const isSelectedTranscript = String(selectedTranscriptEmployeeId || '') === String(row.employeeid || '');
                            return (
                              <TableRow
                                key={`${row.user_id}-${row.employeeid}`}
                                hover
                                selected={isSelectedTranscript}
                                onClick={() => openTranscriptFromReportRow(row)}
                                sx={{ cursor: 'pointer' }}
                              >
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.employeeid}</TableCell>
                                <TableCell>{row.employee_name}</TableCell>
                                <TableCell sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{row.role}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.present_days}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.absent_days}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.half_days || 0}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.late_days}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.leave_days}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.worked_days}</TableCell>
                              </TableRow>
                            );
                          }) : (
                            <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>No attendance report rows found.</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>

                <Box ref={reportTranscriptRef} className="individual-transcript-print" sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 260px)' }, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, bgcolor: '#fff' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>Individual Transcript</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {individualTranscriptReport?.employee?.employee_name
                          ? `${individualTranscriptReport.employee.employee_name} (${individualTranscriptReport.employee.employeeid})`
                          : 'Choose an employee by clicking a row in the monthly report above.'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button variant="outlined" onClick={clearSelectedTranscript} disabled={!selectedTranscriptEmployeeId}>
                        Clear
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => printReportNode('.individual-transcript-print', 'Individual Transcript')}
                        disabled={!individualTranscriptReport}
                      >
                        Print Transcript
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => openReportEmailDialog('individual_transcript')}
                        disabled={!individualTranscriptReport}
                      >
                        Send to Gmail
                      </Button>
                    </Box>
                  </Box>

                  {individualTranscriptLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                  ) : individualTranscriptError ? (
                    <Alert severity="error">{individualTranscriptError}</Alert>
                  ) : !individualTranscriptReport ? (
                    <Alert severity="info">Click an employee row above to load the transcript.</Alert>
                  ) : (
                    <>
                      <Box className="transcript-header" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' }, mb: 2, p: 2.5, border: '1px solid #dbe5f0', borderRadius: 3, bgcolor: '#f8fbff' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="overline" sx={{ letterSpacing: 1.4, color: '#475569' }}>EZRA General Services</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Attendance and Schedule Transcript</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
                            {selectedReportCutoff
                              ? `Showing the selected attendance period inside ${new Date(2000, filterMonth - 1, 1).toLocaleString('en-US', { month: 'long' })} ${filterYear}.`
                              : `Showing all attendance periods that start in ${new Date(2000, filterMonth - 1, 1).toLocaleString('en-US', { month: 'long' })} ${filterYear}.`}
                          </Typography>
                        </Box>
                        <Box className="transcript-header-meta" sx={{ minWidth: { xs: '100%', md: 220 }, textAlign: { xs: 'left', md: 'right' } }}>
                          <Typography variant="body2"><strong>Generated:</strong> {formatDateTime(new Date())}</Typography>
                          <Typography variant="body2"><strong>Employee ID:</strong> {individualTranscriptReport.employee?.employeeid || '-'}</Typography>
                          <Typography variant="body2"><strong>Employee Name:</strong> {individualTranscriptReport.employee?.employee_name || '-'}</Typography>
                          <Typography variant="body2"><strong>Role:</strong> {cleanText(individualTranscriptReport.employee?.role, '-')}</Typography>
                          <Typography variant="body2"><strong>Month:</strong> {new Date(2000, (individualTranscriptReport?.month || filterMonth) - 1, 1).toLocaleString('en-US', { month: 'long' })} {individualTranscriptReport?.year || filterYear}</Typography>
                        </Box>
                      </Box>

                      <TableContainer className="transcript-cutoff-summary" sx={{ overflowX: 'auto', mb: 2 }}>
                        <Table size="small" sx={{ minWidth: 760 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Transcript Part</TableCell>
                              <TableCell>Start Date</TableCell>
                              <TableCell>End Date</TableCell>
                              <TableCell align="right">Present</TableCell>
                              <TableCell align="right">Half Day</TableCell>
                              <TableCell align="right">Absent</TableCell>
                              <TableCell align="right">Worked Days</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getTranscriptSections(individualTranscriptReport).map((section, sectionIndex) => (
                              <TableRow key={`summary-${section.cutoff_id || 'monthly'}-${sectionIndex}`}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {section.cutoff_label || `${new Date(2000, (section.month || filterMonth) - 1, 1).toLocaleString('en-US', { month: 'long' })} ${section.year || filterYear}`}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{section.period_start ? formatDatePH(section.period_start) : '-'}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{section.period_end ? formatDatePH(section.period_end) : '-'}</TableCell>
                                <TableCell align="right">{section.summary?.present_days || 0}</TableCell>
                                <TableCell align="right">{section.summary?.half_days || 0}</TableCell>
                                <TableCell align="right">{section.summary?.absent_days || 0}</TableCell>
                                <TableCell align="right">{section.summary?.worked_days || 0}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {getTranscriptSections(individualTranscriptReport).map((section, sectionIndex) => (
                        <Box key={`${section.cutoff_id || 'monthly'}-${sectionIndex}`} className="transcript-section" sx={{ mb: 2, border: '1px solid #dbe5f0', borderRadius: 3, p: 2 }}>
                          <Typography className="transcript-section-title" variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            {section.cutoff_label || `${new Date(2000, (section.month || filterMonth) - 1, 1).toLocaleString('en-US', { month: 'long' })} ${section.year || filterYear}`}
                          </Typography>

                          <TableContainer sx={{ display: 'none' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Start Date</TableCell>
                                  <TableCell>End Date</TableCell>
                                  <TableCell>Pay Date</TableCell>
                                  <TableCell align="right">Base Salary</TableCell>
                                  <TableCell align="right">Gross</TableCell>
                                  <TableCell align="right">SSS</TableCell>
                                  <TableCell align="right">PhilHealth</TableCell>
                                  <TableCell align="right">Pag-IBIG</TableCell>
                                  <TableCell align="right">Late Deduction</TableCell>
                                  <TableCell align="right">Total Deductions</TableCell>
                                  <TableCell align="right">Net Salary</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{section.period_start ? formatDatePH(section.period_start) : '-'}</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{section.period_end ? formatDatePH(section.period_end) : '-'}</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{section.salary?.pay_date ? formatDatePH(section.salary.pay_date) : 'Not released yet'}</TableCell>
                                  <TableCell align="right">{formatPeso(section.salary?.base_salary)}</TableCell>
                                  <TableCell align="right">{formatPeso(section.salary?.gross_pay)}</TableCell>
                                  <TableCell align="right">{formatPeso(section.salary?.deductions?.sss)}</TableCell>
                                  <TableCell align="right">{formatPeso(section.salary?.deductions?.philhealth)}</TableCell>
                                  <TableCell align="right">{formatPeso(section.salary?.deductions?.pagibig)}</TableCell>
                                  <TableCell align="right">{formatPeso(section.salary?.deductions?.late)}</TableCell>
                                  <TableCell align="right">{formatPeso(section.salary?.deductions?.total)}</TableCell>
                                  <TableCell align="right">{formatPeso(section.salary?.net_salary)}</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>

                          <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 950 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Time In</TableCell>
                                  <TableCell>Time Out</TableCell>
                                  <TableCell>Break Start</TableCell>
                                  <TableCell>Break End</TableCell>
                                  <TableCell>Method</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell align="right">Late Min</TableCell>
                                  <TableCell align="right">Hours</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Array.isArray(section.rows) && section.rows.length > 0 ? section.rows.map((row) => (
                                  <TableRow key={`${section.cutoff_id || sectionIndex}-${row.date}-${row.id || row.time_in || 'entry'}`}>
                                    <TableCell>{formatDateWithWeekday(row.date)}</TableCell>
                                    <TableCell>{formatTime(row.time_in)}</TableCell>
                                    <TableCell>{formatTime(row.time_out)}</TableCell>
                                    <TableCell>{formatTime(row.break_start)}</TableCell>
                                    <TableCell>{formatTime(row.break_end)}</TableCell>
                                    <TableCell>{row.method || '-'}</TableCell>
                                    <TableCell>{renderAttendanceStatusChip(row.status || '-')}</TableCell>
                                    <TableCell align="right">{row.late_minutes || 0}</TableCell>
                                    <TableCell align="right">{Number(row.hours_worked || 0).toFixed(2)}</TableCell>
                                  </TableRow>
                                )) : (
                                  <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>No transcript rows found for this cutoff.</TableCell></TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
              </Paper>
            )}

            {/* Attendance section */}
            {activeSection === 'attendance' && (
              <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 'auto', md: '100%' }, overflow: 'hidden', minWidth: 0 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>Attendance Tracking</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Monitor employee attendance and working hours.</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    alignItems: 'center',
                    width: '100%',
                    mb: 2,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))',
                      xl: 'auto 140px 120px 180px 170px 170px minmax(220px, 1fr) auto',
                    },
                  }}
                >
                    <Button variant="contained" size="small" onClick={() => { setAttendancePersonalOnly(false); setSelectedDay('all'); setAttendanceEmployeeFilter(''); setAttendanceStatusFilter('all'); setAttendanceMethodFilter('all'); }} sx={{ width: '100%' }}>
                      All Records
                    </Button>
                    <TextField select label="Month" value={filterMonth} onChange={(e) => setFilterMonth(Number(e.target.value || defaultReportPeriod.month))} size="small" sx={{ width: '100%' }} SelectProps={{ native: false }}>
                      {getMonthOptionsForYear(filterYear).map((month) => (
                        <MenuItem key={month} value={month}>{APP_MONTH_NAMES[month - 1]}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select label="Year" value={filterYear} onChange={(e) => {
                      const next = clampReportPeriod(filterMonth, e.target.value);
                      setFilterYear(next.year);
                      setFilterMonth(next.month);
                    }} size="small" sx={{ width: '100%' }}>
                      {reportYearOptions.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                    </TextField>
                    <TextField select label="Day" value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)} size="small" sx={{ width: '100%' }} SelectProps={{ native: false }}>
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="all">All days</MenuItem>
                      {uniqueDays.map((dayLabel) => (
                        <MenuItem key={dayLabel} value={dayLabel}>{dayLabel}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select label="Status" value={attendanceStatusFilter} onChange={(e) => setAttendanceStatusFilter(e.target.value)} size="small" sx={{ width: '100%' }} SelectProps={{ native: false }}>
                      <MenuItem value="all">All status</MenuItem>
                      <MenuItem value="present">Present</MenuItem>
                      <MenuItem value="absent">Absent</MenuItem>
                      <MenuItem value="leave">Any Leave</MenuItem>
                      <MenuItem value="pending_leave">Pending Leave</MenuItem>
                      <MenuItem value="paid_leave">Paid Leave</MenuItem>
                      <MenuItem value="unpaid_leave">Unpaid Leave</MenuItem>
                      <MenuItem value="half_day">Half Day</MenuItem>
                    </TextField>
                    <TextField select label="Method" value={attendanceMethodFilter} onChange={(e) => setAttendanceMethodFilter(e.target.value)} size="small" sx={{ width: '100%' }} SelectProps={{ native: false }}>
                      <MenuItem value="all">All methods</MenuItem>
                      <MenuItem value="face">Face</MenuItem>
                      <MenuItem value="manual">Manual</MenuItem>
                      <MenuItem value="leave">Leave Request</MenuItem>
                    </TextField>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={attendanceEmployeeOptions}
                      inputValue={attendanceEmployeeFilter}
                      onInputChange={(_, value) => setAttendanceEmployeeFilter(value || '')}
                      onChange={(_, value) => setAttendanceEmployeeFilter(typeof value === 'string' ? value : (value || ''))}
                      sx={{ width: '100%' }}
                      renderInput={(params) => (
                        <TextField {...params} label="Employee ID" placeholder="10 or 00000010" />
                      )}
                    />
                    <Button variant="outlined" onClick={() => fetchAttendance(filterMonth, filterYear)} sx={{ width: '100%' }}>REFRESH</Button>
                </Box>

                <Box sx={{ maxHeight: { xs: 'none', md: 'calc(100vh - 220px)' }, overflow: 'auto' }}>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee Name</TableCell>
                      <TableCell>Employee ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time In</TableCell>
                      <TableCell>Time Out</TableCell>
                      <TableCell>Break Start</TableCell>
                      <TableCell>Break End</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Hours Worked</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                      {(filteredAttendanceRows && filteredAttendanceRows.length > 0) ? filteredAttendanceRows.map((a, idx) => (
                      <TableRow key={a.id || idx} hover onClick={() => { setAttendanceDetailRow(a); setAttendanceDetailOpen(true); }}>
                        <TableCell>{getAttendanceRowEmployeeName(a)}</TableCell>
                        <TableCell>{getAttendanceRowEmployeeId(a)}</TableCell>
                        <TableCell>{formatAttendanceDate(a)}</TableCell>
                        <TableCell>{formatTime(a.log_time || a.time_in || a.timeIn)}</TableCell>
                        <TableCell>{formatTime(a.time_out || a.timeOut || a.time_out_display)}</TableCell>
                        <TableCell>{formatTime(a.break_start)}</TableCell>
                        <TableCell>{formatTime(a.break_end)}</TableCell>
                        <TableCell>{renderAttendanceMethodChip(getAttendanceMethod(a))}</TableCell>
                        <TableCell>{renderAttendanceStatusChip(getAttendanceStatus(a))}</TableCell>
                        <TableCell align="right">{(() => {
                          const rd = toDateKey(a.log_date || a.date) || toDateKey(new Date());
                          const isHoliday = !!(holidaysByDateRef.current && holidaysByDateRef.current[rd]);
                          if (isHoliday) return '0';
                          const inStr = a.log_time || a.time_in || a.timeIn;
                          const outStr = a.time_out || a.timeOut;
                          if (inStr && outStr) {
                            try {
                              const tIn = new Date(`${rd}T${inStr}`);
                              const tOut = new Date(`${rd}T${outStr}`);
                              const hrs = Math.max(0, (tOut - tIn) / (1000 * 60 * 60));
                              return hrs.toFixed(2);
                            } catch (e) { return '0'; }
                          }
                          // fallback to server-provided hours fields if present
                          if (typeof a.hours_worked !== 'undefined') return String(a.hours_worked);
                          if (typeof a.hours === 'number') return String(a.hours);
                          return '0';
                        })()}</TableCell>
                        <TableCell>
                          {normalizedRole === 'hr' && String(a?.manual_request_status || '').toLowerCase() !== 'pending' ? (
                            <Button size="small" onClick={(e) => { e.stopPropagation(); setAttendanceEditRow(a); setAttendanceEditForm({ log_time: a.log_time || a.time_in || '', time_out: a.time_out || a.timeOut || '', status: getAttendanceEditStatusValue(a), is_absent: !!a.is_absent, leave_approved: !!a.leave_approved, late_minutes: a.late_minutes || a.lateMinutes || 0 }); setAttendanceEditOpen(true); }}>Edit</Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>No attendance records</TableCell></TableRow>
                    )}
                  </TableBody>
                  </Table>
                  </TableContainer>
                </Box>
              </Paper>
            )}

            {activeSection === 'manual-requests' && (
              <Paper sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>Manual Attendance Requests</Typography>
                  <Button variant="outlined" onClick={fetchManualRequests}>Refresh</Button>
                </Box>
                {manualRequestsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
                ) : (
                  <>
                    {manualRequestsError && (
                      <Typography color="error" sx={{ mb: 2 }}>{manualRequestsError}</Typography>
                    )}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee No</TableCell>
                          <TableCell>Employee Name</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {manualRequests.map((r) => (
                          <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => openManualRequestDetail(r)}>
                            <TableCell>{formatManualEmployeeNo(r.employee_no)}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                sx={{ p: 0, minWidth: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                                onClick={(e) => { e.stopPropagation(); openManualRequestDetail(r); }}
                              >
                                {resolveManualEmployeeName(r)}
                              </Button>
                            </TableCell>
                            <TableCell>{formatManualDate(r.requested_date || r.log_date)}</TableCell>
                            <TableCell>{formatManualTime(r.requested_time || r.log_time)}</TableCell>
                            <TableCell>{String(r.reason || '').trim() || '-'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); approveManualRequest(r); }}>Approve</Button>
                                <Button size="small" variant="outlined" color="error" onClick={(e) => { e.stopPropagation(); openRejectDialog(r); }}>Reject</Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        {manualRequests.length === 0 && !manualRequestsError && (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Typography variant="body2">No pending requests.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </>
                )}
              </Paper>
            )}

            {activeSection === 'dtr-photos' && (
              <Paper sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Attendance Evidence / DTR Photos</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Uploaded attendance evidence submitted by employees.
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={fetchDtrEvidence}>Refresh</Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 180px 140px auto' }, gap: 1.5, alignItems: 'center', mb: 2 }}>
                  <TextField
                    label="Employee ID"
                    size="small"
                    placeholder="00000010"
                    value={dtrEvidenceFilters.employeeId}
                    onChange={(e) => setDtrEvidenceFilters((prev) => ({ ...prev, employeeId: e.target.value }))}
                  />
                  <TextField
                    select
                    label="Month"
                    size="small"
                    value={dtrEvidenceFilters.month}
                    onChange={(e) => setDtrEvidenceFilters((prev) => ({ ...prev, month: e.target.value }))}
                  >
                    {getMonthOptionsForYear(dtrEvidenceFilters.year).map((month) => (
                      <MenuItem key={month} value={String(month)}>
                        {APP_MONTH_NAMES[month - 1]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Year"
                    size="small"
                    value={dtrEvidenceFilters.year}
                    onChange={(e) => {
                      const next = clampReportPeriod(dtrEvidenceFilters.month, e.target.value);
                      setDtrEvidenceFilters((prev) => ({ ...prev, month: String(next.month), year: String(next.year) }));
                    }}
                  >
                    {reportYearOptions.map((year) => <MenuItem key={year} value={String(year)}>{year}</MenuItem>)}
                  </TextField>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={fetchDtrEvidence}>Apply</Button>
                    <Button
                      variant="outlined"
                      onClick={() => setDtrEvidenceFilters({ employeeId: '', month: String(defaultReportPeriod.month), year: String(defaultReportPeriod.year) })}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
                {dtrEvidenceLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
                ) : dtrEvidenceError ? (
                  <Alert severity="error">{dtrEvidenceError}</Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)', overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 130 }}>Photo</TableCell>
                          <TableCell>Custodian</TableCell>
                          <TableCell>Employee ID</TableCell>
                          <TableCell>Uploaded At</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell>Employee Review</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dtrEvidenceRows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Typography variant="body2">No attendance evidence uploaded yet.</Typography>
                            </TableCell>
                          </TableRow>
                        ) : dtrEvidenceRows.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>
                              {row.file_url ? (
                                <Box
                                  component="img"
                                  src={row.file_url}
                                  alt="Attendance evidence"
                                  sx={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid #e2e8f0' }}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell>{formatNameHr(row) || row.username || '-'}</TableCell>
                            <TableCell>{String(row.employeeid || '').padStart(8, '0')}</TableCell>
                            <TableCell>{formatDateTimePH(row.captured_at)}</TableCell>
                            <TableCell>{row.notes || '-'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'grid', gap: 0.5 }}>
                                {renderDtrEmployeeReviewChip(row.employee_review_status)}
                                {row.employee_reviewer_name && (
                                  <Typography variant="caption" color="text.secondary">By: {row.employee_reviewer_name}</Typography>
                                )}
                                {row.employee_review_notes && (
                                  <Typography variant="caption" color="text.secondary">{row.employee_review_notes}</Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {row.file_url ? (
                                <Button size="small" variant="outlined" component="a" href={row.file_url} target="_blank" rel="noreferrer">
                                  View Photo
                                </Button>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            )}

            {/* Face Recognition (webcam) section */}
            {normalizedRole === 'custodian' && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Face Recognition</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Capture a frame and let the system recognize your face. Toggle mirror and resolution as needed.
                </Typography>
                {/* Lazy import to avoid bundle blow-up if not used */}
                {(() => {
                  try {
                    const WebcamCapture = require('./components/WebcamCapture').default;
                    return <WebcamCapture />;
                  } catch (e) {
                    return <Typography variant="body2" color="error">Webcam module unavailable.</Typography>;
                  }
                })()}
              </Paper>
            )}

            {/* Inventory section */}
            {/* Applicants section */}
            {activeSection === 'deployments' && (
              <Paper sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Schedule Assignment</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Assign employees to work sites with schedule details.</Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => { fetchDeployments(); fetchBranches(); fetchEmployees(); }}>REFRESH</Button>
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid xs={12} md={3} sx={{ minWidth: { xs: '100%', md: 320 } }}>
                    <FormControl fullWidth size="small" sx={{ minWidth: { xs: '100%', md: 280 } }}>
                      <InputLabel>Custodian *</InputLabel>
                      <Select
                        label="Custodian *"
                        value={deploymentForm.user_id}
                        onChange={(e) => setDeploymentForm((f) => ({ ...f, user_id: e.target.value }))}
                        renderValue={(value) => {
                          const picked = (employees || []).find((e) => String(e.id || e.userId) === String(value));
                          if (!picked) return '';
                          return `${String(picked.employeeid || picked.employee_no || '').padStart(8, '0')} - ${formatNameHr(picked)}`;
                        }}
                      >
                        {(employees || [])
                          .filter((e) => String(e.role || '').toLowerCase() === 'custodian')
                          .map((e) => {
                            const optionUserId = Number(e.id || e.userId || 0);
                            const activeDeployment = unavailableDeploymentByUserId[optionUserId];
                            const hasActiveDeployment = !!activeDeployment;
                            const activeLabel = hasActiveDeployment
                              ? ` (${getDeploymentStatusLabel(activeDeployment)} deployment)`
                              : '';
                            return (
                              <MenuItem key={e.id || e.userId} value={String(e.id || e.userId)} disabled={hasActiveDeployment}>
                                {`${String(e.employeeid || e.employee_no || '').padStart(8, '0')} - ${formatNameHr(e)}${activeLabel}`}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                    {deploymentSelectionConflict?.message && (
                      <Typography variant="caption" color="error.main">
                        {deploymentSelectionConflict.message}
                      </Typography>
                    )}
                  </Grid>
                  <Grid xs={12} md={3} sx={{ minWidth: { xs: '100%', md: 320 } }}>
                    <FormControl fullWidth size="small" sx={{ minWidth: { xs: '100%', md: 280 } }}>
                      <InputLabel>Work Site *</InputLabel>
                      <Select
                        label="Work Site *"
                        value={deploymentForm.branch_id}
                        onChange={(e) => setDeploymentForm((f) => ({ ...f, branch_id: e.target.value }))}
                        renderValue={(value) => {
                          const picked = (branches || []).find((b) => String(b.id) === String(value));
                          if (!picked) return '';
                          const isActive = Number(picked.is_active) !== 0;
                          return `${picked.branch_code || ''} - ${picked.branch_name || ''}${isActive ? '' : ' (Inactive)'}`;
                        }}
                      >
                        {(branches || []).map((b) => {
                          const isActive = Number(b.is_active) !== 0;
                          return (
                            <MenuItem key={b.id} value={String(b.id)} disabled={!isActive}>
                              {`${b.branch_code || ''} - ${b.branch_name || ''}${isActive ? '' : ' (Inactive)'}`}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    {((branches || []).length > 0 && (branches || []).every((b) => Number(b.is_active) === 0)) && (
                      <Typography variant="caption" color="warning.main">
                        No active work sites available. Activate a work site first in Work Site Scheduling.
                      </Typography>
                    )}
                  </Grid>
                  <Grid xs={12} md={2}>
                    <TextField
                      label="Start Date *"
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: toDateKey(new Date()) }}
                      value={deploymentForm.start_date}
                      onChange={(e) => setDeploymentForm((f) => ({ ...f, start_date: e.target.value }))}
                    />
                  </Grid>
                  <Grid xs={12} md={2}>
                    <TextField
                      label="End Date"
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: deploymentForm.start_date || toDateKey(new Date()) }}
                      value={deploymentForm.end_date}
                      onChange={(e) => setDeploymentForm((f) => ({ ...f, end_date: e.target.value }))}
                    />
                  </Grid>
                  <Grid xs={12} md={2}>
                    <TextField
                      label="Shift Start"
                      type="time"
                      fullWidth
                      required
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={deploymentForm.shift_start}
                      onChange={(e) => setDeploymentForm((f) => ({ ...f, shift_start: e.target.value }))}
                    />
                  </Grid>
                  <Grid xs={12} md={2}>
                    <TextField
                      label="Shift End"
                      type="time"
                      fullWidth
                      required
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={deploymentForm.shift_end}
                      onChange={(e) => setDeploymentForm((f) => ({ ...f, shift_end: e.target.value }))}
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <TextField
                      label="Notes"
                      fullWidth
                      size="small"
                      value={deploymentForm.notes}
                      onChange={(e) => setDeploymentForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </Grid>
                  <Grid xs={12} md={2}>
                    <Button variant="contained" fullWidth sx={{ height: '100%' }} onClick={saveDeployment}>ASSIGN</Button>
                  </Grid>
                  <Grid xs={12} md={2}>
                    <Button variant="text" fullWidth sx={{ height: '100%' }} onClick={resetDeploymentForm}>CLEAR</Button>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '160px 180px 140px 1fr' },
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <TextField
                    select
                    label="Status"
                    size="small"
                    value={deploymentStatusFilter}
                    onChange={(e) => setDeploymentStatusFilter(e.target.value)}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="upcoming">Upcoming</MenuItem>
                    <MenuItem value="ended">Ended</MenuItem>
                    <MenuItem value="all">All</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label="Month"
                    size="small"
                    value={deploymentFilterMonth}
                    onChange={(e) => {
                      const next = clampReportPeriod(e.target.value, deploymentFilterYear);
                      setDeploymentFilterMonth(String(next.month));
                      setDeploymentFilterYear(String(next.year));
                    }}
                  >
                    {getMonthOptionsForYear(deploymentFilterYear).map((month) => (
                      <MenuItem key={month} value={String(month)}>{APP_MONTH_NAMES[month - 1]}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Year"
                    size="small"
                    value={deploymentFilterYear}
                    onChange={(e) => {
                      const next = clampReportPeriod(deploymentFilterMonth, e.target.value);
                      setDeploymentFilterMonth(String(next.month));
                      setDeploymentFilterYear(String(next.year));
                    }}
                  >
                    {reportYearOptions.map((year) => <MenuItem key={year} value={String(year)}>{year}</MenuItem>)}
                  </TextField>
                  <TextField
                    label="Search deployment branch/custodian"
                    placeholder="e.g. 01, PSG, BNK-PSG-01, Pasig"
                    fullWidth
                    size="small"
                    value={deploymentSearchTerm}
                    onChange={(e) => setDeploymentSearchTerm(e.target.value)}
                  />
                </Box>

                <Box sx={{ maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' }}>
                  {deploymentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Custodian</TableCell>
                          <TableCell>Work Site</TableCell>
                          <TableCell>Shift</TableCell>
                          <TableCell>Start</TableCell>
                          <TableCell>End</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredDeployments.length === 0 ? (
                          <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>No deployment records found</TableCell></TableRow>
                        ) : filteredDeployments.map((d) => {
                          const status = getDeploymentStatusLabel(d);
                          const displayName = formatNameHr(d) || d.username || '-';
                          const empNo = String(d.employeeid || d.user_employee_no || d.employee_no || '').padStart(8, '0');
                          const branch = resolveBranchForDeployment(d);
                          const branchCode = d.branch_code || branch?.branch_code || '';
                          const branchName = d.branch_name || branch?.branch_name || '-';
                          const branchLabel = branchCode ? `${branchCode} - ${branchName}` : branchName;
                          return (
                            <TableRow key={d.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDeploymentEmployeeDetail(d)}>
                              <TableCell>
                                <Button
                                  size="small"
                                  sx={{ p: 0, minWidth: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                                  onClick={(e) => { e.stopPropagation(); openDeploymentEmployeeDetail(d); }}
                                >
                                  {`${empNo} - ${displayName}`}
                                </Button>
                              </TableCell>
                              <TableCell>{branchLabel}</TableCell>
                              <TableCell>{d.shift_start ? `${d.shift_start}${d.shift_end ? ` - ${d.shift_end}` : ''}` : '-'}</TableCell>
                              <TableCell>{d.start_date ? formatDateOnly(d.start_date) : '-'}</TableCell>
                              <TableCell>{d.end_date ? formatDateOnly(d.end_date) : '-'}</TableCell>
                              <TableCell>{status}</TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={status === 'Ended'}
                                  onClick={(e) => { e.stopPropagation(); endDeployment(d.id); }}
                                >
                                  END NOW
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              </Paper>
            )}

            {activeSection === 'branches' && (
              <Paper sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Work Site Scheduling</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Create, edit, and deactivate work site records.</Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => fetchBranches()}>REFRESH</Button>
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid xs={12} md={2}>
                    <TextField
                      label="Work Site Code *"
                      fullWidth
                      size="small"
                      value={branchForm.branch_code || ''}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        minWidth: 150,
                        '& .MuiInputBase-input': {
                          color: '#000',
                          WebkitTextFillColor: '#000',
                          cursor: 'default',
                        },
                      }}
                      helperText=" "
                    />
                  </Grid>
                  <Grid xs={12} md={3}>
                    <TextField
                      label="Work Site Name *"
                      fullWidth
                      size="small"
                      value={branchForm.branch_name}
                      onChange={(e) => setBranchForm((f) => ({ ...f, branch_name: filterLetters(e.target.value) }))}
                    />
                  </Grid>
                  <Grid xs={12} md={3}>
                    <TextField
                      label="Address"
                      fullWidth
                      size="small"
                      value={branchForm.address}
                      onChange={(e) => setBranchForm((f) => ({ ...f, address: e.target.value }))}
                    />
                  </Grid>
                  <Grid xs={12} md={2} sx={{ minWidth: { xs: '100%', md: 170 } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Province</InputLabel>
                      <Select
                        label="Province"
                        value={branchForm.province}
                        onChange={(e) => setBranchForm((f) => ({ ...f, province: String(e.target.value || '') }))}
                      >
                        {Object.keys(PROVINCE_CITY_OPTIONS).map((province) => (
                          <MenuItem key={province} value={province}>{province}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={3}>
                    <TextField
                      select
                      label="City"
                      fullWidth
                      size="small"
                      value={branchForm.city || ''}
                      onChange={(e) => setBranchForm((f) => ({ ...f, city: String(e.target.value || '') }))}
                      disabled={!branchForm.province}
                      sx={{ minWidth: 170 }}
                    >
                      {availableCities.map((city) => (
                        <MenuItem key={city} value={city}>{city}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid xs={12} md={1}>
                    <TextField
                      label="Minimum"
                      type="number"
                      fullWidth
                      size="small"
                      value={branchForm.min_headcount}
                      onChange={(e) => setBranchForm((f) => ({ ...f, min_headcount: e.target.value }))}
                      error={!!branchHeadcountValidation.minError || !!branchHeadcountValidation.rangeError}
                      helperText={branchHeadcountValidation.minError || ' '}
                      inputProps={{ min: BRANCH_HEADCOUNT_MIN, max: BRANCH_HEADCOUNT_MAX, step: 1 }}
                    />
                  </Grid>
                  <Grid xs={12} md={1}>
                    <TextField
                      label="Maximum"
                      type="number"
                      fullWidth
                      size="small"
                      value={branchForm.max_headcount}
                      onChange={(e) => setBranchForm((f) => ({ ...f, max_headcount: e.target.value }))}
                      error={!!branchHeadcountValidation.maxError || !!branchHeadcountValidation.rangeError}
                      helperText={branchHeadcountValidation.maxError || ' '}
                      inputProps={{ min: BRANCH_HEADCOUNT_MIN, max: BRANCH_HEADCOUNT_MAX, step: 1 }}
                    />
                  </Grid>
                  <Grid xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={branchForm.is_active ? 'active' : 'inactive'}
                        onChange={(e) => setBranchForm((f) => ({ ...f, is_active: e.target.value === 'active' }))}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{ height: '100%' }}
                      onClick={saveBranch}
                      disabled={!branchHeadcountValidation.isValid}
                    >
                      {editingBranchId ? 'UPDATE' : 'ADD'}
                    </Button>
                  </Grid>
                  <Grid xs={12} md={2}>
                    <Button variant="text" fullWidth sx={{ height: '100%' }} onClick={resetBranchForm}>
                      CLEAR
                    </Button>
                  </Grid>
                  {editingBranchId ? (
                    <Grid xs={12} md={2}>
                      <Button variant="outlined" color="error" fullWidth sx={{ height: '100%' }} onClick={resetBranchForm}>
                        CANCEL EDIT
                      </Button>
                    </Grid>
                  ) : null}
                  {branchHeadcountValidation.rangeError ? (
                    <Grid xs={12}>
                      <FormHelperText error sx={{ mt: -1 }}>
                        {branchHeadcountValidation.rangeError}
                      </FormHelperText>
                    </Grid>
                  ) : null}
                </Grid>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid xs={12} md={2} sx={{ minWidth: { xs: '100%', md: 180 } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={branchStatusFilter}
                        onChange={(e) => setBranchStatusFilter(String(e.target.value || 'active'))}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="all">All</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={3} sx={{ minWidth: { xs: '100%', md: 220 } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Filter Province</InputLabel>
                      <Select
                        label="Filter Province"
                        value={branchFilterProvince}
                        onChange={(e) => {
                          const nextProvince = String(e.target.value || '');
                          setBranchFilterProvince(nextProvince);
                          setBranchFilterCity('');
                        }}
                      >
                        <MenuItem value="">All provinces</MenuItem>
                        {branchFilterProvinceOptions.map((province) => (
                          <MenuItem key={province} value={province}>{province}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={3} sx={{ minWidth: { xs: '100%', md: 220 } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Filter City</InputLabel>
                      <Select
                        label="Filter City"
                        value={branchFilterCity}
                        onChange={(e) => setBranchFilterCity(String(e.target.value || ''))}
                        disabled={!branchFilterProvince && branchFilterCityOptions.length === 0}
                      >
                        <MenuItem value="">All cities</MenuItem>
                        {branchFilterCityOptions.map((city) => (
                          <MenuItem key={city} value={city}>{city}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} md={2}>
                    <Button
                      variant="text"
                      fullWidth
                      sx={{ height: '100%' }}
                      onClick={() => {
                        setBranchStatusFilter('active');
                        setBranchFilterProvince('');
                        setBranchFilterCity('');
                      }}
                    >
                      CLEAR FILTERS
                    </Button>
                  </Grid>
                </Grid>

                <Box sx={{ maxHeight: 'calc(100vh - 310px)', overflowY: 'auto' }}>
                  {branchesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Code</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Address</TableCell>
                          <TableCell>City</TableCell>
                          <TableCell>Province</TableCell>
                          <TableCell>Assigned</TableCell>
                          <TableCell>Minimum</TableCell>
                          <TableCell>Maximum</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleBranches.length === 0 ? (
                          <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>No branches found</TableCell></TableRow>
                        ) : (
                          visibleBranches.map((b) => (
                            <TableRow
                              key={b.id}
                              hover
                              onClick={() => openBranchDeploymentDialog(b)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>{b.branch_code}</TableCell>
                              <TableCell>{b.branch_name}</TableCell>
                              <TableCell>{b.address || '-'}</TableCell>
                              <TableCell>{b.city || '-'}</TableCell>
                              <TableCell>{b.province || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={`${Number(b.active_assigned || 0)} / ${Number(b.max_headcount || 0)}`}
                                  color={Number(b.active_assigned || 0) > Number(b.max_headcount || 0) ? 'error' : 'default'}
                                  variant={Number(b.active_assigned || 0) > Number(b.max_headcount || 0) ? 'filled' : 'outlined'}
                                  sx={{ fontWeight: 700 }}
                                />
                              </TableCell>
                              <TableCell>{Number(b.min_headcount || 3)}</TableCell>
                              <TableCell>{Number(b.max_headcount || 5)}</TableCell>
                              <TableCell>{Number(b.is_active) === 0 ? 'Inactive' : 'Active'}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); editBranch(b); }}>EDIT</Button>
                                  {Number(b.is_active) === 0 ? (
                                    <Button size="small" variant="outlined" color="success" onClick={(e) => { e.stopPropagation(); toggleBranchActive(b); }}>ACTIVATE</Button>
                                  ) : (
                                    <Button size="small" variant="outlined" color="error" onClick={(e) => { e.stopPropagation(); toggleBranchActive(b); }}>DEACTIVATE</Button>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              </Paper>
            )}

            <Dialog
              open={branchDeploymentDialog.open}
              onClose={() => setBranchDeploymentDialog({ open: false, branch: null })}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                {branchDeploymentDialog.branch
                  ? `${branchDeploymentDialog.branch.branch_code} - ${branchDeploymentDialog.branch.branch_name}`
                  : 'Work Site Assignments'}
              </DialogTitle>
              <DialogContent dividers>
                {branchDeploymentDialog.branch ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip size="small" label={`Assigned: ${Number(branchDeploymentDialog.branch.active_assigned || 0)}`} />
                    <Chip size="small" label={`Minimum: ${Number(branchDeploymentDialog.branch.min_headcount || 0)}`} />
                    <Chip
                      size="small"
                      label={`Maximum: ${Number(branchDeploymentDialog.branch.max_headcount || 0)}`}
                      color={Number(branchDeploymentDialog.branch.active_assigned || 0) > Number(branchDeploymentDialog.branch.max_headcount || 0) ? 'error' : 'default'}
                    />
                    <Chip size="small" label={Number(branchDeploymentDialog.branch.is_active) === 0 ? 'Inactive' : 'Active'} />
                  </Box>
                ) : null}
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Custodian</TableCell>
                        <TableCell>Employee ID</TableCell>
                        <TableCell>Shift</TableCell>
                        <TableCell>Start</TableCell>
                        <TableCell>End</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedBranchDeploymentRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                            No deployments found for this site.
                          </TableCell>
                        </TableRow>
                      ) : (
                        selectedBranchDeploymentRows.map((row) => (
                          <TableRow key={row.id} hover onClick={() => openDeploymentEmployeeDetail(row)} sx={{ cursor: 'pointer' }}>
                            <TableCell>{formatNameFull(row) || row.username || '-'}</TableCell>
                            <TableCell>{String(row.employeeid || row.user_employee_no || row.employee_no || '').padStart(8, '0')}</TableCell>
                            <TableCell>{row.shift_start && row.shift_end ? `${row.shift_start} - ${row.shift_end}` : '-'}</TableCell>
                            <TableCell>{row.start_date ? formatDateOnly(row.start_date) : '-'}</TableCell>
                            <TableCell>{row.end_date ? formatDateOnly(row.end_date) : '-'}</TableCell>
                            <TableCell>{getDeploymentStatusLabel(row)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setBranchDeploymentDialog({ open: false, branch: null })}>Close</Button>
              </DialogActions>
            </Dialog>

            {activeSection === 'applicants' && (
              <Paper sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Applicants</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button
                      variant={applicantViewMode === 'active' ? 'contained' : 'outlined'}
                      onClick={() => setApplicantViewMode('active')}
                    >
                      ACTIVE QUEUE ({applicantQueueCounts.active})
                    </Button>
                    <Button
                      variant={applicantViewMode === 'history' ? 'contained' : 'outlined'}
                      onClick={() => setApplicantViewMode('history')}
                    >
                      HISTORY ({applicantQueueCounts.history})
                    </Button>
                    <Button variant="outlined" onClick={() => fetchApplicants()}>REFRESH</Button>
                  </Box>
                </Box>
                <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Role Applied</TableCell>
                        <TableCell>Date Applied</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Resume</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedApplicants && sortedApplicants.length > 0 ? sortedApplicants.map(app => (
                        <TableRow key={app.id || app.applicant_id}>
                          <TableCell>{formatName(app)}</TableCell>
                          <TableCell>{app.role_applied || app.role || ''}</TableCell>
                          <TableCell>{app.date_applied ? formatDateTime(app.date_applied) : (app.created_at ? formatDateTime(app.created_at) : (app.applied_at ? formatDateTime(app.applied_at) : ''))}</TableCell>
                          <TableCell>{app.contact || app.phone || app.email || ''}</TableCell>
                          <TableCell>
                            {app.resumeFilePath ? (
                              <Box sx={{ display: 'grid', gap: 0.5 }}>
                                <Chip label="Attached" color="success" size="small" sx={{ width: 'fit-content', fontWeight: 700 }} />
                                <Button
                                  size="small"
                                  variant="text"
                                  sx={{ px: 0, justifyContent: 'flex-start', minWidth: 0 }}
                                  component="a"
                                  href={buildServerFileUrl(app.resumeFilePath)}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {app.resumeFileName || 'View Resume'}
                                </Button>
                              </Box>
                            ) : (
                              <Chip label="Not attached" size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const status = (app.status || 'pending').toLowerCase();
                              if (status === 'accepted') {
                                return (
                                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                                    <Chip label="accepted" color="success" size="small" sx={{ fontWeight: 700, textTransform: 'capitalize', width: 'fit-content' }} />
                                    {app.interview_email_sent_at && (
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Interview email sent {formatDateTime(app.interview_email_sent_at)}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              }
                              if (status === 'rejected') {
                                return (
                                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                                    <Chip label="rejected" color="error" size="small" sx={{ fontWeight: 700, textTransform: 'capitalize', width: 'fit-content' }} />
                                    {app.reject_reason && (
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {app.reject_reason}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              }
                              return (
                                <Box sx={{ display: 'grid', gap: 0.75 }}>
                                  <Chip label="pending" color="warning" size="small" sx={{ fontWeight: 700, textTransform: 'capitalize', width: 'fit-content' }} />
                                  {app.interview_datetime && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      Interview: {formatDateTime(app.interview_datetime)}
                                      {app.interview_location ? ` · ${app.interview_location}` : ''}
                                    </Typography>
                                  )}
                                  {app.interview_email_sent_at && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      Email sent: {formatDateTime(app.interview_email_sent_at)}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); openInterviewDialog(app); }}>INTERVIEW EMAIL</Button>
                              {app.resumeFilePath && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  component="a"
                                  href={buildServerFileUrl(app.resumeFilePath)}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  VIEW RESUME
                                </Button>
                              )}
                              {(app.status || 'pending').toLowerCase() === 'pending' && (
                                <>
                                  <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); openAddFromApplicant(app); }}>ACCEPT</Button>
                                  <Button size="small" variant="outlined" color="error" onClick={async (e) => {
                                    try {
                                      e && e.stopPropagation && e.stopPropagation();
                                      await axios.post(`${API_BASE}/api/applicants/${app.id || app.applicant_id}/reject`, {}, { withCredentials: true });
                                      setSnack({ open: true, message: 'Applicant rejected', severity: 'info' });
                                      try { await fetchApplicants(); } catch (e) {}
                                      try { window.dispatchEvent(new Event('applicantsChanged')); } catch (e) {}
                                    } catch (e) {
                                      console.error('Reject failed', e);
                                      setSnack({ open: true, message: 'Failed to reject applicant', severity: 'error' });
                                    }
                                  }}>REJECT</Button>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                            {applicantViewMode === 'history' ? 'No archived applicants yet' : 'No active applications'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  </TableContainer>
                </Box>
              </Paper>
            )}
            {activeSection === 'inventory' && (
              <Paper sx={{ p: 3, height: '100%', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Inventory Requests</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<HistoryIcon />} onClick={openInventoryHistory}>HISTORY</Button>
                    <Button variant="contained" onClick={() => setInventoryReportOpen(true)}>VIEW REPORT</Button>
                    <Button variant="outlined" onClick={() => { fetchInventoryItems(); fetchInventoryRequests(); fetchInventoryAnalytics(); }}>REFRESH</Button>
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 140px auto' }, gap: 1.5, alignItems: 'center', mb: 2 }}>
                  <TextField
                    select
                    label="Report Month"
                    size="small"
                    value={inventoryReportMonth}
                    onChange={(e) => setInventoryReportMonth(String(e.target.value || defaultReportPeriod.month))}
                  >
                    {getMonthOptionsForYear(inventoryReportYear).map((month) => (
                      <MenuItem key={month} value={String(month)}>
                        {APP_MONTH_NAMES[month - 1]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Year"
                    size="small"
                    value={inventoryReportYear}
                    onChange={(e) => {
                      const next = clampReportPeriod(inventoryReportMonth, e.target.value);
                      setInventoryReportMonth(String(next.month));
                      setInventoryReportYear(String(next.year));
                    }}
                  >
                    {reportYearOptions.map((year) => <MenuItem key={year} value={String(year)}>{year}</MenuItem>)}
                  </TextField>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      fetchInventoryAnalytics();
                      fetchInventoryRequests({
                        scope: 'current_month',
                        month: inventoryReportMonth,
                        year: inventoryReportYear,
                      });
                    }}
                    sx={{ width: { xs: '100%', sm: 'fit-content' } }}
                  >
                    Apply Report Filter
                  </Button>
                </Box>

                <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }} elevation={0}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Inventory Stock View</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{inventoryItems.length} item(s)</Typography>
                  </Box>
                  <TableContainer sx={{ maxHeight: 230, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell align="right">Stock</TableCell>
                          <TableCell align="center">Level</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inventoryItems.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                              No inventory stock records found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          [...inventoryItems]
                            .sort((a, b) => Number(a?.quantity || 0) - Number(b?.quantity || 0) || String(a?.item_name || '').localeCompare(String(b?.item_name || '')))
                            .map((item) => {
                              const quantity = Number(item?.quantity || 0);
                              const level = getInventoryStockLevel(quantity);
                              const levelLabel = level === 'critical' ? 'Critical' : level === 'warning' ? 'Low' : 'Available';
                              const chipColor = level === 'critical' ? 'error' : level === 'warning' ? 'warning' : 'success';
                              return (
                                <TableRow key={`hr-stock-${item.id || item.item_name}`}>
                                  <TableCell sx={{ fontWeight: level === 'ok' ? 400 : 700 }}>{item.item_name}</TableCell>
                                  <TableCell align="right">{quantity}</TableCell>
                                  <TableCell align="center">
                                    <Chip size="small" color={chipColor} variant={level === 'ok' ? 'outlined' : 'filled'} label={levelLabel} sx={{ fontWeight: 700, minWidth: 84 }} />
                                  </TableCell>
                                </TableRow>
                              );
                            })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid xs={12} md={3}>
                    <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Pending Requests</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>{Number(inventoryAnalytics?.statusCounts?.pending || 0)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid xs={12} md={3}>
                    <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Approved</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>{Number(inventoryAnalytics?.statusCounts?.approved || 0)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid xs={12} md={3}>
                    <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Rejected</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#d32f2f' }}>{Number(inventoryAnalytics?.statusCounts?.rejected || 0)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid xs={12} md={3}>
                    <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Cancelled</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>{Number(inventoryAnalytics?.statusCounts?.cancelled || 0)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <Paper sx={{ p: 2, borderRadius: 2, minHeight: 120 }} elevation={0}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Low Stock (Top 5)</Typography>
                      {inventoryAnalyticsLoading ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading...</Typography>
                      ) : inventoryAnalytics.lowStockItems.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No low-stock items.</Typography>
                      ) : (
                        inventoryAnalytics.lowStockItems.slice(0, 5).map((it) => (
                          <Typography key={`low-${it.id || it.item_name}`} variant="body2">
                            {it.item_name}: <strong>{Number(it.quantity || 0)}</strong>
                          </Typography>
                        ))
                      )}
                    </Paper>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <Paper sx={{ p: 2, borderRadius: 2, minHeight: 120 }} elevation={0}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Top Requested (This Month)</Typography>
                      {inventoryAnalyticsLoading ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading...</Typography>
                      ) : inventoryAnalytics.topRequestedItems.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No requests yet.</Typography>
                      ) : (
                        inventoryAnalytics.topRequestedItems.slice(0, 5).map((it, idx) => (
                          <Typography key={`top-${it.item_name}-${idx}`} variant="body2">
                            {it.item_name}: <strong>{Number(it.request_count || 0)}</strong> req
                          </Typography>
                        ))
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 220, whiteSpace: 'nowrap' }}>Item Name</TableCell>
                        <TableCell sx={{ width: 110, whiteSpace: 'nowrap' }}>Quantity</TableCell>
                        <TableCell sx={{ width: 140, whiteSpace: 'nowrap' }}>Requested By</TableCell>
                        <TableCell sx={{ width: 180, whiteSpace: 'nowrap' }}>Branch</TableCell>
                        <TableCell sx={{ width: 220, whiteSpace: 'nowrap' }}>Notes</TableCell>
                        <TableCell sx={{ width: 170, whiteSpace: 'nowrap' }}>Date Requested</TableCell>
                        <TableCell sx={{ width: 120, whiteSpace: 'nowrap' }}>Status</TableCell>
                        <TableCell sx={{ width: 140, whiteSpace: 'nowrap' }}>Approved By</TableCell>
                        <TableCell sx={{ width: 180, whiteSpace: 'nowrap' }}>Approved At</TableCell>
                        <TableCell sx={{ width: 120, whiteSpace: 'nowrap' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventoryRequests && inventoryRequests.length > 0 ? inventoryRequests.map(req => (
                          <TableRow key={req.id}>
                            <TableCell>{req.item_name || req.itemName}</TableCell>
                            <TableCell>{req.requested_quantity || req.quantity || 0}</TableCell>
                            <TableCell>{formatInventoryPersonLabel(req.requested_by_employee_no || req.requested_by || req.requester || req.employeeid || '', req.requested_by_name || req.requested_by || req.requester || '')}</TableCell>
                            <TableCell>{req.branch_code && req.branch_name ? `${req.branch_code} - ${req.branch_name}` : req.branch_code || req.branch_name || req.requested_by_branch_name || '-'}</TableCell>
                            <TableCell>{req.notes || req.message || req.note || '-'}</TableCell>
                            <TableCell>{formatInventoryRequestTimestamp(req.requested_at || req.date_requested || req.created_at)}</TableCell>
                            <TableCell>{req.status || ''}</TableCell>
                            <TableCell>{req.status === 'pending' ? '-' : formatInventoryPersonLabel(req.approved_by_employee_no || '', req.approved_by_name || req.approved_by || '', '-')}</TableCell>
                            <TableCell>{req.status === 'pending' ? '-' : formatInventoryRequestTimestamp(req.approved_at)}</TableCell>
                            <TableCell>
                              {/* HR can only view the request status; approvals are handled by Employees */}
                              <Typography variant="caption">{req.status || 'pending'}</Typography>
                            </TableCell>
                          </TableRow>
                        )) : (
                          <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>No inventory requests</TableCell></TableRow>
                        )}
                    </TableBody>
                  </Table>
                  </TableContainer>
                </Box>
              </Paper>
            )}

            <Dialog open={inventoryReportOpen} onClose={() => setInventoryReportOpen(false)} maxWidth="lg" fullWidth>
              <DialogTitle>
                Inventory Report - {inventoryAnalytics?.reportPeriod?.label || currentInventoryMonthLabel}
              </DialogTitle>
              <DialogContent dividers>
                <Box className="inventory-report-print" sx={{ p: 1 }}>
                  <Box className="inventory-print-header" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                      <Typography className="inventory-print-title" variant="h5" sx={{ fontWeight: 800 }}>Inventory Request Report</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Period: {inventoryAnalytics?.reportPeriod?.label || currentInventoryMonthLabel}
                      </Typography>
                    </Box>
                    <Box className="inventory-print-meta" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                      <Typography variant="body2">Generated: {formatDateTimePH(new Date())}</Typography>
                      <Typography variant="body2">Most requested item: {inventoryAnalytics.topRequestedItems?.[0]?.item_name || 'None'}</Typography>
                    </Box>
                  </Box>

                  <Grid className="inventory-kpi-grid" container spacing={2} sx={{ mb: 2 }}>
                    {[
                      ['Pending', inventoryAnalytics?.statusCounts?.pending || 0],
                      ['Approved', inventoryAnalytics?.statusCounts?.approved || 0],
                      ['Rejected', inventoryAnalytics?.statusCounts?.rejected || 0],
                      ['Cancelled', inventoryAnalytics?.statusCounts?.cancelled || 0],
                    ].map(([label, value]) => (
                      <Grid xs={6} md={3} key={label}>
                        <Paper className="inventory-print-card" variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography className="inventory-print-kpi-label" variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
                          <Typography className="inventory-print-kpi-value" variant="h5" sx={{ fontWeight: 800 }}>{Number(value || 0)}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Grid className="inventory-chart-grid" container spacing={2}>
                    <Grid xs={12}>
                      <Paper className="inventory-print-card" variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Most Requested Items</Typography>
                        {inventoryAnalytics.topRequestedItems.length === 0 ? (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>No requests for this month.</Typography>
                        ) : (() => {
                          const rows = inventoryAnalytics.topRequestedItems.slice(0, 10);
                          const maxCount = Math.max(1, ...rows.map((row) => Number(row.request_count || 0)));
                          return rows.map((row, idx) => {
                            const count = Number(row.request_count || 0);
                            const qty = Number(row.total_quantity || 0);
                            const width = `${Math.max(8, Math.round((count / maxCount) * 100))}%`;
                            return (
                              <Box className="inventory-print-row" key={`report-top-${row.item_name}-${idx}`} sx={{ display: 'grid', gridTemplateColumns: '180px 1fr 92px', gap: 1, alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: idx === 0 ? 800 : 500 }}>{row.item_name}</Typography>
                                <Box sx={{ height: 12, bgcolor: '#e8eef8', borderRadius: 999, overflow: 'hidden' }}>
                                  <Box sx={{ width, height: '100%', bgcolor: idx === 0 ? '#1d4ed8' : '#60a5fa', borderRadius: 999 }} />
                                </Box>
                                <Typography variant="body2" sx={{ textAlign: 'right' }}>{count} req / {qty} qty</Typography>
                              </Box>
                            );
                          });
                        })()}
                      </Paper>
                    </Grid>

                    <Grid xs={12}>
                      <Paper className="inventory-print-card" variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Low Stock Watchlist</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Item</TableCell>
                              <TableCell align="right">Current Stock</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {inventoryAnalytics.lowStockItems.slice(0, 20).map((item) => (
                              <TableRow key={`report-low-${item.id || item.item_name}`}>
                                <TableCell>{item.item_name}</TableCell>
                                <TableCell align="right">{Number(item.quantity || 0)}</TableCell>
                              </TableRow>
                            ))}
                            {inventoryAnalytics.lowStockItems.length === 0 && (
                              <TableRow><TableCell colSpan={2}>No low-stock items.</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setInventoryReportOpen(false)}>Close</Button>
                <Button variant="contained" onClick={() => printReportNode('.inventory-report-print', 'Inventory Request Report')}>
                  Print Report
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={inventoryHistoryOpen}
              onClose={() => setInventoryHistoryOpen(false)}
              maxWidth="xl"
              fullWidth
              PaperProps={{
                sx: {
                  width: 'min(96vw, 1240px)',
                  maxWidth: '1240px',
                },
              }}
            >
              <DialogTitle>Inventory Request History</DialogTitle>
              <DialogContent dividers sx={{ overflowX: 'hidden', px: { xs: 2, sm: 3 }, py: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                  Older requests outside {currentInventoryMonthLabel} are shown here by default. Use the filters below to narrow history further.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'flex-start',
                    mb: 3,
                  }}
                >
                  <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                    <TextField
                      select
                      label="Month"
                      fullWidth
                      size="small"
                      value={inventoryHistoryFilters.month || ''}
                      onChange={(e) => setInventoryHistoryFilters((prev) => ({ ...prev, month: String(e.target.value || '') }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      {getMonthOptionsForYear(inventoryHistoryFilters.year || defaultReportPeriod.year).map((month) => (
                        <MenuItem key={month} value={String(month)}>
                          {APP_MONTH_NAMES[month - 1]}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ flex: '1 1 180px', minWidth: { xs: '100%', sm: 180 } }}>
                    <TextField
                      select
                      label="Year"
                      fullWidth
                      size="small"
                      value={inventoryHistoryFilters.year || ''}
                      onChange={(e) => setInventoryHistoryFilters((prev) => ({ ...prev, year: String(e.target.value || '') }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      {(inventoryHistoryYearOptions.length ? inventoryHistoryYearOptions : reportYearOptions).filter((year) => reportYearOptions.includes(Number(year))).map((year) => (
                        <MenuItem key={year} value={String(year)}>{year}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                    <TextField
                      select
                      label="Status"
                      fullWidth
                      size="small"
                      value={inventoryHistoryFilters.status || ''}
                      onChange={(e) => setInventoryHistoryFilters((prev) => ({ ...prev, status: String(e.target.value || '') }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.25,
                      flex: '1 1 240px',
                      minWidth: { xs: '100%', sm: 240 },
                      justifyContent: { xs: 'stretch', md: 'flex-end' },
                      alignSelf: 'stretch',
                    }}
                  >
                    <Box sx={{ flex: { xs: 1, md: '0 0 120px' } }}>
                      <Button variant="contained" fullWidth onClick={() => fetchInventoryHistory()}>APPLY</Button>
                    </Box>
                    <Box sx={{ flex: { xs: 1, md: '0 0 120px' } }}>
                      <Button
                        variant="text"
                        color="secondary"
                        fullWidth
                        onClick={() => {
                          const nextFilters = { month: '', year: '', status: '' };
                          setInventoryHistoryFilters(nextFilters);
                          fetchInventoryHistory(nextFilters);
                        }}
                      >
                        CLEAR
                      </Button>
                    </Box>
                  </Box>
                </Box>

                <TableContainer
                  sx={{
                    maxHeight: 460,
                    overflowX: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 220, whiteSpace: 'nowrap' }}>Item Name</TableCell>
                        <TableCell sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>Quantity</TableCell>
                        <TableCell sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>Requested By</TableCell>
                        <TableCell sx={{ minWidth: 220 }}>Branch</TableCell>
                        <TableCell sx={{ minWidth: 190, whiteSpace: 'nowrap' }}>Date Requested</TableCell>
                        <TableCell sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>Status</TableCell>
                        <TableCell sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>Approved By</TableCell>
                        <TableCell sx={{ minWidth: 190, whiteSpace: 'nowrap' }}>Approved At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inventoryHistoryLoading ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>Loading history...</TableCell>
                        </TableRow>
                      ) : inventoryHistoryRows.length > 0 ? inventoryHistoryRows.map((req) => (
                        <TableRow key={`history-${req.id}`}>
                          <TableCell sx={{ verticalAlign: 'top' }}>{req.item_name || req.itemName}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{req.requested_quantity || req.quantity || 0}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{formatInventoryPersonLabel(req.requested_by_employee_no || req.requested_by || req.requester || req.employeeid || '', req.requested_by_name || req.requested_by || req.requester || '')}</TableCell>
                          <TableCell sx={{ verticalAlign: 'top', lineHeight: 1.35 }}>{req.branch_code && req.branch_name ? `${req.branch_code} - ${req.branch_name}` : req.branch_code || req.branch_name || req.requested_by_branch_name || '-'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', fontVariantNumeric: 'tabular-nums' }}>{formatInventoryRequestTimestamp(req.requested_at || req.date_requested || req.created_at)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', textTransform: 'capitalize' }}>{req.status || ''}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{req.status === 'pending' ? '-' : formatInventoryPersonLabel(req.approved_by_employee_no || '', req.approved_by_name || req.approved_by || '', '-')}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', fontVariantNumeric: 'tabular-nums' }}>{req.status === 'pending' ? '-' : formatInventoryRequestTimestamp(req.approved_at)}</TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>No history records found.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setInventoryHistoryOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>

                        {/* Leave Management section */}
            {activeSection === 'leave-management' && (
              <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%', overflow: 'hidden', minWidth: 0 }}>
                <Box sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Leave Management</Typography>
                  </Box>
                </Box>

                <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', overflowX: 'hidden' }}>
                  <Typography variant="h6" gutterBottom>All Leave Requests</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={leaveFilterMonth}
                        label="Month"
                        onChange={(e) => setLeaveFilterMonth(e.target.value)}
                      >
                        {getMonthOptionsForYear(leaveFilterYear).map((month) => (
                          <MenuItem key={month} value={String(month)}>{APP_MONTH_NAMES[month - 1]}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      select
                      size="small"
                      label="Year"
                      value={leaveFilterYear}
                      onChange={(e) => {
                        const next = clampReportPeriod(leaveFilterMonth, e.target.value);
                        setLeaveFilterMonth(String(next.month));
                        setLeaveFilterYear(String(next.year));
                      }}
                      sx={{ width: 120 }}
                    >
                      {reportYearOptions.map((year) => <MenuItem key={year} value={String(year)}>{year}</MenuItem>)}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Status"
                      value={leaveStatusFilter}
                      onChange={(e) => setLeaveStatusFilter(String(e.target.value || 'all'))}
                      sx={{ width: 160 }}
                    >
                      <MenuItem value="all">All statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </TextField>
                    <TextField
                      size="small"
                      label="Start Date"
                      type="date"
                      value={leaveFilterStartDate}
                      onChange={(e) => setLeaveFilterStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 170 }}
                    />
                    <TextField
                      size="small"
                      label="End Date"
                      type="date"
                      value={leaveFilterEndDate}
                      onChange={(e) => setLeaveFilterEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 170 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setLeaveFilterMonth(String(defaultReportPeriod.month));
                        setLeaveFilterYear(String(defaultReportPeriod.year));
                        setLeaveStatusFilter('all');
                        setLeaveFilterStartDate('');
                        setLeaveFilterEndDate('');
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                  <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5 }}>
                    {filteredLeaveRequests && filteredLeaveRequests.length > 0 ? filteredLeaveRequests.map((request) => (
                      <Paper key={request.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {(request.username || '-') +
                            ((request.firstname || request.lastname)
                              ? ` - ${[request.firstname, request.lastname].filter(Boolean).join(' ')}`
                              : '')}
                        </Typography>
                        <Typography variant="body2"><strong>Leave:</strong> {request.leave_type}</Typography>
                        <Typography variant="body2"><strong>Start:</strong> {formatDatePH(request.start_date)}</Typography>
                        <Typography variant="body2"><strong>End:</strong> {formatDatePH(request.end_date)}</Typography>
                        <Typography variant="body2">
                          <strong>Requested:</strong>{' '}
                          {request.date_submitted
                            ? formatDateTimePH(request.date_submitted)
                            : (request.date_requested ? formatDateTimePH(request.date_requested) : 'â€“')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Reason:</strong> {request.reason || '-'}
                        </Typography>
                        <Box sx={{ mt: 0.75 }}>
                          <Typography variant="body2" component="div">
                            <strong>Attachment:</strong>{' '}
                            <LeaveAttachmentLink row={request} />
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 0.75 }}>
                          <strong>Status:</strong> {formatLeaveStatusLabel(request.status)}
                        </Typography>
                        <Box sx={{ mt: 1.25 }}>
                          {isPendingLeaveRequest(request.status) ? (
                            <Box sx={{ display: 'grid', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                fullWidth
                                onClick={() => updateLeaveRequestStatus(request.id, 'approved')}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                fullWidth
                                onClick={() => updateLeaveRequestStatus(request.id, 'rejected')}
                              >
                                Reject
                              </Button>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">No action</Typography>
                          )}
                        </Box>
                      </Paper>
                    )) : (
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                        No leave requests found.
                      </Paper>
                    )}
                  </Box>

                  <TableContainer sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 980 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Requester</TableCell>
                          <TableCell>Leave Type</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell>End Date</TableCell>
                          <TableCell>Date Requested</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>Attachment</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredLeaveRequests && filteredLeaveRequests.length > 0 ? filteredLeaveRequests.map((request) => (
                          <TableRow key={request.id}>
                            <TableCell>
                              {request.username || '-'}
                              {(request.firstname || request.lastname) ? ` - ${[request.firstname, request.lastname].filter(Boolean).join(' ')}` : ''}
                            </TableCell>
                            <TableCell>{request.leave_type}</TableCell>
                            <TableCell>{formatDatePH(request.start_date)}</TableCell>
                            <TableCell>{formatDatePH(request.end_date)}</TableCell>
                            <TableCell>{request.date_submitted ? formatDateTimePH(request.date_submitted) : (request.date_requested ? formatDateTimePH(request.date_requested) : '–')}</TableCell>
                            <TableCell>{request.reason}</TableCell>
                            <TableCell>
                              <LeaveAttachmentLink row={request} />
                              {/* legacy attachment fallback
                                <a href={request.file_url} target="_blank" rel="noreferrer">View</a>
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                              */}
                            </TableCell>
                            <TableCell>{formatLeaveStatusLabel(request.status)}</TableCell>
                            <TableCell>
                              {isPendingLeaveRequest(request.status) ? (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Button size="small" variant="contained" color="success" onClick={() => updateLeaveRequestStatus(request.id, 'approved')}>
                                    Approve
                                  </Button>
                                  <Button size="small" variant="contained" color="error" onClick={() => updateLeaveRequestStatus(request.id, 'rejected')}>
                                    Reject
                                  </Button>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                              No leave requests found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            )}

            {activeSection === 'file-leave' && (
              <Paper sx={{ p: { xs: 2, md: 3 }, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>File Leave</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  Request time off and manage your leave history.
                </Typography>

                <Box sx={{ display: 'grid', gap: { xs: 2, md: 3 }, minWidth: 0 }}>
                  <LeaveRequestForm
                    onSubmitted={fetchMyLeaveRequests}
                    onMetaLoaded={handleMyLeaveMetaLoaded}
                    enabled={!mustChangePassword}
                  />

                  <LeaveBalancesSummary
                    balances={leaveBalances}
                    leaveTypes={allowedLeaveTypes}
                  />

                  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
                    <Typography variant="h6" gutterBottom>Your Leave Requests</Typography>
                    <LeaveRequestsTable
                      rows={myLeaveRequests}
                      loading={myLeaveLoading}
                      error={myLeaveError}
                      allowFollowupUpload
                      onAttachmentUploaded={fetchMyLeaveRequests}
                    />
                  </Paper>
                </Box>
              </Paper>
            )}

                <Dialog open={manualRejectOpen} onClose={() => { setManualRejectOpen(false); setManualRejectTarget(null); setManualRejectReason(''); setManualRejectResolution('absent'); }} maxWidth="sm" fullWidth>
                  <DialogTitle>Reject Manual Time-In</DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>Choose how this request should be recorded in attendance.</Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Mark As</InputLabel>
                      <Select value={manualRejectResolution} label="Mark As" onChange={(e) => setManualRejectResolution(e.target.value)}>
                        <MenuItem value="late">Late</MenuItem>
                        <MenuItem value="absent">Absent</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField label="Reason (optional)" fullWidth multiline minRows={2} value={manualRejectReason} onChange={(e) => setManualRejectReason(e.target.value)} />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => { setManualRejectOpen(false); setManualRejectTarget(null); setManualRejectReason(''); setManualRejectResolution('absent'); }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={submitRejectManualRequest}>Apply</Button>
                  </DialogActions>
                </Dialog>

        </Box>
      </Box>

      <Snackbar open={!!snack.open} autoHideDuration={4000} onClose={() => setSnack({ open: false, message: '', severity: 'info' })}>
        <Alert severity={snack.severity || 'info'}>{snack.message}</Alert>
      </Snackbar>
      {/* Edit employee dialog (HR) - global so it works regardless of activeSection */}
      <Dialog open={editOpen} onClose={() => { setEditOpen(false); setEditForm(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { minWidth: 480, maxWidth: 620 } }}>
        <DialogTitle>Edit Employee</DialogTitle>
        <DialogContent>
          {editForm && (
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField label="Firstname *" value={editForm.firstname || ''} onChange={(e) => setEditForm(f => ({ ...f, firstname: toTitleName(filterLetters(e.target.value)) }))} onKeyDown={lettersKeyDown} fullWidth />
              <TextField label="Middlename" value={editForm.middlename || ''} onChange={(e) => setEditForm(f => ({ ...f, middlename: toTitleName(filterLetters(e.target.value)) }))} onKeyDown={lettersKeyDown} fullWidth />
              <TextField label="Lastname *" value={editForm.lastname || ''} onChange={(e) => setEditForm(f => ({ ...f, lastname: toTitleName(filterLetters(e.target.value)) }))} onKeyDown={lettersKeyDown} fullWidth />
              <Grid container spacing={2}>
                <Grid xs={12} md={4}>
                  <TextField label="Birthdate" type="date" value={editForm.birthdate || ''} onChange={(e) => handleEditBirthdateChange(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="Age" value={editForm.age || ''} disabled fullWidth />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="Employee ID" value={String(editForm.employeeid || editForm.userId || editForm.username || '').padStart(8,'0')} disabled fullWidth />
                </Grid>
              </Grid>

              <TextField label="Address *" value={editForm.address || ''} onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))} fullWidth />
              <TextField label="Phone Number *" value={editForm.phone || ''} onChange={(e) => setEditForm(f => ({ ...f, phone: filterPhone(e.target.value) }))} onKeyDown={(e) => phoneKeyDown(e, editForm.phone)} fullWidth inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 11 }} />
              <TextField label="Email *" value={editForm.email || ''} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} fullWidth />
              <TextField label="Password" type="password" value={editForm.password || ''} onChange={(e) => setEditForm(f => ({ ...f, password: e.target.value }))} fullWidth helperText="Leave blank to keep current password. Minimum 8 characters if changed." inputProps={{ autoComplete: 'new-password' }} />

              <Grid container spacing={2}>
                <Grid xs={12} md={4}>
                  <TextField label="SSS Number" value={editForm.sss_no || ''} onChange={(e) => setEditForm(f => ({ ...f, sss_no: formatSssNumber(e.target.value) }))} onKeyDown={digitsKeyDown} fullWidth placeholder="12-3456789-0" error={!!getGovNumberError('sss_no', editForm.sss_no)} helperText={getGovNumberError('sss_no', editForm.sss_no) || 'Format: 12-3456789-0'} inputProps={{ inputMode: 'numeric', maxLength: 12 }} />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="PhilHealth Number" value={editForm.philhealth_no || ''} onChange={(e) => setEditForm(f => ({ ...f, philhealth_no: formatGov12Number(e.target.value) }))} onKeyDown={digitsKeyDown} fullWidth placeholder="1234-5678-9012" error={!!getGovNumberError('philhealth_no', editForm.philhealth_no)} helperText={getGovNumberError('philhealth_no', editForm.philhealth_no) || 'Format: 1234-5678-9012'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="Pag-IBIG Number" value={editForm.pagibig_no || ''} onChange={(e) => setEditForm(f => ({ ...f, pagibig_no: formatGov12Number(e.target.value) }))} onKeyDown={digitsKeyDown} fullWidth placeholder="1234-5678-9012" error={!!getGovNumberError('pagibig_no', editForm.pagibig_no)} helperText={getGovNumberError('pagibig_no', editForm.pagibig_no) || 'Format: 1234-5678-9012'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} />
                </Grid>
              </Grid>

              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select value={editForm.role || ''} label="Role" onChange={(e) => setEditForm(f => ({ ...f, role: e.target.value }))}>
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="Employee">Employee</MenuItem>
                  <MenuItem value="Custodian">Custodian</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditOpen(false); setEditForm(null); }}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            try {
              const editErrors = [
                getGovNumberError('sss_no', editForm?.sss_no),
                getGovNumberError('philhealth_no', editForm?.philhealth_no),
                getGovNumberError('pagibig_no', editForm?.pagibig_no),
                editForm?.password && String(editForm.password).length < 8 ? 'Password must be at least 8 characters' : '',
              ].filter(Boolean);
              if (editErrors.length > 0) {
                setSnack({ open: true, message: editErrors[0], severity: 'error' });
                return;
              }
              // prepare payload with only changed fields
              const payload = {};
              ['firstname','middlename','lastname','address','phone','email','password','role','baseSalary','birthdate','sss_no','philhealth_no','pagibig_no','date_hired','salaryType'].forEach(k => { if (editForm && Object.prototype.hasOwnProperty.call(editForm, k)) payload[k] = editForm[k]; });
              if (payload.password === '') delete payload.password;
              if (Object.prototype.hasOwnProperty.call(payload, 'sss_no')) payload.sss_no = normalizeGovNumberForPayload('sss_no', payload.sss_no);
              if (Object.prototype.hasOwnProperty.call(payload, 'philhealth_no')) payload.philhealth_no = normalizeGovNumberForPayload('philhealth_no', payload.philhealth_no);
              if (Object.prototype.hasOwnProperty.call(payload, 'pagibig_no')) payload.pagibig_no = normalizeGovNumberForPayload('pagibig_no', payload.pagibig_no);
              const idParam = editForm.id || editForm.userId || editForm.employeeid || editForm.username;
              await axios.put(`${API_BASE}/api/employees/${idParam}`, payload, { withCredentials: true });
              setSnack({ open: true, message: 'Employee updated', severity: 'success' });
              setEditOpen(false); setEditForm(null);
              try { await fetchEmployees(); } catch (e) {}
            } catch (e) {
              console.error('Failed updating employee', e);
              const data = e?.response?.data;
              let msg = e?.message || 'Failed to update employee';
              if (data && data.error) msg = String(data.error);
              if (e?.response?.data?.validationErrors) {
                // show first validation error in snack
                const ve = e.response.data.validationErrors;
                const firstKey = Object.keys(ve)[0];
                setSnack({ open: true, message: ve[firstKey], severity: 'error' });
              } else {
                setSnack({ open: true, message: msg, severity: 'error' });
              }
            }
          }}>UPDATE EMPLOYEE</Button>
        </DialogActions>
      </Dialog>

      {/* Terminate confirmation dialog - global */}
  <Dialog open={deleteDialogOpen} onClose={() => { if (terminatingAccount) return; setDeleteDialogOpen(false); setDeleteTarget(null); }} PaperProps={{ sx: { minWidth: 420, maxWidth: 520 } }}>
        <DialogTitle>Confirm account termination</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to terminate this account?</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            This will disable login and hide the account from active operations, while keeping history records.
          </Typography>
          {deleteTarget && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>{String(deleteTarget.employeeid || deleteTarget.employee_id || deleteTarget.userId || '')}</Typography>
              <Typography variant="caption">{deleteTarget.email || ''}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={terminatingAccount} onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}>CANCEL</Button>
          <Button color="error" variant="contained" disabled={terminatingAccount} onClick={async () => {
            if (!deleteTarget) return;
            if (terminatingAccount) return;
            setTerminatingAccount(true);
            try {
              // prefer numeric id when available
              const idParam = deleteTarget.id || deleteTarget.userId || deleteTarget.employeeid || deleteTarget.username || deleteTarget.email;
              const res = await axios.delete(`${API_BASE}/api/employees/${idParam}`, { withCredentials: true });
              setSnack({ open: true, message: res?.data?.message || 'Account terminated', severity: 'info' });
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
              fetchEmployees();
            } catch (e) {
              console.error('Delete failed', e);
              // show server-provided error text when available
              const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to terminate account';
              setSnack({ open: true, message: msg, severity: 'error' });
            } finally {
              setTerminatingAccount(false);
            }
          }}>{terminatingAccount ? 'TERMINATING...' : 'TERMINATE'}</Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={employeeDetailOpen}
        onClose={handleCloseEmployeeDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Employee Details</DialogTitle>
        <DialogContent dividers>
          {selectedEmployee && (
            <Box mb={2} sx={{ display: 'grid', gap: 0.75 }}>
              <Typography variant="h6">{formatNameFull(selectedEmployee)}</Typography>
              <Typography variant="body2">Employee ID: {String(selectedEmployee.employeeid || selectedEmployee.employee_no || '').padStart(8, '0')}</Typography>
              <Typography variant="body2">Role: {selectedEmployee.role || '-'}</Typography>
              <Typography variant="body2">Middle Name: {toTitleName(selectedEmployee.middlename || selectedEmployee.middle_name || '-')}</Typography>
              <Typography variant="body2">Age: {selectedEmployee.age || '-'}</Typography>
              <Typography variant="body2">Gender: {selectedEmployee.gender || '-'}</Typography>
              <Typography variant="body2">Email: {selectedEmployee.email || '-'}</Typography>
              <Typography variant="body2">Phone: {selectedEmployee.phone || selectedEmployee.contact || '-'}</Typography>
              <Typography variant="body2">Address: {selectedEmployee.address || '-'}</Typography>
              <Typography variant="body2">SSS Number: {formatGovNumberForDisplay('sss_no', selectedEmployee.sss_no) || 'None'}</Typography>
              <Typography variant="body2">PhilHealth Number: {formatGovNumberForDisplay('philhealth_no', selectedEmployee.philhealth_no) || 'None'}</Typography>
              <Typography variant="body2">Pag-IBIG Number: {formatGovNumberForDisplay('pagibig_no', selectedEmployee.pagibig_no) || 'None'}</Typography>
              <Typography variant="body2">Date Hired: {selectedEmployee.date_hired ? formatDateOnly(selectedEmployee.date_hired) : '-'}</Typography>
              <Typography variant="body2">Status: {String(selectedEmployee.status || 'active').toUpperCase()}</Typography>
              {(selectedEmployee.branch_code || selectedEmployee.branch_name) ? (
                <Typography variant="body2">
                  Branch: {[selectedEmployee.branch_code, selectedEmployee.branch_name].filter(Boolean).join(' - ')}
                </Typography>
              ) : null}
              {(selectedEmployee.branch_city || selectedEmployee.branch_province) ? (
                <Typography variant="body2">
                  Branch Location: {[selectedEmployee.branch_city, selectedEmployee.branch_province].filter(Boolean).join(', ')}
                </Typography>
              ) : null}
              {(selectedEmployee.shift_start || selectedEmployee.shift_end) ? (
                <Typography variant="body2">
                  Shift: {selectedEmployee.shift_start || '-'}{selectedEmployee.shift_end ? ` - ${selectedEmployee.shift_end}` : ''}
                </Typography>
              ) : null}
              {manualRequestDetail ? (
                <Box sx={{ mt: 1, p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1, bgcolor: '#f8fafc' }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Manual Request Details</Typography>
                  <Typography variant="body2">Date: {formatManualDate(manualRequestDetail.requested_date)}</Typography>
                  <Typography variant="body2">Time: {formatManualTime(manualRequestDetail.requested_time)}</Typography>
                  <Typography variant="body2">Status: {String(manualRequestDetail.status || '').toUpperCase() || '-'}</Typography>
                  <Typography variant="body2">Reason: {manualRequestDetail.reason || '-'}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>Submitted Proof Photo</Typography>
                    {manualRequestEvidenceLoading ? (
                      <Typography variant="body2">Loading evidence...</Typography>
                    ) : manualRequestEvidence.length === 0 ? (
                      <Typography variant="body2">No evidence photo attached.</Typography>
                    ) : (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {manualRequestEvidence.map((evidence) => (
                          <Box key={evidence.id} border={1} borderRadius={1} p={0.5}>
                            <img
                              src={evidence.file_url}
                              alt="Manual attendance evidence"
                              style={{ width: 120, height: 120, objectFit: 'cover' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              ) : null}
            </Box>
          )}

          {employeeDetailShowPhotos ? (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Face Photos
              </Typography>
              {loadingEmployeePhotos && (
                <Typography variant="body2">Loading photos...</Typography>
              )}
              {!loadingEmployeePhotos && employeePhotos.length === 0 && (
                <Typography variant="body2">No photos found for this employee.</Typography>
              )}
              <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
                {Array.isArray(employeePhotos) &&
                  employeePhotos.map((photo) => (
                    <Box key={photo.id} border={1} borderRadius={1} p={0.5}>
                      <img
                        src={`${API_BASE}/api/photos_blob/image/${photo.id}`}
                        alt="Employee"
                        style={{ width: 120, height: 120, objectFit: 'cover' }}
                      />
                    </Box>
                  ))}
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmployeeDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportEmailOpen} onClose={() => { if (!reportEmailSending) setReportEmailOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Send Report by Gmail</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Enter the Gmail address that should receive this report.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Recipient Gmail"
            placeholder="name@gmail.com"
            value={reportEmailTarget}
            onChange={(e) => setReportEmailTarget(e.target.value)}
            disabled={reportEmailSending}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportEmailOpen(false)} disabled={reportEmailSending}>Cancel</Button>
          <Button variant="contained" onClick={sendReportEmail} disabled={reportEmailSending || !String(reportEmailTarget || '').trim()}>
            {reportEmailSending ? <CircularProgress size={18} /> : 'Send Report'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={interviewDialogOpen} onClose={closeInterviewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send Interview Email</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {interviewApplicant
                ? `Send a formal interview invitation to ${formatName(interviewApplicant)} (${interviewApplicant.email || 'no email'})`
                : 'Prepare an interview invitation email.'}
            </Typography>
            <TextField
              label="Interview Date & Time"
              type="datetime-local"
              value={interviewForm.interview_at}
              onChange={(e) => setInterviewForm((prev) => ({ ...prev, interview_at: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Interview Location"
              value={interviewForm.location}
              onChange={(e) => setInterviewForm((prev) => ({ ...prev, location: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Email Subject"
              value={interviewForm.subject}
              onChange={(e) => setInterviewForm((prev) => ({ ...prev, subject: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Formal Message"
              value={interviewForm.message}
              onChange={(e) => setInterviewForm((prev) => ({ ...prev, message: e.target.value }))}
              multiline
              minRows={5}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeInterviewDialog} disabled={interviewSending}>Cancel</Button>
          <Button variant="contained" onClick={sendApplicantInterviewEmail} disabled={interviewSending}>
            {interviewSending ? <CircularProgress size={18} /> : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

{/* Live attendance mini-feed (HR only) */}
      {normalizedRole === 'hr' && liveAttendance && liveAttendance.length > 0 && (
        <Box sx={{ position: 'fixed', right: 16, bottom: 16, width: 340, zIndex: 1400 }}>
          <Paper elevation={6} sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Live attendance</Typography>
            <List dense sx={{ maxHeight: 220, overflow: 'auto' }}>
              {liveAttendance.map((ev, i) => (
                <ListItem key={i} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Avatar sx={{ width: 28, height: 28 }}>{String(ev.userId || ev.user || ev.userId || '').slice(0,2)}</Avatar>
                  </ListItemIcon>
                  <ListItemText primary={ev.message || (ev.userId ? `User ${ev.userId}` : JSON.stringify(ev))} secondary={ev.timestamp || ''} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}
        {/* Add Employee Dialog */}
        <Dialog open={addOpen} onClose={closeAdd} maxWidth="md" fullWidth>
          <DialogTitle>Add Employee</DialogTitle>
          <DialogContent>
            <Box component="form" autoComplete="off" onSubmit={(e) => { e.preventDefault(); submitAdd(); }} sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid xs={12} md={4}>
                  <TextField label="Employee ID" fullWidth value={addForm.employeeid || peekEmployeeId} disabled />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="First name" fullWidth value={addForm.firstname} onChange={handleChange('firstname')} onKeyDown={lettersKeyDown} error={!!addErrors.firstname} helperText={addErrors.firstname} InputProps={{ sx: { color: addErrors.firstname ? 'error.main' : undefined } }} />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="Middle name" fullWidth value={addForm.middlename} onChange={handleChange('middlename')} onKeyDown={lettersKeyDown} error={!!addErrors.middlename} helperText={addErrors.middlename || 'Optional'} InputProps={{ sx: { color: addErrors.middlename ? 'error.main' : undefined } }} disabled={addNoMiddleName} placeholder={addNoMiddleName ? 'N/A' : ''} />
                  <FormControlLabel
                    control={<Checkbox checked={addNoMiddleName} onChange={(e) => {
                      const checked = e.target.checked;
                      setAddNoMiddleName(checked);
                      if (checked) {
                        setAddForm(f => ({ ...f, middlename: '' }));
                        setAddErrors(prev => { const next = { ...prev }; delete next.middlename; return next; });
                      }
                    }} />}
                    label="No Middle Name"
                  />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="Last name" fullWidth value={addForm.lastname} onChange={handleChange('lastname')} onKeyDown={lettersKeyDown} error={!!addErrors.lastname} helperText={addErrors.lastname} InputProps={{ sx: { color: addErrors.lastname ? 'error.main' : undefined } }} />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="Birthdate" type="date" fullWidth InputLabelProps={{ shrink: true }} inputProps={{ min: '1983-01-01', max: '2004-12-31' }} value={addForm.birthdate} onChange={handleChange('birthdate')} error={!!addErrors.birthdate} helperText={addErrors.birthdate || 'Year must be between 1983 and 2004'} />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="Age" fullWidth value={addForm.age} disabled error={!!addErrors.age} helperText={addErrors.age} InputProps={{ sx: { color: addErrors.age ? 'error.main' : undefined } }} />
                </Grid>

                <Grid xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select value={addForm.gender} label="Gender" onChange={(e) => setAddForm(f => ({ ...f, gender: e.target.value }))}>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Civil status</InputLabel>
                    <Select value={addForm.civil_status} label="Civil status" onChange={(e) => setAddForm(f => ({ ...f, civil_status: e.target.value }))}>
                      <MenuItem value="Single">Single</MenuItem>
                      <MenuItem value="Married">Married</MenuItem>
                      <MenuItem value="Widowed">Widowed</MenuItem>
                      <MenuItem value="Separated">Separated</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid xs={12} md={8}>
                  <TextField label="Address" fullWidth value={addForm.address} onChange={handleChange('address')} error={!!addErrors.address} helperText={addErrors.address} InputProps={{ sx: { color: addErrors.address ? 'error.main' : undefined } }} />
                </Grid>

                <Grid xs={12} md={4}>
                  <TextField label="Email" fullWidth value={addForm.email} onChange={handleChange('email')} error={!!addErrors.email} helperText={addErrors.email} InputProps={{ sx: { color: addErrors.email ? 'error.main' : undefined } }} />
                </Grid>

                <Grid xs={12} md={4}>
                  <TextField label="Contact number" fullWidth value={addForm.phone} onChange={handleChange('phone')} onKeyDown={(e) => phoneKeyDown(e, addForm.phone)} error={!!addErrors.phone} helperText={addErrors.phone} InputProps={{ sx: { color: addErrors.phone ? 'error.main' : undefined } }} />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="SSS No. (optional)" placeholder="12-3456789-0" fullWidth value={addForm.sss_no} onChange={handleChange('sss_no')} onKeyDown={digitsKeyDown} error={!!addErrors.sss_no} helperText={addErrors.sss_no || 'Optional'} inputProps={{ inputMode: 'numeric', maxLength: 12 }} InputProps={{ sx: { color: addErrors.sss_no ? 'error.main' : undefined } }} />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="PhilHealth No. (optional)" placeholder="1234-5678-9012" fullWidth value={addForm.philhealth_no} onChange={handleChange('philhealth_no')} onKeyDown={digitsKeyDown} error={!!addErrors.philhealth_no} helperText={addErrors.philhealth_no || 'Optional'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} InputProps={{ sx: { color: addErrors.philhealth_no ? 'error.main' : undefined } }} />
                </Grid>
                <Grid xs={12} md={4}>
                  <TextField label="Pag-IBIG No. (optional)" placeholder="1234-5678-9012" fullWidth value={addForm.pagibig_no} onChange={handleChange('pagibig_no')} onKeyDown={digitsKeyDown} error={!!addErrors.pagibig_no} helperText={addErrors.pagibig_no || 'Optional'} inputProps={{ inputMode: 'numeric', maxLength: 14 }} InputProps={{ sx: { color: addErrors.pagibig_no ? 'error.main' : undefined } }} />
                </Grid>

                <Grid xs={12} md={4}>
                  <TextField label="Password" type="password" fullWidth value={addForm.password} onChange={handleChange('password')} error={!!addErrors.password} helperText={addErrors.password || 'Optional. Minimum 8 characters if set.'} inputProps={{ autoComplete: 'new-password' }} InputProps={{ sx: { color: addErrors.password ? 'error.main' : undefined } }} />
                </Grid>

                <Grid xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select value={addForm.role} label="Role" onChange={handleChange('role')}>
                      <MenuItem value="Employee">Employee</MenuItem>
                      {/* Removed Admin role (no such role in system) */}
                      <MenuItem value="Custodian">Custodian</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid xs={12} md={4}>
                  <TextField label="Base salary per day" fullWidth value={addForm.base_salary_per_day} onChange={handleChange('base_salary_per_day')} error={!!addErrors.base_salary_per_day} helperText={addErrors.base_salary_per_day} InputProps={{ sx: { color: addErrors.base_salary_per_day ? 'error.main' : undefined } }} />
                </Grid>

                <Grid xs={12} md={4}>
                  <TextField label="Date hired" type="date" fullWidth InputLabelProps={{ shrink: true }} value={addForm.date_hired} onChange={(e) => setAddForm(f => ({ ...f, date_hired: e.target.value }))} />
                </Grid>

              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAdd}>Cancel</Button>
            <Button variant="contained" onClick={submitAdd} type="submit">Save</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={Boolean(homeIsOnBreak)}
          disableEscapeKeyDown
          fullWidth
          maxWidth="sm"
          onClose={() => {}}
        >
          <DialogTitle sx={{ pb: 1 }}>Currently On Break</DialogTitle>
          <DialogContent>
            <Alert severity={homeBreakCountdownMs > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
              Your account is paused while you are on break. End your break first before using the rest of your account.
            </Alert>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#0f172a',
                color: '#fff',
                textAlign: 'center'
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1', letterSpacing: 0.8 }}>
                BREAK COUNTDOWN
              </Typography>
              <Typography sx={{ fontSize: { xs: 30, sm: 40 }, fontWeight: 800, lineHeight: 1.1, fontFamily: 'monospace', mt: 0.5 }}>
                {homeBreakCountdownLabel}
              </Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'grid', gap: 0.75 }}>
              <Typography variant="body2"><strong>Break started:</strong> {homeBreakStartLabel}</Typography>
              <Typography variant="body2"><strong>Expected return:</strong> {homeBreakReturnLabel}</Typography>
              <Typography variant="body2" color="text.secondary">
                {homeBreakCountdownMs > 0
                  ? 'End your break when you return to work.'
                  : 'Your one-hour break window is over. End your break now to continue working.'}
              </Typography>
              {homeAttendanceError && <Alert severity="error" sx={{ mt: 1 }}>{homeAttendanceError}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button variant="contained" disabled={homeAttendanceLoading} onClick={endHrBreak}>
              {homeAttendanceLoading ? <CircularProgress size={18} /> : 'End Break'}
            </Button>
          </DialogActions>
        </Dialog>
        <MyProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </Box>
  );
}

// NOTE: File end clean-up: ensure no stray JSX fragments after component.



